import * as React from 'react';
import React__default, { createContext, useContext, useState, useEffect, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Text, Spacer, SvgIcon, Logo as Logo$1, Button, bodySDemiStyle, FeedbackAlert as FeedbackAlert$1, Link, footNoteMediumStyle, Checkbox, ChoiceBox, ChoiceboxGroup, CodeInput, Icon, LoadingSpinner, InfoBanner, TextInput, getEmptyCode, transformCodeToString, colors, FormDropdown } from '@sparknz/spark-pattern-library';
import { useQuery, useMutation, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import * as Sentry from '@sentry/nextjs';
import { v4 } from 'uuid';
import { useForm, Controller } from 'react-hook-form';
import qs from 'qs';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import cookies from 'js-cookie';
import { useAsync } from 'react-async';
import { rest } from 'msw';

const ComponentLibaryContext = createContext(null);
function ComponentLibraryProvider({ children, componentLibrary, }) {
    return (React__default.createElement(ComponentLibaryContext.Provider, { value: componentLibrary }, children));
}
function useComponentLibrary() {
    const context = React__default.useContext(ComponentLibaryContext);
    if (!context) {
        throw new Error('useComponentLibrary should be used inside ComponentLibraryProvider');
    }
    return context;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

// Masking function
const mask = (str) => {
    const strLength = str.length;
    if (strLength > 4) {
        return (str.substring(0, 1) +
            str.substring(1, strLength - 1).replace(/\w/g, '*') +
            str.substring(strLength - 1, strLength));
    }
    return str.replace(/\w/g, '*');
};
// Mask email address
const maskEmailAddress = (emailAddress) => {
    if (!emailAddress)
        return '';
    const [user, domain] = emailAddress.split('@');
    if (!user || !domain)
        return ''; // Invalid email address
    return `${mask(user)}@${mask(domain)}`;
};

const CODES_FOR_NOT_LOGGING_IN_SENTRY = [200, 400, 401, 403, 404];
const sentryLog = (response) => {
    var _a;
    try {
        const responseURL = (_a = response === null || response === void 0 ? void 0 : response.request) === null || _a === void 0 ? void 0 : _a.responseURL;
        const filteredResponseURL = maskEmailAddress(responseURL === null || responseURL === void 0 ? void 0 : responseURL.split('?')[0]);
        if (!CODES_FOR_NOT_LOGGING_IN_SENTRY.includes(response.status)) {
            const transactionId = response.headers['x-global-transaction-id'];
            Sentry.setTag('x-global-transaction-id', transactionId);
            Sentry.captureException(new Error(`API error in ${filteredResponseURL}`), {
                contexts: { response: { messages: JSON.stringify(response.data) } },
                extra: { 'x-global-transaction-id': transactionId },
            });
        }
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to log sentry error:', error);
    }
};

var IdentityServiceBrands;
(function (IdentityServiceBrands) {
    IdentityServiceBrands["SKINNYWEB"] = "SKINNYWEB";
    IdentityServiceBrands["NA"] = "NA";
})(IdentityServiceBrands || (IdentityServiceBrands = {}));
var IdentityServiceRealms;
(function (IdentityServiceRealms) {
    IdentityServiceRealms["SPARK_ID"] = "SPARK_ID";
    IdentityServiceRealms["SKINNY"] = "SKINNY";
    IdentityServiceRealms["XTRAMAIL"] = "XTRAMAIL";
    IdentityServiceRealms["MYSB"] = "MYSB";
})(IdentityServiceRealms || (IdentityServiceRealms = {}));
const getBrandConfig = (universalLoginBrand) => {
    if (universalLoginBrand === 'Skinny') {
        return {
            brand: IdentityServiceBrands.SKINNYWEB,
            realm: IdentityServiceRealms.SKINNY,
        };
    }
    if (universalLoginBrand === 'XtraMail') {
        return {
            brand: IdentityServiceBrands.NA,
            realm: IdentityServiceRealms.XTRAMAIL,
        };
    }
    if (universalLoginBrand === 'MySB') {
        return {
            brand: IdentityServiceBrands.NA,
            realm: IdentityServiceRealms.MYSB,
        };
    }
    return {
        brand: IdentityServiceBrands.NA,
        realm: IdentityServiceRealms.SPARK_ID,
    };
};

const configWithoutRealmAndBrandInHeaders = {
    hasRealm: false,
    hasBrand: false,
};
const configWithRealmInHeaders = {
    hasRealm: true,
    hasBrand: false,
};
const configWithRealmAndBrandInHeaders = {
    hasRealm: true,
    hasBrand: true,
};
function getRealmAndBrandInHeadersConfig({ brand, }) {
    const sparkConfig = {
        withoutRealmAndBrandInHeaders: configWithoutRealmAndBrandInHeaders,
        realmInHeaders: configWithoutRealmAndBrandInHeaders,
        realmAndBrandInHeaders: configWithoutRealmAndBrandInHeaders,
    };
    const skinnyConfig = {
        withoutRealmAndBrandInHeaders: configWithoutRealmAndBrandInHeaders,
        realmInHeaders: configWithRealmInHeaders,
        realmAndBrandInHeaders: configWithRealmAndBrandInHeaders,
    };
    const xtraMailConfig = {
        withoutRealmAndBrandInHeaders: configWithoutRealmAndBrandInHeaders,
        realmInHeaders: configWithRealmInHeaders,
        realmAndBrandInHeaders: configWithRealmInHeaders,
    };
    const mysbConfig = {
        withoutRealmAndBrandInHeaders: configWithoutRealmAndBrandInHeaders,
        realmInHeaders: configWithRealmInHeaders,
        realmAndBrandInHeaders: configWithRealmInHeaders,
    };
    const config = {
        Spark: sparkConfig,
        Skinny: skinnyConfig,
        XtraMail: xtraMailConfig,
        MySB: mysbConfig,
    };
    return config[brand];
}

function getUiSessionId() {
    const sessionIDKey = 'uiSessionId';
    const sessionId = sessionStorage === null || sessionStorage === void 0 ? void 0 : sessionStorage.getItem(sessionIDKey);
    if (sessionId) {
        return sessionId;
    }
    const newSessionId = v4();
    sessionStorage === null || sessionStorage === void 0 ? void 0 : sessionStorage.setItem(sessionIDKey, newSessionId);
    return newSessionId;
}

class ValidationError extends Error {
    constructor(message, code) {
        super(message);
        this.code = code;
    }
}
let baseURL = '';
const setEnvConfig = (URL) => {
    baseURL = URL;
};
const handleResponseData = (response) => {
    if (!response.data.messages) {
        throw new ValidationError('Oops - something went wrong', 9001);
    }
    sentryLog(response);
    const responseData = response.data;
    const { code, message } = responseData.messages[0];
    if (code < 2000 || code > 2999) {
        throw new ValidationError(message, code);
    }
    return Object.assign({}, responseData);
};
const getClient = () => {
    return axios.create({
        baseURL,
        validateStatus: (status) => {
            const validStatus = [200, 400, 401, 403, 404, 500];
            return validStatus.indexOf(status) !== -1;
        },
    });
};
const request = (method, url, headers, data, withCredentials = false, hasRealm = false, hasBrand = false, universalLoginBrand = 'Spark') => __awaiter(void 0, void 0, void 0, function* () {
    const client = getClient();
    const daslSessionId = getUiSessionId();
    const { brand, realm } = getBrandConfig(universalLoginBrand);
    const additionalParametersInHeaders = Object.assign(Object.assign({}, (hasRealm && { realm })), (hasBrand && { brand }));
    return client
        .request({
        method,
        url,
        data,
        withCredentials,
        headers: Object.assign(Object.assign({ 'X-DASL-SESSION-ID': daslSessionId, 'Content-Type': 'application/json' }, (headers || {})), additionalParametersInHeaders),
    })
        .then(handleResponseData);
});
const put = (url, data, realmAndBrandInHeadersOptions = 'withoutRealmAndBrandInHeaders', brand = 'Spark') => __awaiter(void 0, void 0, void 0, function* () {
    const withCredentials = false;
    const config = getRealmAndBrandInHeadersConfig({ brand })[realmAndBrandInHeadersOptions];
    return request('put', url, {}, data, withCredentials, config.hasRealm, config.hasBrand, brand);
});
const get = (url, data, headers, withCredentials, realmAndBrandInHeadersOptions = 'withoutRealmAndBrandInHeaders', brand = 'Spark') => __awaiter(void 0, void 0, void 0, function* () {
    const config = getRealmAndBrandInHeadersConfig({ brand })[realmAndBrandInHeadersOptions];
    return request('get', url, headers, data, withCredentials, config.hasRealm, config.hasBrand, brand);
});
const post = (url, data, headers, withCredentials, realmAndBrandInHeadersOptions = 'withoutRealmAndBrandInHeaders', brand = 'Spark') => __awaiter(void 0, void 0, void 0, function* () {
    const config = getRealmAndBrandInHeadersConfig({ brand })[realmAndBrandInHeadersOptions];
    return request('post', url, headers, data, withCredentials, config.hasRealm, config.hasBrand, brand);
});
const del = (url, data) => __awaiter(void 0, void 0, void 0, function* () { return request('delete', url, {}, data); });
const patch = (url, data, headers, realmAndBrandInHeadersOptions = 'withoutRealmAndBrandInHeaders', brand = 'Spark') => __awaiter(void 0, void 0, void 0, function* () {
    const withCredentials = false;
    const config = getRealmAndBrandInHeadersConfig({ brand })[realmAndBrandInHeadersOptions];
    return request('patch', url, headers, data, withCredentials, config.hasRealm, config.hasBrand, brand);
});
const httpClient = {
    get,
    del,
    post,
    put,
    patch,
};
const handleAEMResponseData = (response) => {
    var _a;
    const responseData = response.data;
    sentryLog(response);
    if (responseData.errors) {
        throw new ValidationError((_a = responseData === null || responseData === void 0 ? void 0 : responseData.errors[0]) === null || _a === void 0 ? void 0 : _a.message, 5001);
    }
    return responseData;
};
const fetchAEMContent = (url) => __awaiter(void 0, void 0, void 0, function* () {
    const client = getClient();
    return client
        .request({
        method: 'get',
        url,
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then(handleAEMResponseData);
});

const ConfigProviderContext = createContext(null);
function ConfigProvider(_a) {
    var { children } = _a, rest = __rest(_a, ["children"]);
    const { apiBaseURL } = rest;
    setEnvConfig(apiBaseURL);
    return (React__default.createElement(ConfigProviderContext.Provider, { value: Object.assign({}, rest) }, children));
}
function useConfig() {
    const context = React__default.useContext(ConfigProviderContext);
    if (!context) {
        throw new Error('useConfig should be used inside ConfigProvider');
    }
    return context;
}

function useBrand() {
    const { brand } = useConfig();
    const isSpark = brand === 'Spark';
    const isSkinny = brand === 'Skinny';
    const isXtraMail = brand === 'XtraMail';
    const isMySB = brand === 'MySB';
    return { isSpark, isSkinny, isXtraMail, isMySB };
}

const AUTHORABLE_CONTENT_PERSISTED_QUERY_BASE_PATH = '/v2/utility/content/fragments';
const AUTHORABLE_CONTENT_PERSISTED_QUERY_NAME = 'ula-content';
const AUTHORABLE_CONTENT_PERSISTED_QUERY_CONFIGURATION = 'spark-models';
const AUTHORABLE_CONTENT_PERSISTED_QUERY_CONFIGS = {
    Spark: {
        modelId: 'ula-spark',
        contentId: 'spark-content',
        variation: 'spark',
    },
    Skinny: {
        modelId: 'ula-skinny',
        contentId: 'skinny-content',
        variation: 'skinny',
    },
    XtraMail: {
        modelId: 'ula-xtramail',
        contentId: 'xtramail-content',
        variation: 'xtramail',
    },
    MySB: {
        modelId: 'ula-mysb',
        contentId: 'mysb-content',
        variation: 'mysb',
    },
};

const constructAuthorableContentBaseUrl = () => {
    const headlessCmsUrl = AUTHORABLE_CONTENT_PERSISTED_QUERY_BASE_PATH;
    const configuration = AUTHORABLE_CONTENT_PERSISTED_QUERY_CONFIGURATION;
    const persistedQueryName = AUTHORABLE_CONTENT_PERSISTED_QUERY_NAME;
    return `${headlessCmsUrl}/${configuration}/${persistedQueryName}/models`;
};
const authorableContentBaseUrl = constructAuthorableContentBaseUrl();
const constructAuthorableContentModelUrl = ({ modelId, contentId, variation, format = 'plaintext', }) => {
    return `${authorableContentBaseUrl}/${modelId}/${contentId}/${format}?variation=${variation}`;
};

const getAuthorableContents = ({ modelId, contentId, variation, }) => __awaiter(void 0, void 0, void 0, function* () {
    const persistedQueryUrl = constructAuthorableContentModelUrl({
        modelId,
        contentId,
        variation,
    });
    return fetchAEMContent(persistedQueryUrl);
});

const useAuthorableContentsQueryConfig = () => {
    const { brand } = useConfig();
    return AUTHORABLE_CONTENT_PERSISTED_QUERY_CONFIGS[brand] || {};
};

const buildAuthorableContents = (ulaContentItem) => {
    if (!ulaContentItem)
        return null;
    return Object.entries(ulaContentItem).reduce((formattedContent, [ulaContentKey, ulaContentValue]) => {
        return Object.assign(Object.assign({}, formattedContent), { [ulaContentKey.replace('_', '.')]: ulaContentValue.plaintext });
    }, {});
};

const initialAuthorableContentsContextValue = {
    authorableContents: null,
};
const AuthorableContentsContext = React__default.createContext(initialAuthorableContentsContextValue);
const AuthorableContentsProviderInternal = ({ children, }) => {
    var _a, _b;
    const { isXtraMail, isMySB } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    const authorableContentEnabled = isXtraMail || isMySB;
    const { modelId, contentId, variation } = useAuthorableContentsQueryConfig();
    const { data } = useQuery({
        queryKey: ['AuthorableContents', modelId, contentId, variation],
        queryFn: () => getAuthorableContents({
            modelId,
            contentId,
            universalLoginBrand,
            variation,
        }),
        enabled: authorableContentEnabled,
    });
    const ulaContentItem = (_b = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.ulaContentModelList) === null || _b === void 0 ? void 0 : _b.items[0];
    const authorableContents = buildAuthorableContents(ulaContentItem);
    return (React__default.createElement(AuthorableContentsContext.Provider, { value: { authorableContents } }, children));
};
function AuthorableContentsProvider({ children }) {
    return (React__default.createElement(AuthorableContentsProviderInternal, null, children));
}
const useAuthorableContents = () => {
    const { authorableContents } = useContext(AuthorableContentsContext);
    return {
        getText: (key) => authorableContents ? authorableContents[key] : '',
    };
};

const FieldErrorMessage = styled(Text) `
  color: ${({ theme: { palette } }) => palette.baseColors.marangai};
`;
const ContentText = styled.span `
  ${({ fontSize, fontWeight, lineHeight }) => css `
    font-size: ${fontSize ? `${fontSize}px` : 'unset'};
    font-weight: ${fontWeight || 'unset'};
    line-height: ${lineHeight ? `${lineHeight}px` : 'unset'};
  `};
`;

const SKINNY_CONTENT = {
    emailLabel: 'Email Address',
    userIdLabel: '',
    authenticatingTo: 'Authenticating to MySkinny',
    firstName: 'First Name',
    lastName: 'Last Name',
    returnToSignIn: (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20, fontWeight: 400 }, "Return to Login")),
    'login.loginText': 'Login',
    'login.toContinue': 'To continue to your Skinny account',
    'login.clickHere': (React__default.createElement(ContentText, { fontSize: 16, fontWeight: 700 }, "Click here")),
    'login.resetPasswordText': 'Can’t sign in?',
    'login.buttonText': 'LOGIN',
    'login.register': (React__default.createElement(ContentText, { fontSize: 16, fontWeight: 700 }, "Register")),
    'login.orSignInWith': 'Or sign in with',
    'login.noAccount': (React__default.createElement(ContentText, { fontSize: 16 },
        "Don\u2019t have an account?",
        ` `)),
    'login.needHelp': '',
    'login.twoStepVerification': 'Enter Code',
    'login.toVerifyItsYou': 'To sign in using two-factor authentication',
    'login.continueVerify': 'VERIFY',
    'login.resendVerificationCode': "HAVEN'T RECEIVED A CODE?",
    'login.returnToSignInButtonText': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20, fontWeight: 700 }, "RETURN TO LOGIN")),
    'resetPassword.resetDescription': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "We'll email you a link to reset your password.")),
    'resetPassword.send': 'SEND',
    'resetPassword.forgotYourEmail': (React__default.createElement(ContentText, { fontSize: 14, lineHeight: 16 }, "Forgot your email?")),
    'resetPassword.sendResetLink': 'RESEND RESET LINK',
    'resetPassword.resetPassword': 'Reset Password',
    'resetPassword.followTheDirections': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "If your email address is associated with a Skinny account, we'll email you instructions to reset your password.")),
    'resetPassword.enterNewPassword': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "Almost done. Enter your new password and you\u2019re good to go.")),
    'resetPassword.changePassword': 'CHANGE PASSWORD',
    'resetPassword.enterPassword': 'Enter Password',
    'resetPassword.success': 'Success',
    'resetPassword.successMessage': "You've successfully changed your password.",
    'resetPassword.newPassword': 'New Password',
    'resetPassword.verifyCodeTitle': '',
    'resetPassword.verifyCodeDescriptionFirstBlock': '',
    'resetPassword.verifyCodeDescriptionSecondBlock': '',
    'resetPassword.verifyCodeVerifyButtonText': '',
    'resetPassword.verifyCodeResendCodeButtonText': '',
    'resetPassword.reenterNewPassword': '',
    'resetPassword.passwordsDontMatch': '',
    'register.title': 'Register',
    'register.toManage': 'To manage your Skinny account',
    'register.marketingCommsMessage': '',
    'register.marketingCommsNote': '',
    'register.enterYourName': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "Enter your name to personalise your SkinnyID.")),
    'register.nextStepText': (React__default.createElement(ContentText, { lineHeight: 20, fontWeight: 700 }, "REGISTER")),
    'register.next': (React__default.createElement(ContentText, { lineHeight: 20, fontWeight: 700 }, "NEXT")),
    'register.enterCode': 'Enter Code',
    'register.toVerifyItsYou': 'To verify it’s you',
    'register.verify': 'VERIFY',
    'register.haveNotReceivedACode': (React__default.createElement(ContentText, { lineHeight: 20 }, "HAVEN'T RECEIVED A CODE?")),
    'recoverEmail.enterYourName': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "Enter your name and we\u2019ll recover the email associated with your account.")),
    'recoverEmail.continue': React__default.createElement(ContentText, { lineHeight: 20 }, "CONTINUE"),
    'recoverEmail.selectYourEmail': (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20 }, "Select your email address and we\u2019ll send a reminder shortly.")),
    'recoverEmail.submit': 'SUBMIT',
    // MFA
    'mfa.optionsSubheader': 'Two-step verification',
    'mfa.optionsBody': 'How would you like to sign in using two-step verification?',
    'mfa.viewCode': 'Open your authenticator app to view the six digit code.',
    'mfa.codeSentTo': 'A six digit code has been sent to',
    'mfa.enterCodeBelow': 'Please enter it below.',
    // Forgot user id page content
    'forgotUserId.title': '',
    'forgotUserId.description': '',
    'forgotUserId.forgotYourEmail': '',
    'forgotUserId.send': '',
    'forgotUserId.contactAdminTitle': '',
    'forgotUserId.confirmationDetails': '',
    'forgotUserId.contactAdminMessage': '',
    'forgotUserId.resendEmail': '',
};
const SPARK_CONTENT = {
    emailLabel: 'Email',
    userIdLabel: '',
    authenticatingTo: 'Authenticating to MySpark',
    firstName: 'First name',
    lastName: 'Last name',
    returnToSignIn: 'Return to sign in',
    'login.loginText': 'Sign in',
    'login.toContinue': 'to continue',
    'login.clickHere': '',
    'login.resetPasswordText': 'Forgot your password?',
    'login.buttonText': 'Sign in',
    'login.register': 'Create an account',
    'login.orSignInWith': 'Or sign in with',
    'login.noAccount': 'Don’t have an account?',
    'login.needHelp': '',
    'login.twoStepVerification': '2-Step verification',
    'login.toVerifyItsYou': 'to verify it’s you',
    'login.continueVerify': 'Continue',
    'login.resendVerificationCode': 'Resend verification code',
    'login.returnToSignInButtonText': 'Return to sign in',
    'resetPassword.resetDescription': "We'll email you a link to reset your password.",
    'resetPassword.send': 'Send',
    'resetPassword.forgotYourEmail': 'Forgot your email?',
    'resetPassword.sendResetLink': 'Resend reset link',
    'resetPassword.resetPassword': 'Reset password',
    'resetPassword.followTheDirections': "If your email address is associated with a MySpark account, we'll email you instructions to reset your password.",
    'resetPassword.enterNewPassword': 'Almost done. Enter your new password and you’re good to go.',
    'resetPassword.changePassword': 'Change password',
    'resetPassword.enterPassword': 'Enter password',
    'resetPassword.success': 'Enter password',
    'resetPassword.successMessage': "You've successfully changed your password.",
    'resetPassword.newPassword': 'New password',
    'resetPassword.verifyCodeTitle': '',
    'resetPassword.verifyCodeDescriptionFirstBlock': '',
    'resetPassword.verifyCodeDescriptionSecondBlock': '',
    'resetPassword.verifyCodeVerifyButtonText': '',
    'resetPassword.verifyCodeResendCodeButtonText': '',
    'resetPassword.reenterNewPassword': '',
    'resetPassword.passwordsDontMatch': '',
    'register.title': 'Create an account',
    'register.toManage': 'to access and manage your account',
    'register.marketingCommsMessage': 'I want to receive exclusive offers and marketing from Spark and their partners.',
    'register.marketingCommsNote': 'You can change your preferences at any time in MySpark.',
    'register.enterYourName': 'Enter your name to personalise your SparkID.',
    'register.nextStepText': 'Next',
    'register.next': 'Next',
    'register.enterCode': 'Enter code',
    'register.toVerifyItsYou': 'to verify it’s you',
    'register.verify': 'Verify',
    'register.haveNotReceivedACode': "Haven't received a code?",
    'recoverEmail.enterYourName': 'Enter your name and we’ll recover the email associated with your account.',
    'recoverEmail.continue': 'Continue',
    'recoverEmail.selectYourEmail': "Select your email address below and we'll send a reminder shortly.",
    'recoverEmail.submit': 'Submit',
    // MFA
    'mfa.optionsSubheader': 'Two-step verification',
    'mfa.optionsBody': 'How would you like to sign in using two-step verification?',
    'mfa.viewCode': 'Open your authenticator app to view the six digit code.',
    'mfa.codeSentTo': 'A six digit code has been sent to',
    'mfa.enterCodeBelow': 'Please enter it below.',
    // Forgot user id page content
    'forgotUserId.title': '',
    'forgotUserId.description': '',
    'forgotUserId.forgotYourEmail': '',
    'forgotUserId.send': '',
    'forgotUserId.contactAdminTitle': '',
    'forgotUserId.confirmationDetails': '',
    'forgotUserId.contactAdminMessage': '',
    'forgotUserId.resendEmail': '',
};
function useContent() {
    const { brand } = useConfig();
    const { getText: getAuthorableContent } = useAuthorableContents();
    function getText(contentKey) {
        if (brand === 'Skinny') {
            return SKINNY_CONTENT[contentKey];
        }
        if (brand === 'XtraMail') {
            return getAuthorableContent(contentKey);
        }
        if (brand === 'MySB') {
            return getAuthorableContent(contentKey);
        }
        return SPARK_CONTENT[contentKey];
    }
    return { getText };
}

const Margin = styled.div `
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isCentered ? 'center' : 'initial')};
`;
const FormRow = styled.div `
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  align-items: ${(props) => (props.centered ? 'center' : 'initial')};
  > * {
    align-self: ${(props) => (props.centered ? 'center' : 'initial')};
  }
`;
const LinkRow = styled(FormRow) `
  margin-bottom: 0;
`;

const EmailInput = ({ disabled, error, name, onChange, value, inputType = 'email', label, placeholder, }) => {
    const { Text, TextInput } = useComponentLibrary();
    const { getText } = useContent();
    const inputLabel = inputType === 'email' ? getText('emailLabel') : label;
    return (React__default.createElement(FormRow, null,
        React__default.createElement(Text, { variant: "bodySDemi" }, inputLabel),
        React__default.createElement(Spacer, { variant: "spacing-half" }),
        React__default.createElement(TextInput, { disabled: disabled, errorText: error === null || error === void 0 ? void 0 : error.message, isError: Boolean(error), name: name, type: inputType, onChange: onChange, value: value, placeholder: placeholder })));
};

const FALLBACK_ERROR_MESSAGE = 'Oops - something went wrong';
const getValidationError = (error) => {
    if (error && 'code' in error) {
        return error;
    }
    return null;
};
function ErrorMessage({ error, errorMap }) {
    const { InfoBanner } = useComponentLibrary();
    const validationError = getValidationError(error);
    const errorMessageDetails = (validationError === null || validationError === void 0 ? void 0 : validationError.code)
        ? errorMap[validationError.code]
        : null;
    if ((validationError === null || validationError === void 0 ? void 0 : validationError.code) == null) {
        return React__default.createElement(InfoBanner, { variant: "ERROR", text: FALLBACK_ERROR_MESSAGE });
    }
    const message = (errorMessageDetails === null || errorMessageDetails === void 0 ? void 0 : errorMessageDetails.message) || FALLBACK_ERROR_MESSAGE;
    const buttonText = errorMessageDetails === null || errorMessageDetails === void 0 ? void 0 : errorMessageDetails.linkText;
    const handleClick = errorMessageDetails === null || errorMessageDetails === void 0 ? void 0 : errorMessageDetails.action;
    return (React__default.createElement(InfoBanner, { variant: "ERROR", text: message, buttonText: buttonText, onClick: handleClick }));
}

var _g, _defs;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function SvgXtramailLogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 811 137",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#xtramail_logo_svg__clip0_1_2)",
    fill: "currentColor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M250.545 77.848l-33.099-49.63h22.869l21.966 36.395 21.665-36.394h21.815l-32.497 50.08 38.214 56.396h-23.921l-26.029-41.808-24.975 41.808h-22.869l36.861-56.847zM311.058 76.945V62.508h12.638V41.603h17.753v20.905h18.054v14.437h-18.054v33.537c0 3.209.552 5.866 1.655 7.971 1.204 2.106 3.811 3.158 7.824 3.158 1.203 0 2.507-.1 3.911-.3 1.404-.301 2.658-.702 3.762-1.204l.601 14.137c-1.604.602-3.51 1.053-5.717 1.354-2.206.401-4.313.601-6.319.601-4.814 0-8.726-.651-11.735-1.955-3.009-1.404-5.416-3.258-7.222-5.564-1.705-2.407-2.909-5.114-3.611-8.121-.601-3.108-.902-6.417-.902-9.926V76.945h-12.638zM371.065 62.508h17.302v12.031h.301c2.006-4.21 4.814-7.62 8.425-10.227 3.611-2.606 7.823-3.91 12.638-3.91.702 0 1.454.05 2.257.15.802 0 1.504.101 2.106.301v16.544c-1.204-.301-2.257-.502-3.16-.602-.802-.1-1.604-.15-2.407-.15-4.112 0-7.422.752-9.93 2.255-2.507 1.504-4.463 3.309-5.867 5.414-1.404 2.106-2.357 4.262-2.859 6.467-.501 2.206-.752 3.961-.752 5.264v38.65h-18.054V62.508zM467.288 125.521h-.451c-1.806 3.209-4.714 5.866-8.726 7.971-4.012 2.005-8.576 3.008-13.691 3.008-2.909 0-5.968-.401-9.178-1.203a27.744 27.744 0 01-8.726-3.609c-2.608-1.805-4.815-4.111-6.62-6.918-1.705-2.908-2.558-6.467-2.558-10.678 0-5.414 1.505-9.725 4.514-12.934 3.109-3.208 7.071-5.665 11.885-7.369 4.815-1.704 10.131-2.807 15.948-3.309a171.255 171.255 0 0117.302-.902v-1.805c0-4.511-1.655-7.82-4.965-9.925-3.209-2.206-7.071-3.309-11.584-3.309-3.812 0-7.473.802-10.983 2.406-3.511 1.605-6.42 3.56-8.727 5.866L421.4 71.832c4.113-3.81 8.827-6.667 14.143-8.572 5.416-1.905 10.883-2.858 16.399-2.858 6.419 0 11.685.903 15.798 2.707 4.212 1.805 7.522 4.161 9.929 7.069 2.408 2.907 4.063 6.166 4.965 9.775 1.003 3.61 1.505 7.219 1.505 10.828v43.914h-16.851v-9.174zm-.301-23.761h-4.062c-2.909 0-5.968.15-9.177.451-3.21.2-6.169.752-8.877 1.654-2.708.802-4.965 2.005-6.77 3.61-1.806 1.504-2.709 3.609-2.709 6.316 0 1.704.351 3.158 1.054 4.361a9.725 9.725 0 003.009 2.707c1.203.702 2.557 1.204 4.062 1.504a22.95 22.95 0 004.513.451c6.219 0 10.933-1.654 14.143-4.962 3.21-3.309 4.814-7.821 4.814-13.536v-2.556zM540.68 28.219h29.639l27.683 75.646h.451l27.383-75.646h29.789v106.476h-18.806V49.123h-.301l-32.348 85.572h-13.239l-31.896-85.572h-.301v85.572H540.68V28.219zM721.512 125.521h-.452c-1.805 3.209-4.714 5.866-8.726 7.971-4.012 2.005-8.576 3.008-13.691 3.008-2.909 0-5.968-.401-9.178-1.203a27.744 27.744 0 01-8.726-3.609c-2.608-1.805-4.815-4.111-6.62-6.918-1.705-2.908-2.558-6.467-2.558-10.678 0-5.414 1.505-9.725 4.514-12.934 3.109-3.208 7.071-5.665 11.886-7.369 4.814-1.704 10.13-2.807 15.948-3.309a171.24 171.24 0 0117.302-.902v-1.805c0-4.511-1.655-7.82-4.965-9.925-3.21-2.206-7.072-3.309-11.585-3.309-3.812 0-7.473.802-10.983 2.406-3.511 1.605-6.419 3.56-8.726 5.866l-9.328-10.979c4.112-3.81 8.826-6.667 14.142-8.572 5.416-1.905 10.883-2.858 16.399-2.858 6.42 0 11.685.903 15.798 2.707 4.213 1.805 7.523 4.161 9.93 7.069 2.407 2.907 4.062 6.166 4.965 9.775 1.003 3.61 1.504 7.219 1.504 10.828v43.914h-16.85v-9.174zm-.301-23.761h-4.063c-2.908 0-5.968.15-9.177.451-3.21.2-6.169.752-8.877 1.654-2.708.802-4.965 2.005-6.77 3.61-1.806 1.504-2.708 3.609-2.708 6.316 0 1.704.351 3.158 1.053 4.361a9.725 9.725 0 003.009 2.707c1.203.702 2.558 1.204 4.062 1.504a22.96 22.96 0 004.514.451c6.218 0 10.932-1.654 14.142-4.962 3.21-3.309 4.815-7.821 4.815-13.536v-2.556zM755.943 62.508h18.054v72.187h-18.054V62.508zm-2.557-24.514c0-2.907 1.053-5.414 3.159-7.52 2.207-2.205 4.965-3.308 8.275-3.308 3.31 0 6.068 1.053 8.275 3.158 2.307 2.005 3.46 4.562 3.46 7.67 0 3.108-1.153 5.715-3.46 7.82-2.207 2.006-4.965 3.008-8.275 3.008-3.31 0-6.068-1.053-8.275-3.158-2.106-2.206-3.159-4.762-3.159-7.67zM792.447 21h18.054v113.695h-18.054V21z"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M.126 35.937v-6.151h80.008c3.418 0 6.19 2.75 6.19 6.144 0 3.393-2.142 6.144-5.56 6.144H18.005l56.791 35.184a4.152 4.152 0 004.367.002l12.335-7.631c2.9-1.795 6.718-.915 8.526 1.965 1.808 2.88.922 6.67-1.98 8.464L85.71 87.689a16.609 16.609 0 01-17.467-.006L12.504 53.151v65.596c0 2.9 2.35 5.25 5.249 5.25H136.2a5.249 5.249 0 005.248-5.25V96.86c0-3.393 2.771-6.144 6.19-6.144 3.418 0 6.189 2.751 6.189 6.144V125.786c0 5.799-4.7 10.5-10.497 10.5H10.622c-5.797 0-10.497-4.701-10.497-10.501v-17.169 21.523-87.97-6.232z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M158.142 38.442c.019-.01.01-.01 0 0zm-59.53-.423c-.038.067.01.067.047.086.037.02.103.03.122.039 1.239.365 2.553.75 3.773 1.105.037.01.075.038.122.028-1.352-.422-2.713-.826-4.064-1.258zm78.545 10.778c-3.595-.614-12.746-.605-18.358-.538-.31-.144-.61-.297-.91-.442-2.394-1.143-4.805-2.286-7.18-3.439.404-.374.798-.758 1.22-1.124.01 0 .01.01.019.01 3.538 1.345 9.066 3.295 12.689 4.553h.018c.254.058.798-.403 1.014-1.133.216-.73.122-1.441-.113-1.537 0 0-8.155-2.92-11.252-4.044 1.642-1.499 3.35-2.901 5.021-4.352 1.67-1.45 3.331-2.91 5.03-4.342.845-.711 1.689-1.431 2.553-2.133 1.717-1.412 3.435-2.814 5.246-4.111.451-.327.911-.653 1.389-.951.301-.192.61-.394.911-.548.197-.105.413-.173.61-.278.009 0 .009-.01.018-.01 0-.038-.047-.01-.028-.048.028-.02.066-.038.094-.067.019-.125.085-.346-.009-.442-.122-.029-.207.067-.31.077-.019-.029.009-.039.019-.058-.451.25-.911.49-1.37.711-.075.039-.198.077-.263.115-.028.02-.057.03-.094.048-.648.337-1.305.663-1.962 1-.61.316-1.22.624-1.82.95-.601.337-1.211.634-1.812.961-2.402 1.306-4.805 2.594-7.161 3.958.263-.211.517-.442.817-.615.3-.173.61-.336.91-.51a336.147 336.147 0 015.481-3.073c1.22-.663 2.45-1.316 3.707-1.989 1.061-.557 2.178-1.152 3.313-1.671.151-.067.357-.192.554-.163.019 0 .066.019.103.038.094.029.141.058.179.115.065.106.131.433.056.615-.038.077-.075.077-.131.115-.029.02-.038.039-.066.058-.047.029-.103.058-.15.096-.319.183-.61.355-.901.548-.582.384-1.164.787-1.727 1.181-.188.135-.376.279-.563.413a71.946 71.946 0 00-3.276 2.546 58.608 58.608 0 00-2.168 1.835c-.15.134-.309.26-.46.394-1.567 1.354-3.115 2.728-4.655 4.082-1.032.913-2.055 1.835-3.097 2.738-.009.02-.009.02.01.01a390.925 390.925 0 017.508-6.33 452.053 452.053 0 00-6.072 5.311c-.01.03.028-.01.028-.01.244-.201.497-.431.76-.595-.77.817-1.624 1.576-2.459 2.344a80.766 80.766 0 00-1.858 1.748c-.169.173-.376.346-.526.52-.019.018-.065.076-.037.086.234-.173.441-.375.666-.567 1.089-.97 2.177-1.931 3.275-2.892.883-.768 1.765-1.537 2.638-2.295.441-.385.891-.76 1.332-1.134.01-.01.019-.029.038-.038.047-.039.084-.077.122-.106.835-.72 1.671-1.422 2.506-2.123a171.561 171.561 0 014.073-3.324c.694-.547 1.389-1.076 2.093-1.604.235-.173.469-.346.713-.519.742-.528 1.455-.98 2.262-1.431.282-.154.554-.288.704-.529.159-.25.272-.624.216-.989-.057-.336-.197-.596-.479-.75.141.116.254.27.329.462.075.173.14.422.103.692-.01.095-.057.201-.094.307-.084.202-.169.346-.329.47-.075.058-.15.135-.234.173-.169.097-.347.183-.526.279-.525.269-1.023.596-1.501.893-.338.212-.648.442-.986.625a15.923 15.923 0 012.3-1.652c.197-.116.413-.24.619-.365.206-.125.375-.298.46-.52.103-.259.084-.585-.028-.825a.998.998 0 00-.498-.49c.329-.164.648-.346.958-.538.225-.144.459-.288.628-.47.066-.068.141-.126.188-.193.103-.135.169-.288.244-.452.075-.153.131-.355.141-.547.019-.211-.047-.394-.113-.576a1.378 1.378 0 00-.244-.452 1.46 1.46 0 00-.835-.538 1.596 1.596 0 00-.61-.058c-1.811.231-3.322.798-4.815 1.336.291-.068.685-.25.995-.317a53.413 53.413 0 00-2.994 1.23c-.582.25-1.088.624-1.605.94.47-.403.92-.806 1.352-1.248.422-.442.807-.932 1.173-1.431.169-.231.357-.48.535-.76.178-.268.338-.556.404-.902.037-.183.056-.442.018-.596-.037-.154-.15-.288-.253-.403a2.28 2.28 0 00-.357-.298c-.131-.087-.281-.192-.45-.202-.085 0-.178.029-.272.048-.479.077-.798.298-1.108.51-.131.095-.253.191-.375.278-1.023.759-1.924 1.556-2.853 2.373-1.399 1.23-2.76 2.468-4.139 3.698a743.994 743.994 0 00-3.961 3.554c-.056.048-.122.096-.178.154-1.849 1.643-3.679 3.314-5.519 4.947-1.389 1.23-2.759 2.488-4.129 3.718 1.004-.98 1.99-1.989 2.994-2.968.253-.24.497-.49.751-.73.253-.24.488-.51.741-.75.263-.24.497-.5.751-.74.253-.24.507-.49.751-.74 1.961-2.007 3.941-4.015 5.903-6.022 2.459-2.517 4.927-5.015 7.339-7.57.366-.384.723-.778 1.07-1.162.122-.135.235-.27.329-.423.197-.308.3-.653.3-1.028 0-.115 0-.23-.009-.346-.019-.25-.179-.49-.357-.663-.047-.048-.094-.096-.15-.134a.99.99 0 00-.526-.211c-.225-.039-.432 0-.657.057-.394.106-.666.288-1.004.48 0 0-.385.25-.554.385.122-.135.31-.288.47-.423 0-.02-.01-.029-.019-.038 0 0 0-.01.009-.01.357-.384.441-.922.16-1.24-.291-.335-.873-.297-1.295.097a3.458 3.458 0 00-.244.269c-.667.72-1.239 1.383-1.924 2.084-.188.192-.357.394-.564.577l-1.811 1.623c-.403.365-.807.72-1.211 1.086-.403.365-.807.72-1.21 1.076-.601.538-1.192 1.095-1.793 1.633-.197.173-.403.346-.591.538.488-.375.995-.74 1.492-1.105-.882.788-1.774 1.575-2.675 2.363-.901.788-1.773 1.595-2.665 2.383-1.755 1.546-3.491 3.14-5.218 4.716a.234.234 0 01-.094.058c-.009-1.21-.047-3.516-.056-3.631 0-.164-.32-.298-.704-.298-.113 0-.216.01-.31.029.038-1.777.075-3.555.122-5.322v-.01c-.009-.384-.46-.701-1.032-.701-.028 0-.057 0-.085.01-.028 0-.056.01-.084.01-.057.009-.104.009-.16.018-.047.01-.094.02-.15.039-.047.01-.094.038-.131.057-.047.02-.085.048-.122.068a.431.431 0 00-.104.076c-.028.03-.056.058-.084.096a.253.253 0 00-.056.097.519.519 0 00-.038.115c-.009.038-.009.077-.019.125V20.555c0 .019 0 .048.01.067v.077c0 .029 0 .048.009.077V20.939c0 .029 0 .058.009.087v.096l.01.096c0 .028 0 .067.009.096v.105c0 .039 0 .068.01.106v.106a.5.5 0 00.009.115v.115l.009.116c0 .038 0 .076.01.115v.125c0 .038 0 .077.009.125 0 .038 0 .086.01.125v.124c0 .039.009.087.009.125v.135c0 .038.009.086.009.134 0 .048.01.087.01.135 0 .048.009.086.009.134l.009.135c0 .048.01.096.01.134l.009.135c0 .038.01.086.01.134l.009.135c0 .048.009.096.009.144 0 .048.01.096.01.144l.009.144c0 .048.01.096.01.144 0 .048 0 .096.009.144 0 .048.009.096.009.144 0 .048 0 .096.01.144 0 .048 0 .096.009.144 0 .048.009.096.009.144a.7.7 0 00.01.145c0 .048.009.096.009.144.01.048.01.096.01.144 0 .048.009.096.009.144.009.048.009.096.009.144 0 .048.01.096.01.144 0 .048.009.096.009.144l.009.144a.7.7 0 00.01.144c0 .048.009.096.009.144 0 .048.01.096.01.144 0 .049.009.097.009.135 0 .048.009.086.009.134 0 .048 0 .096.01.135 0 .038 0 .086.009.134 0 .049 0 .087.01.135 0 .038 0 .086.009.134 0 .048.009.087.009.135 0 .038 0 .086.01.125 0 .038 0 .086.009.125 0 .038 0 .086.01.125 0 .038 0 .086.009.125v.124c0 .039.009.077.009.125v.24c0 .039.01.077.01.116 0 .038.009.076.009.115v.106c-.113-.25-.235-.49-.338-.72-.037-.087-.103-.212-.103-.299-.038-.49-.038-.98-.066-1.47l-.084-1.479c-.056-.98-.113-1.96-.16-2.949-.131-2.719-.319-5.399-.413-8.127 0-.125-.018-.25-.018-.365 0-.115.028-.24.037-.355.291-2.728.882-8.223.882-8.358.028-1.085-.516-1.979-1.22-2.008-.713-.028-1.314.836-1.361 1.912-.009.221.235 4.054.329 6.033.009.154.037.307.028.461-.019.298-.084.586-.113.874a99.691 99.691 0 00-.703-4.41 124.827 124.827 0 00-.845-4.236 7.575 7.575 0 00-.807-2.123c-.366-.634-.92-1.268-1.662-1.421a1.92 1.92 0 00-1.548.374c-.526.327-.901 1.019-1.014 1.585-.14.73.019 1.509.122 2.239.254 1.748.385 3.506.62 5.254.131.98.234 1.97.356 2.95-.234-.48-.459-.98-.685-1.46-1.548-3.469-3.134-6.917-4.683-10.375-.037-.03-.019-.02-.028-.02l-.009.01c.666 1.537 1.389 3.103 2.036 4.688-.76-1.508-1.455-3.084-2.187-4.62-.46.23-.872.393-1.332.624.037-.01-.019.01.018 0 1.896 4.486 3.848 8.943 5.725 13.43.582 1.402 1.183 2.814 1.765 4.226.037.096.094.173.103.279-1.502-3.315-3.003-6.638-4.514-9.943 0-.01-.01-.01-.01 0a517.84 517.84 0 013.238 7.455c.028.076.066.153.094.24-1.398-3.017-2.806-6.014-4.204-9.03-.019-.048-.029-.058-.038-.077-.113.038-.178.096-.272.115-.892-2.2-2.609-6.426-2.694-6.609-.431-.96-1.304-1.518-2.018-1.24-.778.299-.919 1.288-.788 2.009.103.566.329 1.085.535 1.623.207.557.413 1.114.629 1.671.047.135.103.298.169.452.047.134.084.346.169.461-.413-.538-.929-.816-1.342-.653-.517.211-.685 1.057-.432 1.97-.385-.52-.873-.789-1.258-.635-.535.221-.657 1.163-.281 2.123.009.029.028.058.037.087.047.096.085.163.113.24.385 1.018.779 2.027 1.164 3.045-.31-.528-.592-1.095-.901-1.633a1.673 1.673 0 00-.226-.288.664.664 0 00-.75-.116.679.679 0 00-.394.625c0 .153.056.288.093.413.047.125.104.24.151.355.581 1.48 1.182 2.853 1.783 4.323a590.35 590.35 0 003.622 8.588c.761 1.787 1.54 3.574 2.309 5.332.056.115.113.23.141.345-.892-1.95-1.774-3.91-2.675-5.86 1.117 2.623 2.215 5.303 3.351 7.897.037.077.075.154.094.24-.169-.038-.32-.096-.479-.153-.16-.058-.319-.097-.469-.164a652.91 652.91 0 01-3.614-1.844c-.835-.452-11.093-4.947-11.506-5.101-.047-.02-.094-.048-.141-.067-1.22-.558-1.576.7-1.454 1.018a53.27 53.27 0 00-1.286-.48c-.432-.154-1.305-.48-1.305-.48-.206-.078-.497.191-.647.595-.085.23-.104.46-.066.624a16.103 16.103 0 01-1.549-.442c-.272-.096-.544-.201-.835-.288-.291-.086-.582-.173-.891-.23-.32-.068-.676-.125-1.005-.087-.347.038-.722.154-.957.432-.253.298-.291.702-.197 1.067.094.365.272.672.479.98.065.096.131.182.206.268.075.087.15.164.225.24.094.087.188.183.291.27.104.096.216.182.338.259.122.087.253.163.376.24.15.087.29.173.441.25.159.086.328.173.497.24.188.087.376.154.563.23.216.078.423.154.638.222.057.019.104.038.16.048a15.056 15.056 0 01-.695-.125c-.159.576-.403 1.422-.403 1.45l.009.01s9.817 4.284 14.782 6.378c-2.891-.96-5.847-1.863-8.756-2.814-.977-.317-1.953-.625-2.929-.932-1.933-.605-3.998-1.162-6.231-1.47-.582-.086-1.174-.125-1.755-.163-.291-.02-.573-.067-.864-.096-.291-.038-.572-.077-.854-.106-.141-.019-.282-.029-.413-.067l-.366-.115a67.27 67.27 0 00-1.82-.596 2.263 2.263 0 00-.32-.086.07.07 0 00-.047-.02c-.385-.076-.788.308-.891.865-.103.548.112 1.047.497 1.143.028.01.056.01.066.02.028.009.056.009.084.009.892.115 1.765.23 2.638.336.215.029.412.115.619.173.61.192 1.239.375 1.821.577-.122.269-.179.576-.272.874-.066.22-.132.441-.198.653-.018.067-.028.096-.037.134.666.183 1.323.394 1.98.596 4.655 1.422 9.31 2.824 13.956 4.256-.131.413-.253.826-.385 1.239-1.436-.5-2.872-1.028-4.317-1.518-.723-.25-1.436-.519-2.149-.759-1.483-.5-3.013-1.076-4.458-1.556-.01 0-.15-.058-.066-.02.075.04.178.068.272.097 1.267.46 2.487.893 3.745 1.364.619.23 1.248.451 1.839.701.076.038.169.058.235.115-.601-.144-1.192-.326-1.783-.509-1.164-.365-2.318-.75-3.482-1.105-.591-.182-1.154-.393-1.717-.586-.639-.22-1.277-.451-1.915-.672-.3-.115-.929-.336-1.614-.576-1.014-.365-2.046-.72-3.041-1.086-.066-.029-.131-.067-.197-.096-.32-.096-.676.192-.798.644-.131.442.019.874.32.98.046.019.112.028.112.028.094.02.16.039.253.068 2.403.662 4.796 1.383 7.161 2.065.254.077.497.144.741.23 1.192.423 2.403.875 3.576 1.297-.6-.153-1.182-.307-1.783-.47-.009 0-.009.01.009.01.77.22 1.53.422 2.281.643.366.106.713.26 1.07.384.366.135.723.25 1.079.384 1.446.52 2.882 1.009 4.317 1.528a326.22 326.22 0 004.318 1.527c1.445.49 2.872 1.018 4.326 1.518-3.576 3.324-7.17 6.619-10.361 10.317a41.77 41.77 0 00-1.736 2.142c-.066.087-.113.183-.188.25-.15.135-.319.25-.479.384 0 0-.788.596-.929.807-.141.212.066.654.479.951.413.298.873.365 1.032.144 0 0 0-.01.01-.01.009 0 .009-.009.009-.018.197-.413.441-.76.685-1.124.066-.096.113-.192.197-.27.075-.067.16-.143.254-.21.169-.135.338-.28.506-.414 4.655-3.813 9.292-7.637 13.919-11.48.075-.066.225-.201.272-.201.037 0 .103.039.15.058.16.057.31.115.469.153-1.088 1.25-2.215 2.46-3.303 3.699-.273.307-.554.615-.826.922-.554.624-1.098 1.249-1.643 1.864-.272.307-.544.624-.788.97.075 0 .15-.125.207-.144-1.962 2.151-6.045 6.301-6.035 6.311.122.115.187.192.3.307-.957.913-1.905 1.826-2.862 2.729.093.086.178.192.272.288-.376.336-.76.672-1.155 1.009-.047.038-.178.153-.178.153-.206.183-.047.73.366 1.23.347.422.751.663.995.624.009 0 .009.01.019.01-.629.653-1.455 1.393-2.093 2.065-.075.077-.272.27-.291.298-.225.25-.338.951-.197 1.326.028.077.094.269.197.355-.544.48-1.051.894-1.605 1.374-.056.048-.103.106-.122.115-.141.192-.084.519.169.865.272.365.657.538.854.384l.019-.02a.78.78 0 00.141.548c.168.27.431.413.61.356.037-.01.047-.02.065-.03.038-.018.057-.047.085-.086 1.811-1.47 3.613-2.958 5.415-4.428.61-.5 1.22-1.009 1.839-1.499-.581.826-1.116 1.653-1.679 2.45a1.843 1.843 0 01-.179.202c-.244.25-.497.5-.751.758-.497.51-1.013.99-1.511 1.49-.413.422-.854.845-1.267 1.267-.084.087-.234.231-.234.231-.404.452-.394 1.153.009 1.518.01.01.019.01.028.02.01.009.019.018.038.028.019.02.047.029.066.048.018.01.047.029.065.038.019.01.047.02.066.03.028.009.047.018.075.028.028.01.047.02.075.02.028.009.056.009.075.019.028 0 .057.01.075.01h.085c.028 0 .056 0 .084-.01.028-.01.057-.01.085-.02.028 0 .056-.01.084-.019l.085-.029.084-.029c.028-.01.047-.029.075-.038.028-.01.047-.029.075-.038.028-.02.047-.03.075-.049.029-.019.047-.038.075-.057.019-.02.047-.038.066-.067.028-.03.056-.048.085-.077.009-.01.009-.02.018-.02a.077.077 0 01.038-.038.075.075 0 01.037-.038c.01-.02.019-.039.038-.048.009-.02.028-.039.038-.058.018-.02.028-.038.046-.058.019-.019.029-.038.047-.067a.274.274 0 00.047-.067c.019-.029.038-.048.057-.077l.056-.086c.019-.03.047-.058.066-.087.018-.029.047-.058.065-.096a.699.699 0 01.075-.096c.019-.029.047-.067.075-.106.029-.038.047-.067.076-.105a.837.837 0 00.075-.116l.084-.115c.028-.038.056-.086.085-.125.028-.038.056-.077.084-.125.028-.038.056-.086.084-.125.029-.038.057-.086.094-.124.029-.039.057-.087.094-.125.028-.039.066-.087.094-.125.028-.048.066-.087.094-.135.028-.038.066-.086.094-.134.028-.039.065-.087.094-.135.028-.038.065-.086.093-.134.029-.039.066-.087.094-.135.028-.038.066-.086.094-.134.028-.038.066-.087.094-.135a.591.591 0 01.094-.124c.028-.039.056-.087.094-.125.028-.039.056-.087.084-.125.028-.039.057-.087.085-.125l.084-.115c.028-.039.056-.077.075-.116l.085-.115c.028-.038.047-.077.075-.115a.437.437 0 01.075-.106c.028-.038.047-.077.075-.106l.075-.105c.197-.288.385-.586.629-.817.244-.23.478-.48.713-.72.713-.73 1.455-1.432 2.168-2.162.713-.73 1.455-1.44 2.168-2.161.235-.24.488-.47.723-.72.478-.48.976-.952 1.445-1.432.244-.24.488-.47.723-.71.478-.49.966-.961 1.445-1.432.732-.71 1.436-1.45 2.177-2.142.122-.116.244-.24.357-.356.357-.365.732-.71 1.089-1.076.234-.24.469-.49.713-.72.122-.115.263-.211.394-.327.789-.653 1.577-1.316 2.356-1.969.92-.769 1.849-1.527 2.759-2.286-6.438 6.791-12.876 13.602-19.258 20.451-.028.03-.047.058-.085.087-.009.02-.028.029-.037.048.018-.01.028-.029.047-.038a578.498 578.498 0 005.377-5.447c1.117-1.143 2.225-2.296 3.323-3.43.666-.682 1.323-1.383 1.989-2.065.892-.913 1.755-1.854 2.647-2.767 1.783-1.825 3.529-3.688 5.312-5.514l2.553-2.612a.79.79 0 01.131-.106c.047.144.141.24.178.374.038.125.047.28.075.413.066.337.123.683.179 1.019.338 2.997.816 7.3.863 7.646.16 1.163.329 2.335.488 3.497.376 2.661 2.215 13.295 2.553 14.025.16.356.479.711.882.75.404.038.873-.385.92-.798.066-.538-.141-1.066-.141-1.604V74.59c0-.413 0-.826-.009-1.239a43.74 43.74 0 00-.113-2.229c-.112-1.498-.338-2.959-.525-4.448-.047-.374-.254-2.756-.263-3.093a14224.21 14224.21 0 016.889 13.824c.103.153.488.144.891-.029.432-.183.704-.49.629-.672v-.01c-1.83-4.66-3.651-9.328-5.518-13.958 1.22 1.633 2.43 3.314 3.641 4.995.075.116.16.202.225.317.075.115.103.24.16.385.722 1.805 1.53 3.573 2.318 5.254-.967-1.72-1.821-3.564-2.619-5.466 0-.01-.009-.01-.009.01.498 1.287.976 2.574 1.52 3.823a27.494 27.494 0 001.831 3.526c.319.528.666 1.076 1.069 1.594.282.356.639.72.977 1.028.328.288.929.557 1.492.404.328-.087.563-.289.76-.567.188-.27.3-.625.282-1.038a3.828 3.828 0 00-.244-1.104c.366-.164.741-.346 1.088-.52.047-.018.113-.038.15-.076-.722-1.575-1.482-3.132-2.196-4.717-.713-1.575-1.483-3.131-2.196-4.716-.723-1.585-1.473-3.142-2.196-4.717-.732-1.566-1.511-3.113-2.224-4.678.281.105.591.182.863.297 3.238 6.85 6.495 13.69 9.826 20.452.01.02.029.02.01 0-3.079-6.762-6.204-13.535-9.282-20.269 3.482 1.172 6.982 2.325 10.493 3.477.018 0 .018-.01 0-.01-1.765-.662-3.548-1.277-5.322-1.92-1.774-.644-3.548-1.288-5.34-1.912 0-.01-.009-.01-.019-.02-.178-.432-.403-.864-.572-1.267 1.567.518 3.181 1.037 4.758 1.537.225.067.45.153.676.22 2.299.712 4.589 1.442 6.917 2.085.459.125.91.298 1.36.442.901.288 1.802.605 2.694.903.892.308 1.783.615 2.656.942.657.25 1.323.48 1.962.758l.459.27c.038.009.085.019.132.009.469-.02 1.088-.865 1.426-2.008.376-1.248.273-2.469-.216-2.613 0 0-.281-.048-.3-.057.113-.548.056-.98-.253-1.076-.028-.01-7.49-2.46-11.122-3.631-.525-.173-1.051-.337-1.586-.51-2.065-.662-4.12-1.325-6.194-1.978.225 0 .45-.01.685-.01.404.135 3.285 1.076 4.176 1.364 1.493.48 2.938.932 4.402 1.412 1.398.461 4.233 1.403 4.252 1.403 0 0 .047.019.094-.01.112-.057.244-.22.309-.432.066-.24.057-.47 0-.576 0 0-.019-.048-.075-.068a1.172 1.172 0 01-.094-.029c-.403-.144-.798-.288-1.192-.432-1.389-.48-2.768-.95-4.139-1.44-.91-.327-1.83-.644-2.749-.961-.329-.106-.657-.231-.976-.346 9.009-.317 18.667-1.038 14.96-1.672zm-59.775-5.34c-.535-.126-1.051-.327-1.548-.49.14.009.272.076.413.115.272.086.553.172.844.25.132.038.282.067.394.124-.018.039-.065.01-.103 0zm23.444-19.8c.029-.22.047-.46.066-.681.038-.327.085-.634.113-.932.131.816.225 1.662.366 2.478.037.23.084.529.094.817-.188-.404-.385-.836-.582-1.259-.028-.067-.075-.134-.075-.182-.019-.077.009-.154.018-.24zm-.253 2.085c.019-.163.056-.403.075-.557.263.548.535 1.124.798 1.69.037.087.094.154.112.231.019.087.01.192.019.288.019.27.028.558.047.856-.319-.692-.666-1.451-1.004-2.172-.056-.125-.066-.163-.047-.336zm-.357-8.665c-.047.48-.112 1-.159 1.48-.075.71-.15 1.412-.216 2.084.16-1.23.3-2.478.469-3.689.057.23.01.529-.018.798-.113 1.364-.273 2.776-.404 4.13-.066-.134-.131-.259-.197-.403a.958.958 0 01-.057-.144c-.009-.087.019-.202.038-.307.178-1.538.366-3.16.544-4.65.076.202.019.461 0 .701zm-2.646 2.296c-.038-.336-.085-.653-.122-.99.638 1.326 1.248 2.671 1.886 3.997-.028.22-.056.442-.075.672-.572-1.22-1.117-2.45-1.689-3.679zm1.558 4.717c-.019.115-.038.24-.047.365-.394-.884-.751-1.777-1.145-2.68-.028-.058-.066-.115-.085-.173-.037-.135-.018-.288-.056-.413.46.96.882 1.94 1.333 2.9zm-2.168-4.784c.169.346.319.71.478 1.057.01.105.029.192.029.307-.169-.375-.329-.768-.498-1.134.009-.096-.019-.153-.009-.23zm.206-1.537c.028.307.066.596.094.893-.178-.365-.338-.759-.516-1.124-.028-.297-.066-.586-.094-.874.178.365.338.75.516 1.105zm-.704-2.623c-.028-.288-.065-.557-.094-.845.198.403.366.826.554 1.24.028.23.047.45.075.691-.178-.355-.347-.73-.535-1.086zm.685.913c.395.893.798 1.777 1.155 2.709-.028.029-.028-.02-.038-.029-.094-.125-.169-.298-.244-.451a83.65 83.65 0 01-.666-1.316c-.047-.087-.113-.173-.131-.26-.029-.086-.029-.211-.038-.317 0-.115-.028-.22-.038-.336zm2.121 8.367c.094.164.16.346.245.51 0 .153-.038.278-.047.422-.085-.173-.151-.355-.235-.529.009-.143.037-.259.037-.403zm.198-.912c-.019-.039-.057-.087-.066-.125-.009-.077.028-.23.037-.327.019-.125.029-.25.047-.346.094.163.16.346.254.51-.028.23-.066.441-.085.681-.075-.115-.131-.259-.187-.393zM128.635 1.419c-.019-.038-.037-.115-.066-.163-.018-.048-.056-.096.029-.116.028-.01.037.039.046.058.076.173.151.365.226.548.582 1.45 1.192 2.958 1.783 4.428-.676-1.566-1.342-3.18-2.018-4.755zm2.159 2.824c-.244-.557-.498-1.133-.761-1.729-.197-.442-.441-1.018-.666-1.508 0-.029-.028-.077-.037-.115-.038-.106-.047-.077.037-.106.056.048.066.135.094.202.695 1.546 1.417 3.122 2.13 4.678.986 2.161 1.981 4.313 2.976 6.494.206.451.431.941.656 1.422.029.067.085.134.094.192.019.057.01.134.019.201.019.183.056.394.075.586-1.567-3.4-3.078-6.858-4.617-10.317zm4.805 2.162c.037.096.047.23.066.355-.01-.26-.057-.528-.085-.788a163.764 163.764 0 01-.338-3.189c-.037-.355-.019-.768.179-1.076.159-.25.488-.5.788-.49.544.02.995.72 1.164 1.172.112.27.225.567.309.855.085.288.151.577.244.874.357 1.201.639 2.44.892 3.699.253 1.258.497 2.584.704 3.9.188 1.172.357 2.286.544 3.477.019.173.066.346.066.51 0 .105-.028.22-.038.336-.187 1.623-.413 3.372-.6 4.986-.723-1.576-1.483-3.247-2.225-4.861-.046-.106-.112-.221-.14-.317-.047-.154-.047-.346-.075-.529-.16-1.239-.31-2.459-.47-3.708-.197-1.47-.366-2.891-.553-4.37.009.336.065.672.103 1.008.272 2.354.516 4.736.779 7.118a49.33 49.33 0 01-.488-1.047c-.019-.048-.057-.105-.066-.144-.019-.067-.019-.163-.028-.25-.131-1.19-.244-2.45-.366-3.63-.132-1.317-.272-2.604-.366-3.891zm-3.351 9.548c.075.135.169.394.226.529 1.257 3.103 2.505 6.205 3.782 9.308.169.404.338.836.507 1.259.028.057.056.115.075.173.028.124.019.23.037.374-1.548-3.89-3.069-7.694-4.589-11.537-.009-.029-.019-.048-.038-.086-.009-.02-.037-.077 0-.02zm2.084 30.789c.141-.145.281-.317.432-.461.112.038.234.067.337.105.029.01-.028.058-.046.067-.16.173-.357.375-.535.538a2.416 2.416 0 00-.31-.105c0-.049.075-.096.122-.144zm-.319-.01c-.019.02-.038.058-.066.058-.037-.02.028-.048.047-.068.15-.163.329-.355.479-.509-.122.135-.301.356-.46.519zm-1.783-30.836c-.029.01-.038-.087-.057-.116-.009 0-.009-.01-.009-.019.028.039.037.096.066.135zm-.085-.183c.009.01.009.02.019.03 0 .018-.019-.02-.019-.03zm0-.019c-.009.02-.019-.01-.019-.029.01.01.01.02.019.029zm-.047-.106c0 .02-.019-.019-.019-.028.01 0 .019.019.019.028zm-.028-.076c.009.01.009.019.019.028-.01.02-.028-.019-.019-.028zm0-.02c-.009.02-.019-.019-.019-.029 0 .01.01.02.019.03zm-.047-.105c0 .019-.019-.01-.019-.03.01.01.01.02.019.03zm.019.038c0 .02-.019-.02-.019-.029.01.01.01.02.019.029zm.985 9.165c.517 1.104.911 2.276 1.38 3.429-.253-.538-.544-1.066-.788-1.614-.244-.567-.46-1.143-.695-1.71-.469-1.133-.929-2.286-1.389-3.42.516 1.095.986 2.21 1.492 3.315zm-1.257 1.008c.281.576.516 1.182.769 1.777.254.596.507 1.182.751 1.787a171.814 171.814 0 01-2.731-4.87c-.15-.279-.3-.548-.45-.827-.151-.278-.273-.576-.395-.874a475.867 475.867 0 01-2.909-7.243c0-.038.047.048.056.067.535 1.096 1.07 2.21 1.615 3.305.563 1.143 1.088 2.296 1.642 3.43.563 1.152 1.098 2.305 1.652 3.448zm-5.5-23.535c.338.835.666 1.7 1.004 2.536.047.125.085.25.16.355-.319-.999-.676-1.97-1.033-2.94l.047-.019a438.52 438.52 0 012.731 6.427c-.14.058-.291.115-.422.192-.347-.816-.676-1.614-1.014-2.43-.112-.27-.215-.548-.319-.826-.216-.548-.459-1.057-.694-1.595-.225-.51-.432-1.047-.479-1.7h.019zm-.366.134l.028-.01c.826 2.027 1.68 4.064 2.525 6.1.028.068.094.202.122.308 0-.01.028.048.028.048 0 .038-.009.029-.038.038-.037.02-.047 0-.065-.048a2.251 2.251 0 01-.113-.317c-.807-2.055-1.661-4.082-2.487-6.119zm-1.558 12.373c.047.038.056.106.094.154.206.384.432.778.61 1.21.197.49.403.99.582 1.499-.451-.932-.854-1.912-1.286-2.863zm.798 1.518c0-.039.009 0 .019 0a461.275 461.275 0 001.623 3.103c.094.173.197.336.272.518 1.737 4.275 3.482 8.608 5.369 12.748.15.336.319.663.469 1.018 0 .01.047.039.009.048-.037-.019-.093-.038-.14-.057-2.628-5.707-5.153-11.518-7.621-17.378zm1.389 19.25c.291.116.582.25.873.395 1.68.807 3.416 1.604 5.124 2.41l-1.464.971a8.46 8.46 0 01-.507-.25c-.582-.297-1.211-.566-1.821-.835-.413-.173-.816-.365-1.229-.548-1.211-.547-2.44-1.134-3.557-1.767-.3.047-.582-.03-.826-.116-.713-.25-1.37-.624-2.008-.97a1.316 1.316 0 01-.16-.096 80.216 80.216 0 00-1.783-.941c-.225-.116-.441-.231-.685-.318 0-.01 0-.01.009-.01 2.44.51 4.815 1.087 7.123 1.74.301.086.601.22.911.336zm5.537 5.342c-.216-.058-.422-.163-.638-.25l-.507-.202c.038-.019.066-.038.103-.047.366.153.714.317 1.042.499zm-11.403-9.578c.075.01.16.058.244.097.085.038.178.057.244.096.047.028.103.086.15.124.141.125.31.27.451.385-.479-.192-.976-.452-1.474-.663.132-.01.254-.058.385-.038zm-4.495-.643c.403.192.816.394 1.22.576 1.22.548 2.421 1.163 3.66 1.72-2.018-.433-4.092-.817-6.166-1.22-.141-.029-.291-.039-.404-.087-.056-.019-.112-.067-.169-.105l4.299.346s-2.281-1.105-4.909-2.21c.197.067.394.125.582.183.235.067.469.105.676.21.413.193.798.395 1.211.587zm-4.702-3.564c1.107.403 2.214.797 3.284 1.181a6.262 6.262 0 01-.459-.115c-.451-.125-.911-.269-1.352-.403.038-.02-.009 0 .047 0-.178-.048-.244-.077-.422-.106-.179-.029-.366-.038-.535-.096-.141-.038-.282-.134-.432-.202-.075-.038-.15-.057-.225-.086-.207-.096-.404-.202-.639-.308-.159-.067-.337-.115-.441-.23.413.038.845.25 1.174.365zm6.954 8.252c.357.211.76.432 1.126.643-.751-.317-1.501-.653-2.252-.99-2.253-.998-4.533-1.978-6.814-2.958.15 0 .291.039.441.067h.01l1.567.346c.263.135.554.279.845.365.215.068.469.087.713.087.225 0 .46.01.638.077l1.746 1.21.187.087 3.792 1.633 4.176 1.96c-2.102-.875-4.232-1.73-6.325-2.623.056.029.103.067.15.096zm2.59 7.012c.15.02.301.077.451.125.441.135.91.279 1.361.413-.019.02-.029.01-.047 0-.301-.067-.601-.163-.901-.25-.31-.076-.592-.192-.864-.288zm.948.337c2.328.691 4.655 1.364 6.973 2.055.029.01-.018.039-.018.048-.094.096-.235.202-.338.298-2.206-.797-4.421-1.575-6.617-2.401-.009.01-.009 0 0 0zm2.346 3.439c.808.22 1.596.46 2.403.672a.397.397 0 01-.131.125c-.732-.27-1.539-.528-2.272-.797zm3.961 2.007c-2.74 2.258-5.472 4.515-8.203 6.782-.018.01-.018.01-.009-.01.901-.893 1.783-1.805 2.722-2.67.15-.144.291-.307.45-.451.15-.144.31-.289.451-.442.572-.625 1.192-1.21 1.783-1.816.441-.451.91-.883 1.361-1.335.15-.154.3-.298.469-.432.328.115.666.23.995.346.019 0-.019.019-.019.028zm.516-.442c-.347-.124-.694-.25-1.051-.365.009-.067.122-.115.16-.182.394.106.769.22 1.163.326-.093.077-.169.164-.272.221zm-.732-5.811c-3.088-1-6.203-1.98-9.254-2.998 4.111 1.288 8.259 2.546 12.333 3.872-.047.067-.094.077-.169.057-.188-.048-.395-.124-.582-.182-.779-.24-1.568-.5-2.328-.75zm1.633 3.602c0-.048.047-.077.085-.106.093-.086.197-.182.291-.278 0-.01.018-.01.018-.02.47.125.92.27 1.38.404-.169.144-.347.288-.516.432-.422-.134-.835-.297-1.258-.432zm.591 2.738c.113-.106.226-.202.348-.298l.478.144c-.094.115-.187.23-.291.327-.178-.048-.356-.125-.535-.173zm-16.095 25.505c.835-.73 1.699-1.47 2.543-2.21 2.882-2.526 5.744-5.11 8.616-7.656-.216.355-.469.701-.713 1.057-.038.048-.066.115-.094.153-.047.058-.141.115-.207.173-1.004.845-2.036 1.748-3.031 2.603.159-.096.31-.21.46-.317a46.236 46.236 0 01-1.934 1.653c-1.98 1.614-3.988 3.227-5.969 4.841.075-.105.207-.192.329-.297zm1.286-.74c-.854.73-1.727 1.498-2.581 2.238-.028.02-.047.048-.075.067 0-.048-.01-.096-.019-.153a.849.849 0 01.084-.096c.066-.058.132-.087.207-.125.056-.039.112-.096.169-.144.854-.702 1.755-1.422 2.599-2.133-.112.125-.253.23-.384.346zm.431-.375c.019-.01-.065.048 0 0zm5.65-4.332c-.694.614-1.426 1.22-2.149 1.834-.957.807-1.924 1.624-2.9 2.412.103-.116.225-.202.347-.298.366-.298.732-.596 1.08-.903.469-.413.938-.826 1.407-1.249.47-.413.92-.855 1.408-1.24.488-.393.976-.787 1.483-1.162-.188.221-.441.404-.676.606zm-.065-.125a.578.578 0 01.131-.135c.328-.297.666-.614 1.032-.903.357-.288.714-.576 1.061-.864-.01.086-.103.125-.178.192-.667.547-1.38 1.143-2.046 1.71zm1.942.538c.132-.212.282-.413.432-.634.047-.067.094-.154.141-.212.056-.057.122-.096.187-.144.413-.336.845-.7 1.249-1.047-.629.682-1.342 1.355-2.009 2.037zm1.492-12.123c-.056.048-.159.172-.178.172-.009 0 .085-.096.103-.115.254-.278.526-.557.761-.826.14-.163.309-.298.441-.461.563-.624 1.173-1.23 1.755-1.864.713-.778 1.473-1.556 2.196-2.324.15-.154.3-.308.441-.471.141-.154.291-.307.441-.47.216-.24.432-.462.657-.692.075-.077.132-.173.225-.221.028.019.066.029.104.038-.404.49-.845.951-1.258 1.441.46-.461.901-.95 1.37-1.403.103.039.207.077.319.106-.45.51-.938.97-1.398 1.48a9.238 9.238 0 00-.103.105c-.028.039-.047.087-.075.115-.057.058-.132.116-.188.173-1.877 1.749-3.745 3.459-5.613 5.217zm8.072-10.548c-.122.154-.301.317-.441.47-.057-.009-.094-.028-.141-.047.188-.173.394-.327.591-.5.01 0 .01.01.019.01.028.019-.019.048-.028.067zm.075 2.651c0 .039-.038.068-.066.096-.084.096-.197.193-.291.289a1.481 1.481 0 01-.309-.116c-.038-.01.018-.029.018-.038.085-.096.207-.23.301-.327.131.02.225.077.347.096zm-.582-.173c.028.01.066.02.094.03-.103.134-.216.259-.329.374-.037-.01-.056-.029-.094-.029-.009-.038.047-.067.076-.096.084-.077.169-.192.253-.279zm-3.2 12.47c-.085.105-.197.182-.301.278-.309.269-.638.547-.948.826.057-.106.141-.211.216-.317.028-.038.047-.086.066-.115.066-.067.16-.125.244-.173.235-.163.497-.327.723-.5zm3.744-2.527c-1.895 1.518-3.801 3.084-5.696 4.61.14-.2.262-.393.45-.556.619-.548 1.258-1.115 1.877-1.681.225-.202.451-.413.695-.586 1.023-.692 2.036-1.403 3.04-2.104a2.813 2.813 0 01-.366.317zm.657-14.794c-.516-.154-1.032-.307-1.53-.47-.037-.01.038-.058.057-.077.516.153 1.023.326 1.539.48-.019.038-.047.048-.066.067zm.047-3.324c-.019 0-.103-.038-.169-.057a25.754 25.754 0 01-1.295-.461c.479-.221.967-.442 1.37-.644.132.288.273.586.404.874-.028.067-.085.096-.141.154-.047.048-.112.134-.169.134zm.057 16.264c-.01-.02.018-.029.028-.038.159-.145.328-.289.497-.433a39.362 39.362 0 011.52-1.24 3.545 3.545 0 01-.337.318c1.248-1.066 2.534-2.19 3.801-3.285.178.067.356.124.535.182-1.999 1.508-4.036 2.988-6.044 4.496zm7.038-5.418c-.168.173-.356.384-.544.547-.188-.057-.356-.124-.535-.182.216-.202.451-.394.666-.586.16.038.31.096.479.135.01.038-.047.057-.066.086zm-.366-23.276c-.028-.067-.075-.154-.075-.202-.009-.077.019-.163.028-.25.01-.105.019-.182.029-.269.431.923.844 1.864 1.276 2.786l.056.98c-.441-.999-.872-2.017-1.314-3.045zm4.768 29.078l.028.99c-.507-1-1.004-2.018-1.511-3.017.066.067.132.154.188.24.422.586.873 1.191 1.295 1.787zm-1.436-4.957c.056.173.122.317.179.49a7.513 7.513 0 01-.404-.557c.075.02.141.048.225.067zm-1.023-22.642c.15.298.282.615.441.913.01.105.01.211.019.317-.15-.327-.291-.673-.45-1 .009-.095-.01-.153-.01-.23zm.216 2.027c-.037-.086-.103-.173-.122-.269-.019-.086-.009-.22-.019-.336-.018-.269-.028-.576-.047-.874.085.173.226.48.329.692.047.086.103.182.112.278.019.125.01.317.019.47 0 .049.019.116.01.145-.019.077-.104.115-.169.163a3.087 3.087 0 00-.113-.269zm.141 22.7c.047.029.075.086.112.125.029.048.085.086.104.144.018.048.037.115.056.163.056.144.131.317.178.47-.159-.287-.291-.604-.45-.902zm1.276 6.071c-.112-.23-.366-.922-.666-1.787-.066-.317-.113-.634-.169-.95.516.97 1.023 2.093 1.558 3.112.075.144.159.297.216.451v.02c.028.134.018.316.018.47 0 .202.01.404.019.605a96.954 96.954 0 01-.976-1.921zm12.633 13.026c.159.221.328.461.488.692.441.605.891 1.2 1.323 1.825.216.307.441.624.573.999-.01.038-.066-.048-.094-.077-.46-.509-.901-1.056-1.305-1.642-.432-.635-.788-1.326-1.164-1.999.075.058.122.125.179.202zm-6.026-24.42c.751.203 1.502.404 2.253.596l-.094.087c-.723-.23-1.445-.461-2.159-.682zm1.145 3.709c-.309-.086-.619-.182-.929-.269-.122-.26-.253-.5-.375-.759.319.106.638.202.967.288.112.25.225.49.337.74zm.113-4.918c0 .01.038.019 0 .019-.723-.24-1.436-.471-2.168-.702-.075-.124-.122-.278-.197-.403.103.02.197.087.3.125.676.317 1.38.644 2.065.96zm-1.483-3.776a15.69 15.69 0 01-.601.538c-.544-.25-1.069-.518-1.614-.768-.169-.394-.356-.788-.535-1.172.094-.116.207-.192.31-.288 1.023.374 2.008.778 3.031 1.143-.187.192-.394.365-.591.547zm-1.361 2.863c-.225-.077-.441-.154-.666-.221.103-.125.225-.23.347-.336.038 0 .047.019.085.029.065.172.159.346.234.528zm-.873-.826c-.065.077-.15.154-.234.23-.028.02-.047.058-.085.058-.065-.182-.15-.346-.215-.519.197.068.356.164.534.23zm3.764-3.42c-1.032-.365-2.065-.71-3.097-1.076-.038-.01.028-.048.047-.067.037-.029.065-.067.103-.096 1.023.374 2.027.759 3.05 1.124 0 .038-.075.077-.103.115zm3.848 1.057c2.149.768 6.269 2.228 8.456 2.968.075.029.141.058.216.087.065.019.159.028.188.096-.057.057-.207.047-.291.019a.384.384 0 01-.151-.096c-2.186-.778-6.288-2.248-8.418-3.074-.019.01-.019 0 0 0zm7.733 3.026c.085.029.169.048.244.077.226.086.47.182.704.268.047.02.113.03.141.077-.854-.307-3.651-1.277-4.505-1.585-2.534-.912-5.077-1.825-7.64-2.737.029-.039.066-.068.094-.096 3.004 1.104 7.968 2.881 10.962 3.996zm6.823-19.77c-.159.154-.356.269-.544.423-1.286.99-2.553 2.027-3.81 3.045-2.872 2.344-5.669 4.793-8.447 7.253-1.342 1.19-2.684 2.382-4.036 3.573-.037.038-.075.077-.131.087-1.023-.356-2.037-.73-3.069-1.086-.028-.029.028-.058.037-.067.141-.144.329-.288.479-.433.535-.49 1.098-.989 1.642-1.479.366-.326.742-.663 1.099-.99.187-.163.356-.345.544-.499.938-.788 1.896-1.585 2.853-2.363.188-.154.422-.269.648-.404 4.11-2.468 8.437-4.755 12.735-7.06zm1.521-2.133c-1.427.807-2.91 1.547-4.355 2.325-.206.106-.404.23-.61.336-1.23.663-2.478 1.335-3.688 2.037.065-.077.15-.115.225-.173 1.22-.855 2.553-1.633 3.904-2.373.357-.202.742-.375 1.108-.567.366-.192.75-.365 1.126-.538.751-.365 1.52-.71 2.28-1.056.047 0 .01 0 .01.01zm2.872-4.86c.122-.03.244-.058.366-.077.253-.039.506-.048.76.01.056.009.103.028.159.038.057.01.122.01.169.029.094.028.207.124.282.201.178.164.31.336.441.538.066.106.131.23.15.365.028.154 0 .356-.028.5-.16.864-.882 1.249-1.52 1.633-.057.038-.104.067-.16.105-.056.03-.141.068-.169.087-.094.058-.235.134-.263.154-.075-.02-.159-.068-.263-.039.094-.048.32-.153.423-.201a9.45 9.45 0 001.539-1.076c.188-.183.328-.51.328-.884 0-.404-.234-.711-.45-.942-.075-.076-.15-.173-.235-.22a1.396 1.396 0 00-.394-.154c-.375-.087-.826-.01-1.192.067a19.51 19.51 0 00-2.083.557c-.92.288-1.793.615-2.619.913a29.312 29.312 0 014.759-1.604zm-7.246 4.774c.038-.029.216-.173.216-.173-.019-.029-.066-.087-.141-.183.545-.355 1.089-.71 1.699-.99.61-.278 1.276-.489 1.943-.73a34.207 34.207 0 013.031-.979c.244-.067.479-.144.742-.192.112-.02.234-.038.356-.038.028.01 0 .028-.009.038-.225.173-.526.326-.798.47-.582.318-1.173.616-1.793.904-.309.144-.638.259-.957.393-.319.135-.638.26-.957.394-.929.404-2.628 1.24-2.769 1.326a40.33 40.33 0 00-1.999 1.095c.226-.24.526-.49.836-.759.168-.173.384-.384.6-.576zM148.766 36.5c.244-.22.507-.451.704-.634a294.87 294.87 0 012.863-2.507c.244-.211.488-.413.722-.624.704-.634 1.389-1.297 2.084-1.94 1.868-1.73 3.698-3.43 5.584-5.15.939-.855 1.877-1.72 2.806-2.565 1.408-1.249 2.816-2.555 4.289-3.765.507-.413 1.023-.826 1.568-1.163-.798 1.403-1.953 2.498-3.173 3.516-.244.202-.469.423-.703.634-.892.788-1.765 1.566-2.666 2.344-.028.029-.066.048-.094.077-.488.432-1.013.874-1.511 1.316-.948.836-1.924 1.643-2.862 2.478-.939.846-1.877 1.681-2.825 2.517l-1.417 1.25-2.825 2.516c-1.408 1.258-2.882 2.45-4.308 3.698-.056-.029-.272-.057-.319-.105-.028-.039-.028-.01 0-.039a.704.704 0 01.131-.115c.657-.576 1.314-1.162 1.952-1.739zm-.948 6.504c-.159-.068-.3-.145-.459-.212.075-.086.168-.144.244-.23.009 0 .018 0 .018.01.057.144.132.278.197.432zm.948-8.742c-.694.71-1.361 1.422-2.064 2.142-.01.01-.047.039-.066.067-.028.03-.028.058-.066.039-.019-.01-.037-.067-.028-.096 0 .01.066-.058.075-.068 1.455-1.43 2.891-2.91 4.336-4.342-.694.711-1.445 1.509-2.187 2.258zm-3.012-3.4c0-.097-.019-.212 0-.299.009-.057.122-.124.197-.192.197-.182.403-.384.6-.547.817-.73 1.624-1.46 2.422-2.19.272-.24.554-.48.816-.72.141-.126.273-.241.404-.366l2.018-1.806c-1.051 1.163-2.14 2.325-3.219 3.497-.808.874-1.615 1.748-2.412 2.642-.263.288-.526.585-.808.854 0-.259-.018-.585-.018-.874zm.065 3.91c.01-.078.207-.241.301-.337.713-.75 1.407-1.547 2.102-2.296.3-.327.591-.663.892-.999.488-.538 1.004-1.095 1.492-1.633.694-.778 1.408-1.528 2.102-2.296.094-.115.207-.211.3-.327.789-.884 1.662-1.69 2.487-2.536.413-.422.845-.826 1.268-1.258 1.238-1.268 2.524-2.498 3.801-3.737l.647-.605c.648-.596 1.286-1.22 1.962-1.806.45-.394.901-.778 1.351-1.163.46-.384.92-.768 1.389-1.133.009-.01.019 0 .009 0-2.43 2.632-4.945 5.187-7.423 7.781a181.13 181.13 0 01-1.868 1.93c-.103.107-.216.203-.319.308-.056.058-.094.125-.141.173l-8.916 9.126a94.3 94.3 0 00-.948.951c-.075.077-.15.164-.234.24-.028.02-.047.058-.085.048-.047-.144-.15-.24-.159-.422v-.01h-.01zm.629 18.097l.732 1.076c-.234-.086-.469-.163-.704-.25-.018-.268-.018-.547-.028-.826zm3.857 15.015c-.009.038-.037-.058-.056-.087-.291-.557-.582-1.152-.863-1.719-.301-.615-.61-1.23-.911-1.835-.6-1.23-1.229-2.45-1.83-3.66-.009-.634-.037-1.258-.047-1.902a503.595 503.595 0 013.707 9.203zm1.54-4.582c-.404-.529-.789-1.086-1.173-1.633-1.136-1.605-2.281-3.19-3.426-4.794-.094-.134-.169-.355-.244-.528-.141-.356-.282-.701-.422-1.066-.029-.068-.076-.154-.085-.212-.019-.144 0-.307-.009-.46-.01-.155-.01-.308-.019-.452.366.124.723.25 1.089.365l3.228 4.784c.376 1.344.695 2.68 1.061 3.996.009.01.009.01.018.01 0 .009-.018 0-.018-.01zm-.31-10.654c-.019-.019-.019-.048-.038-.067 0-.01.01 0 .01 0 .234.067.497.135.751.202.065.02.178.038.187.134-.309-.086-.6-.182-.91-.269zm4.89 16.638c-.836-1.095-1.699-2.209-2.544-3.333-.056-.077-.141-.153-.178-.22-.047-.087-.066-.212-.103-.308a66.617 66.617 0 01-1.061-3.036l3.886 6.897zm-2.328-12.891c-.281-.077-.591-.202-.873-.298-.197-.413-.403-.826-.6-1.249.3.096.581.202.872.279.198.422.385.855.601 1.268zm21.971 2.363c.066-.067.131-.067.188-.067.037.134.037.355.009.519-.019.153-.066.345-.178.384-.085.029-.244-.03-.385-.067-1.821-.471-3.642-1-5.425-1.509-1.454-.403-2.881-.816-4.326-1.22-3.839-1.076-7.668-2.19-11.507-3.295-.366-.105-.779-.211-1.163-.336-.01-.01-.029-.01-.047-.019-.104-.048-.226-.02-.244-.154.009 0 .009 0 .009.01.479.125.948.269 1.417.394 1.417.374 2.844.778 4.28 1.172 1.905.518 3.829 1.018 5.744 1.518 1.914.499 3.819 1.018 5.725 1.536.469.135.929.28 1.398.413.713.202 1.427.385 2.159.567.244.058.488.116.713.192.46.154.901.317 1.351.462.057-.048.094-.116.085-.183-.01-.106-.132-.144-.244-.173.15.029.3-.029.441-.144zm-.544.106c.018.01.037.019.065.029h.019c-.028-.01-.056-.01-.084-.02v-.01zm-2.966-.923c.169.048.272.087.394.125.835.27 1.689.529 2.534.798a2.319 2.319 0 01-.441-.068c-.094-.028-.197-.076-.291-.115a40.83 40.83 0 00-2.074-.692c.178.068.366.135.544.202-.178-.029-.356-.086-.535-.144-1.22-.375-2.468-.768-3.688-1.172-1.399-.46-2.806-.884-4.205-1.354a829.36 829.36 0 017.762 2.42zm-14.463-4.476c.179.058.366.125.545.192.891.327 1.83.644 2.74.941 2.985.961 5.978 1.883 8.925 2.863a2498.507 2498.507 0 01-17.55-4.995c-.056-.038-.056-.115-.084-.173-.085-.173-.179-.355-.254-.547.76.23 1.521.45 2.271.691 1.145.346 2.272.672 3.407 1.028zm2.619-3.871a.793.793 0 00-.15-.404c4.204-.125 11.356-.307 12.792-.163 1.492.144-7.593.422-12.642.567z"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "xtramail_logo_svg__clip0_1_2"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M0 0h811v137H0z"
  })))));
}

const NAME_TO_LOGO_SVG_MAP = {
    xtramail_logo: SvgXtramailLogo,
};
const defaultXtraMailHeight = 70;
const defaultXtraMailWidth = 224;
const Logo = ({ name, height, width, fill, className }) => {
    const svg = NAME_TO_LOGO_SVG_MAP[name];
    // Although we can condense definedHeight or definedWidth into a single line, it is designed to be easily modified to accommodate additional logos in the future.
    const getHeight = () => {
        if (!height && name === 'xtramail_logo') {
            return defaultXtraMailHeight;
        }
        return height;
    };
    const getWidth = () => {
        if (!width && name === 'xtramail_logo') {
            return defaultXtraMailWidth;
        }
        return width;
    };
    return svg ? (React__default.createElement(SvgIcon, { component: svg, className: className, height: getHeight(), width: getWidth(), fill: fill })) : null;
};

function PasswordInput({ disabled, error, isPasswordStrengthEnabled = false, name, onChange, title, value, placeholder, passwordMinimalLength, }) {
    const { Text, TextInput } = useComponentLibrary();
    return (React__default.createElement(FormRow, null,
        React__default.createElement(Text, { variant: "bodySDemi" }, title),
        React__default.createElement(Spacer, { variant: "spacing-half" }),
        React__default.createElement(TextInput, { name: name, type: "password", isError: Boolean(error), errorText: error === null || error === void 0 ? void 0 : error.message, showPasswordStrengthMeter: isPasswordStrengthEnabled, disabled: disabled, onChange: onChange, value: value, placeholder: placeholder, passwordMinimalLength: passwordMinimalLength })));
}

const SKINNY_BROADBAND_LOGIN = 'https://www.broadbandunlimited.nz/login';
const DEFAULT_REDIRECT_URL = '/';
const HELP_PAGE_URL = 'https://www.spark.co.nz/help';
const CONTENT_DAM_BASE_URL = 'https://spark.co.nz/content/dam';

const ULA_BACKGROUNDS_BASE_URL = `${CONTENT_DAM_BASE_URL}/spark/images/backgrounds/generic/ula`;
const SPARK_BACKGROUND = `${ULA_BACKGROUNDS_BASE_URL}/background_spark.svg`;
const SPARK_DARK_BACKGROUND = `${ULA_BACKGROUNDS_BASE_URL}/background_spark_dark.svg`;
const XTRAMAIL_BACKGROUND = `${ULA_BACKGROUNDS_BASE_URL}/background_xtramail.svg`;
const isXtraMailTheme = (brand) => {
    const xtraMailTheme = useTheme().palette.baseColors.poroporo;
    const sparkTheme = useTheme().palette.ulaContainerBackgroundColor;
    return (brand === null || brand === void 0 ? void 0 : brand.isXtraMail) ? xtraMailTheme : sparkTheme;
};
const getSvgBackground = (brand) => {
    const { name } = useTheme();
    if (brand === null || brand === void 0 ? void 0 : brand.isXtraMail) {
        return XTRAMAIL_BACKGROUND;
    }
    if (name === 'darkTheme') {
        return SPARK_DARK_BACKGROUND;
    }
    return SPARK_BACKGROUND;
};
const AppContainer = styled.div `
  ${({ theme: { palette, media }, brand }) => css `
    display: flex;
    justify-content: center;
    height: 100%;
    min-height: 100vh;
    overflow: hidden;
    background-color: ${palette.backgroundColour};
    ${media.up('slim')} {
      background-color: ${isXtraMailTheme(brand)};
      background-image: url('${getSvgBackground(brand)}');
      background-size: 100%;
      background-position: bottom center;
      background-repeat: no-repeat;
    }
  `}
`;
const Container$2 = styled.div `
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme: { spacing } }) => spacing.s3};
`;
const Card = styled.div `
  ${({ theme: { palette, media, spacing, borderRadius } }) => css `
    border-color: ${palette.baseColors.moeraki};
    color: ${palette.baseColors.newZealandBlack};
    box-sizing: border-box;
    background-color: ${palette.backgroundColour};
    border-style: solid;
    position: relative;

    ${media.down('lil')} {
      max-width: 376px;
      width: 100%;
      padding: 0 ${spacing.s3} ${spacing.s3};
      border-width: 0;
    }
    ${media.up('slim')} {
      width: 376px;
      padding: ${spacing.s5} ${spacing.s4} ${spacing.s4};
      border-width: 1px;
      border-radius: ${borderRadius.r2};
    }
    ${media.up('big')} {
      width: 424px;
      padding: ${spacing.s6} ${spacing.s5} ${spacing.s4};
    }
  `};
`;
const LogoContainer = styled.div `
  ${({ theme: { media, spacing, zIndex } }) => css `
    z-index: ${zIndex.overlay};
    padding: ${spacing.s3};
    ${media.up('slim')} {
      padding: ${spacing.s3} 0;
    }
  `}
`;
const StyledLogo = styled(Logo$1) `
  ${({ theme: { media, palette } }) => css `
    fill: ${palette.baseColors.sparkPurple};
    width: 120px;
    height: 47px;
    margin-left: -5px;
    ${media.up('slim')} {
      margin-left: 0px;
      width: 158px;
      height: 61px;
    }
  `}
`;
const XtramailLogo = styled(Logo) `
  ${({ theme: { media, palette } }) => css `
    color: ${palette.baseColors.sparkPurple};

    width: 160px;
    height: 27px;
    ${media.up('slim')} {
      color: white;
      width: 224px;
      height: 70px;
    }
  `}
`;
const PageContainer = ({ children }) => {
    const brand = useBrand();
    const { isSpark, isXtraMail, isMySB } = useBrand();
    const isSparkLogo = isSpark || isMySB;
    return (React__default.createElement(AppContainer, { brand: brand },
        React__default.createElement(Container$2, { brand: brand },
            React__default.createElement(LogoContainer, null,
                isSparkLogo ? React__default.createElement(StyledLogo, { name: "horizontal_logo" }) : null,
                isXtraMail ? React__default.createElement(XtramailLogo, { name: "xtramail_logo" }) : null),
            React__default.createElement(Card, null, children))));
};

styled(Logo$1) `
  ${({ theme: { media, spacing } }) => css `
    margin-top: ${spacing.s3};
    width: 130px;
    height: 47px;

    ${media.up('slim')} {
      width: 166px;
      height: 61px;
    }
  `}
`;
const SocialLoginButton = styled(Button) `
  ${({ theme: { spacing, palette } }) => css `
    flex: 1;
    margin: 0px ${spacing.s1};
    border-color: ${palette.baseColors.alto};
    &:last-child {
      margin-right: 0;
    }

    &:first-child {
      margin-left: 0;
    }

    :disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `};
`;
const TextButton = styled(Button) `
  ${bodySDemiStyle}
  padding: 0;
  height: auto;
  color: ${({ theme: { palette } }) => palette.baseColors.lupin};
`;
const FeedbackAlert = styled(FeedbackAlert$1) `
  color: ${({ theme: { palette } }) => palette.baseColors.wairua};
`;
const InlineLink = styled(Link) `
  ${({ variant }) => variant === 'bodySDemiStyle' ? bodySDemiStyle : footNoteMediumStyle}
  display: 'inline';
  text-decoration: none;
`;

const SparkComponentLibrary = {
    Button: ({ disabled, variant, children, onClick, isLoading, type = 'button', }) => (React__default.createElement(Button, { disabled: disabled, onClick: onClick, variant: variant, isLoading: isLoading, type: type }, children)),
    Checkbox: ({ label, checked, onChange, disabled }) => (React__default.createElement(Checkbox, { label: label, checked: checked, onCheck: onChange, disabled: disabled })),
    ChoiceBox,
    ChoiceboxGroup,
    CodeInput,
    FeedbackAlert: ({ variant, children }) => {
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FeedbackAlert, { variant: variant }, children)));
    },
    Icon: ({ name, fill }) => React__default.createElement(Icon, { name: name, fill: fill }),
    InlineLink,
    LoadingSpinner,
    PageContainer,
    InfoBanner: ({ variant, text, onClick, buttonText }) => (React__default.createElement(InfoBanner, { variant: variant, text: text, onClick: onClick, buttonText: buttonText })),
    SocialLoginButton: React__default.forwardRef(({ disabled, children, onClick, variant }, ref) => (React__default.createElement(SocialLoginButton, { disabled: disabled, onClick: onClick, ref: ref, variant: variant, type: "button" }, children))),
    Spacer: ({ variant }) => React__default.createElement(Spacer, { variant: variant }),
    Text: ({ children, variant, color, component }) => (React__default.createElement(Text, { variant: variant, color: color, component: component }, children)),
    TextButton: ({ children, onClick }) => (React__default.createElement(TextButton, { variant: "textbutton", type: "button", onClick: onClick }, children)),
    TextInput: ({ name, type, isError, errorText, onChange, showPasswordStrengthMeter, value, placeholder, passwordMinimalLength, }) => (React__default.createElement(TextInput, { name: name, type: type, isError: isError, errorText: errorText, onChange: (_value, _name, event) => onChange(event), showPasswordStrengthMeter: showPasswordStrengthMeter, value: value, placeholder: placeholder, passwordMinimalLength: passwordMinimalLength })),
    // Functions
    getEmptyCode,
    transformCodeToString,
    skinType: 'Spark',
};

const FEATURE_FLAGS_LOCAL_STORAGE_KEY = 'online-web-frontend-feature-flags';
function readLocalStorageFlags() {
    try {
        return JSON.parse(localStorage.getItem(FEATURE_FLAGS_LOCAL_STORAGE_KEY) || '');
    }
    catch (_a) {
        return {};
    }
}
const FeatureFlagsContext = createContext(null);
function FeatureFlagsProvider({ children, flags, }) {
    const contextValue = Object.assign(Object.assign({}, flags), readLocalStorageFlags());
    return (React__default.createElement(FeatureFlagsContext.Provider, { value: contextValue }, children));
}
function useFeatureFlags() {
    const context = useContext(FeatureFlagsContext);
    if (!context) {
        throw new Error('useFeatureFlags should be used inside FeatureFlagsProvider');
    }
    return context;
}

var Flags;
(function (Flags) {
    Flags["flagEnableRegisterJourney"] = "flagEnableRegisterJourney";
    Flags["flagEnableForgotEmailJourney"] = "flagEnableForgotEmailJourney";
    Flags["flagEnableImpersonateJourney"] = "flagEnableImpersonateJourney";
    Flags["flagEnableAppSSOJourney"] = "flagEnableAppSSOJourney";
    Flags["flagEnablePasswordResetByCode"] = "flagEnablePasswordResetByCode";
    Flags["flagEnableLoginRememberMe"] = "flagEnableLoginRememberMe";
    Flags["flagEnableFacebookLogin"] = "flagEnableFacebookLogin";
    Flags["flagEnableGoogleLogin"] = "flagEnableGoogleLogin";
    Flags["flagEnableAppleLogin"] = "flagEnableAppleLogin";
    Flags["flagEnableForgotUserIdJourney"] = "flagEnableForgotUserIdJourney";
    Flags["flagEnableSecurityQuestionJourney"] = "flagEnableSecurityQuestionJourney";
})(Flags || (Flags = {}));
const SKINNY_FEATURE_FLAGS = {
    [Flags.flagEnableRegisterJourney]: true,
    [Flags.flagEnableForgotEmailJourney]: false,
    [Flags.flagEnableImpersonateJourney]: true,
    [Flags.flagEnableAppSSOJourney]: true,
    [Flags.flagEnableForgotUserIdJourney]: false,
    [Flags.flagEnableSecurityQuestionJourney]: false,
    [Flags.flagEnablePasswordResetByCode]: false,
    [Flags.flagEnableLoginRememberMe]: false,
    [Flags.flagEnableFacebookLogin]: true,
    [Flags.flagEnableGoogleLogin]: true,
    [Flags.flagEnableAppleLogin]: false,
};
const SPARK_FEATURE_FLAGS = {
    [Flags.flagEnableRegisterJourney]: true,
    [Flags.flagEnableForgotEmailJourney]: true,
    [Flags.flagEnableImpersonateJourney]: true,
    [Flags.flagEnableAppSSOJourney]: true,
    [Flags.flagEnableForgotUserIdJourney]: false,
    [Flags.flagEnableSecurityQuestionJourney]: false,
    [Flags.flagEnablePasswordResetByCode]: false,
    [Flags.flagEnableLoginRememberMe]: false,
    [Flags.flagEnableFacebookLogin]: true,
    [Flags.flagEnableGoogleLogin]: true,
    [Flags.flagEnableAppleLogin]: false,
};
const XTRAMAIL_FEATURE_FLAGS = {
    [Flags.flagEnableRegisterJourney]: false,
    [Flags.flagEnableForgotEmailJourney]: false,
    [Flags.flagEnableImpersonateJourney]: false,
    [Flags.flagEnableAppSSOJourney]: false,
    [Flags.flagEnableForgotUserIdJourney]: false,
    [Flags.flagEnableSecurityQuestionJourney]: false,
    [Flags.flagEnablePasswordResetByCode]: true,
    [Flags.flagEnableLoginRememberMe]: true,
    [Flags.flagEnableFacebookLogin]: false,
    [Flags.flagEnableGoogleLogin]: false,
    [Flags.flagEnableAppleLogin]: false,
};
const MYSB_FEATURE_FLAGS = {
    [Flags.flagEnableRegisterJourney]: false,
    [Flags.flagEnableForgotEmailJourney]: false,
    [Flags.flagEnableImpersonateJourney]: false,
    [Flags.flagEnableAppSSOJourney]: false,
    [Flags.flagEnableForgotUserIdJourney]: true,
    [Flags.flagEnableSecurityQuestionJourney]: true,
    [Flags.flagEnablePasswordResetByCode]: false,
    [Flags.flagEnableLoginRememberMe]: false,
    [Flags.flagEnableFacebookLogin]: false,
    [Flags.flagEnableGoogleLogin]: false,
    [Flags.flagEnableAppleLogin]: false,
};
const featureFlagsByBrand = {
    Spark: SPARK_FEATURE_FLAGS,
    Skinny: SKINNY_FEATURE_FLAGS,
    XtraMail: XTRAMAIL_FEATURE_FLAGS,
    MySB: MYSB_FEATURE_FLAGS,
};

const Container$1 = styled.div `
  height: 106px;
`;
const PageNotAvailableMessage = ({ pageName }) => {
    const { Text, Spacer } = useComponentLibrary();
    return (React__default.createElement(Container$1, null,
        React__default.createElement(Text, { variant: "subheadingL" }, pageName),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(Text, { variant: "bodySMedium" },
            pageName,
            " is not available")));
};

function withPageBrandGuard(flagKey, PageComponent, fallback) {
    return function WrapperComponent(props) {
        const { [flagKey]: flagEnabled } = useFeatureFlags(); // Retrieve the feature flag value based on the provided key
        const FallbackComponent = fallback;
        if (!flagEnabled) {
            return FallbackComponent ? React__default.createElement(FallbackComponent, null) : null;
        }
        return (React__default.createElement("div", { "data-testid": "journey-container" },
            React__default.createElement(PageComponent, Object.assign({}, props))));
    };
}

const ForgotEmailConfirmation = ({ onSignInResult, maskedEmail, }) => {
    const { Text, TextButton, Spacer } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny } = useBrand();
    const messageContent = `We've emailed you. You'll receive a reminder on ${maskedEmail}.`;
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, "Recover email"),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(Text, { variant: "bodySDemi" }, isSkinny ? (React__default.createElement(ContentText, { fontSize: 16, lineHeight: 20, fontWeight: 400 }, messageContent)) : (messageContent)),
        React__default.createElement(Spacer, { variant: "spacing-04" }),
        React__default.createElement(LinkRow, { centered: true },
            React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))));
};

const ALLOWED_SPECIAL_CHARACTERS = '!"#$%&\'()*+,-./:;<=>?@\\[\\\\\\]^_`{|}~';
const isEmailValid = (email) => {
    if (email.length > 50) {
        return 'Email exceeded maximum length';
    }
    const emailRegex = new RegExp(
    // POTENTIALLY CPU INTENSIVE REGEX (Taken from Gen3 App)
    '[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,64}');
    const isValidatedEmail = emailRegex.test(email);
    if (!isValidatedEmail) {
        return 'Please enter a valid email address';
    }
    return true;
};
const isUserIdValid = (userId) => {
    const mySbEmailRegex = new RegExp(/^([\w+-.%]+@[\w-.]+\.[a-z]{2,13},?)+$/);
    const sparkUserIdRegex = [
        new RegExp(/^t[\d]{6}@spark$/),
        new RegExp(/^t[\d]{6}$/),
    ];
    const isValidatedUserId = [mySbEmailRegex, ...sparkUserIdRegex].some((regex) => regex.test(userId.toLowerCase()));
    if (isValidatedUserId) {
        return true;
    }
    return 'Please enter a valid User ID';
};
const engAlphabetOnlyRegex = /^[a-zA-Z]+$/;
const isValidName = (name) => {
    const nameRegex = new RegExp("^[A-Za-z-'~]*$");
    const isValidatedName = nameRegex.test(name);
    if (!isValidatedName) {
        return 'Please enter a valid name';
    }
    return true;
};
const isPasswordValid = (password, passwordMinimalLength = 8) => {
    if (password.length > 100) {
        return false;
    }
    const passwordRegex = new RegExp(`^(?=(?:.*[a-z]))(?=(?:.*[A-Z]))(?=(?:.*[0-9]))(?=(?:.*[${ALLOWED_SPECIAL_CHARACTERS}]))[a-zA-Z0-9${ALLOWED_SPECIAL_CHARACTERS}]{${passwordMinimalLength},}$`);
    if (!passwordRegex.test(password)) {
        return false;
    }
    return true;
};
const isPasswordEntered = (password) => {
    if (password.length === 0) {
        return false;
    }
    if (password.length > 100) {
        return 'Password exceeded maximum length';
    }
    return true;
};

const ERROR_MESSAGES_MAP$d = {
    4023: {
        message: `Sorry, we couldn't find a Spark ID associated with the details provided.`,
    },
    4040: { message: `Sorry - We couldn't find any match for your name` },
};
const onRequestEmailOptions$1 = ([firstName, lastName, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield httpClient.get(`/v1/user/identity?firstName=${firstName}&lastName=${lastName}`, {}, {}, false, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
    if (response.identities.length === 0) {
        throw new ValidationError('Identities length is zero', 4040);
    }
    return response;
});
const ForgotEmailDetailsForm = ({ onSignInResult, onSaveDetails, }) => {
    const { Text, TextButton, Button, Spacer, TextInput } = useComponentLibrary();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const { control, handleSubmit, formState, watch } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            firstName: '',
            lastName: '',
        },
    });
    const { errors } = formState;
    const { firstName, lastName } = watch();
    const { mutate, error, isLoading } = useMutation({
        mutationFn: onRequestEmailOptions$1,
        onSuccess: (res) => {
            if (res.identities.length) {
                onSaveDetails(firstName, lastName, res.identities);
            }
        },
    });
    function submitHandler(formData) {
        mutate([formData.firstName, formData.lastName, universalLoginBrand]);
    }
    return (React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, "Recover email"),
            error ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$d }))) : null,
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('recoverEmail.enterYourName')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('firstName')),
                React__default.createElement(Controller, { control: control, name: "firstName", rules: {
                        required: true,
                        validate: isValidName,
                    }, render: ({ field }) => {
                        var _a;
                        return (React__default.createElement(TextInput, { errorText: (_a = errors === null || errors === void 0 ? void 0 : errors.firstName) === null || _a === void 0 ? void 0 : _a.message, disabled: isLoading, isError: Boolean(errors.firstName), name: field.name, type: "text", value: field.value, onChange: field.onChange }));
                    } })),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('lastName')),
                React__default.createElement(Controller, { control: control, name: "lastName", rules: {
                        required: true,
                        validate: isValidName,
                    }, render: ({ field }) => {
                        var _a;
                        return (React__default.createElement(TextInput, { isError: Boolean(errors.lastName), errorText: (_a = errors === null || errors === void 0 ? void 0 : errors.lastName) === null || _a === void 0 ? void 0 : _a.message, name: field.name, type: "text", disabled: isLoading, onChange: field.onChange, value: field.value }));
                    } })),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: !firstName || !lastName || isLoading, isLoading: isLoading, type: "submit", variant: "primary" }, getText('recoverEmail.continue'))),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn'))))));
};

const ERROR_MESSAGES_MAP$c = {
    4002: {
        message: "The parameter is invalid and doesn't match one of the allowed values.",
    },
    4033: { message: 'The combination of names provided is invalid.' },
    4040: {
        message: "The identity was created using a social sign-in and can't be used for the Spark recovery process.",
    },
    4023: { message: 'No Spark Identity was found.' },
};
const onRequestEmailOptions = ([firstName, lastName, rowId, email, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.put(`/v1/user/identity/${email}/action/sparkForgottenUserId?firstName=${firstName}&lastName=${lastName}&rowId=${rowId}`, {}, 'realmAndBrandInHeaders', universalLoginBrand);
});
const ForgotEmailSelectEmail = ({ onSignInResult, onSelectEmail, emailOptions, firstName, lastName, }) => {
    const [currentlySelected, setCurrentlySelected] = useState(null);
    const { palette } = useTheme();
    const { Text, TextButton, Button, Spacer, ChoiceBox, ChoiceboxGroup } = useComponentLibrary();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const validSelection = currentlySelected !== null;
    const { mutate, error, isLoading } = useMutation({
        mutationFn: onRequestEmailOptions,
        onSuccess: () => {
            if (currentlySelected == null) {
                return;
            }
            onSelectEmail(emailOptions[currentlySelected].email);
        },
    });
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, "Recover email"),
        error ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$c }))) : null,
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(Text, { variant: "bodySDemi" }, getText('recoverEmail.selectYourEmail')),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(ChoiceboxGroup, null, emailOptions.map((identity, index) => (React__default.createElement(ChoiceBox, { key: identity.rowId, checked: currentlySelected === index, value: identity.email, name: "IdentityRadioGroup", onChange: () => setCurrentlySelected(index), disabled: isLoading },
                React__default.createElement(Text, { variant: "bodySMedium", color: palette.textColor }, identity.email)))))),
        React__default.createElement(Spacer, { variant: "spacing-04" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(Button, { isLoading: isLoading, disabled: !validSelection && !isLoading, onClick: () => {
                    if (currentlySelected == null) {
                        return;
                    }
                    mutate([
                        firstName,
                        lastName,
                        String(currentlySelected),
                        emailOptions[currentlySelected].email,
                        universalLoginBrand,
                    ]);
                }, variant: "primary" }, getText('recoverEmail.submit'))),
        React__default.createElement(Spacer, { variant: "spacing-04" }),
        React__default.createElement(LinkRow, { centered: true },
            React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))));
};

var ForgotEmailStep;
(function (ForgotEmailStep) {
    ForgotEmailStep[ForgotEmailStep["RecoveryDetailsForm"] = 0] = "RecoveryDetailsForm";
    ForgotEmailStep[ForgotEmailStep["SelectRecoveryEmail"] = 1] = "SelectRecoveryEmail";
    ForgotEmailStep[ForgotEmailStep["RecoveryConfirmation"] = 2] = "RecoveryConfirmation";
})(ForgotEmailStep || (ForgotEmailStep = {}));
const initialState$7 = {
    step: ForgotEmailStep.RecoveryDetailsForm,
    firstName: '',
    lastName: '',
    maskedEmail: '',
    emailOptions: [],
};
const useForgotEmail = () => {
    const [state, setState] = useState(initialState$7);
    const saveDetailForm = (firstName, lastName, emailOptions) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: ForgotEmailStep.SelectRecoveryEmail, firstName,
            lastName,
            emailOptions })));
    };
    const submitRecoverEmail = (maskedEmail) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { maskedEmail, step: ForgotEmailStep.RecoveryConfirmation })));
    };
    return {
        state,
        saveDetailForm,
        submitRecoverEmail,
    };
};

const ForgotEmail = ({ onSignInResult }) => {
    const { state, submitRecoverEmail, saveDetailForm } = useForgotEmail();
    if (state.step === ForgotEmailStep.RecoveryConfirmation) {
        return (React__default.createElement(ForgotEmailConfirmation, { maskedEmail: state.maskedEmail, onSignInResult: onSignInResult }));
    }
    if (state.step === ForgotEmailStep.SelectRecoveryEmail) {
        return (React__default.createElement(ForgotEmailSelectEmail, { onSignInResult: onSignInResult, onSelectEmail: submitRecoverEmail, emailOptions: state.emailOptions, firstName: state.firstName, lastName: state.lastName }));
    }
    const onSaveDetails = (firstName, lastName, emailOptions) => saveDetailForm(firstName, lastName, emailOptions);
    return (React__default.createElement(ForgotEmailDetailsForm, { onSignInResult: onSignInResult, onSaveDetails: onSaveDetails }));
};
var ForgotEmail$1 = withPageBrandGuard(Flags.flagEnableForgotEmailJourney, ForgotEmail, () => React__default.createElement(PageNotAvailableMessage, { pageName: "Email recovery" }));

var VerificationMethod;
(function (VerificationMethod) {
    VerificationMethod["CODE"] = "Code";
    VerificationMethod["TOKEN"] = "Token";
})(VerificationMethod || (VerificationMethod = {}));
var ForgotPasswordStep;
(function (ForgotPasswordStep) {
    ForgotPasswordStep[ForgotPasswordStep["EnterEmail"] = 0] = "EnterEmail";
    ForgotPasswordStep[ForgotPasswordStep["VerificationCode"] = 1] = "VerificationCode";
    ForgotPasswordStep[ForgotPasswordStep["ConfirmationMessage"] = 2] = "ConfirmationMessage";
    ForgotPasswordStep[ForgotPasswordStep["DisplayError"] = 3] = "DisplayError";
})(ForgotPasswordStep || (ForgotPasswordStep = {}));
const initialState$6 = {
    step: ForgotPasswordStep.EnterEmail,
    email: '',
};
function getNextStep(verificationMethod) {
    if (verificationMethod === VerificationMethod.CODE) {
        return ForgotPasswordStep.VerificationCode;
    }
    return ForgotPasswordStep.ConfirmationMessage;
}
const useForgotPassword = () => {
    const [state, setState] = useState(initialState$6);
    const confirmEmail = (email, verificationMethod) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: getNextStep(verificationMethod), email })));
    };
    return {
        state,
        confirmEmail,
    };
};

const initialInfoBannerState = {
    variant: null,
    text: '',
    buttonText: undefined,
    onClick: undefined,
};
const useInfoBanner = () => {
    const { InfoBanner: LibInfoBanner } = useComponentLibrary();
    const [infoBannerState, setInfoBannerState] = useState(initialInfoBannerState);
    const InfoBanner = ({ spacer }) => {
        const { Spacer } = useComponentLibrary();
        const { variant, text, onClick, buttonText } = infoBannerState;
        if (variant === null) {
            return null;
        }
        return (React__default.createElement(React__default.Fragment, null,
            spacer === 'preceding' ? React__default.createElement(Spacer, { variant: "spacing-02" }) : null,
            React__default.createElement(LibInfoBanner, { variant: variant, text: text, buttonText: buttonText, onClick: onClick }),
            spacer === 'succeeding' ? React__default.createElement(Spacer, { variant: "spacing-02" }) : null));
    };
    return {
        InfoBanner,
        setInfoBannerState,
        infoBannerState,
    };
};

const VERIFY_BY_PARAM = 'verifyBy';
const handleForgotPassword = ([email, universalLoginBrand, verificationMethod,]) => __awaiter(void 0, void 0, void 0, function* () {
    if (universalLoginBrand === 'MySB') {
        return httpClient.put(`/v1/user/Identity/${email}/action/MySparkBusinessForgottenPassword`, {}, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
    }
    const queryString = qs.stringify({
        [VERIFY_BY_PARAM]: verificationMethod,
    });
    return httpClient.put(`/v1/user/identity/${email}/action/sparkForgottenPassword?${queryString}`, {}, 'realmAndBrandInHeaders', universalLoginBrand);
});
const CODE_VERIFICATION_PROCESS_TYPES = {
    XtraMail: 'XtramailForgottenPassword',
};
function getProcessTypeByBrand(universalLoginBrand) {
    const processType = CODE_VERIFICATION_PROCESS_TYPES[universalLoginBrand];
    if (processType) {
        return processType;
    }
    throw new Error('No supported process type was found for this brand');
}
const handleCodeVerification$1 = ([email, code, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    const processType = getProcessTypeByBrand(universalLoginBrand);
    return httpClient.get(`/v1/utility/verification/${processType}/${email}/${code}`, {}, {}, false, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

function getBreadcrumbs() {
    // TODO look out for the breadcrumb custom component and derive value from there
    return [];
}
function pushToDataLayer(event, w3cddl) {
    const digitalEvent = {
        event,
        w3cddl,
    };
    window.digitalData.push(digitalEvent);
}
function trackDigitalEvent(consumerProps) {
    var _a, _b, _c, _d, _e, _f, _g;
    const breadcrumbs = getBreadcrumbs();
    if (!document) {
        return;
    }
    const w3cddl = {
        page: {
            pageInfo: {
                brand: (_a = consumerProps === null || consumerProps === void 0 ? void 0 : consumerProps.page) === null || _a === void 0 ? void 0 : _a.brand,
                pageID: window.location.pathname,
                pageName: ((_b = consumerProps === null || consumerProps === void 0 ? void 0 : consumerProps.page) === null || _b === void 0 ? void 0 : _b.pageName) || (document === null || document === void 0 ? void 0 : document.title),
                destinationURL: window.location.origin + window.location.pathname,
                referringURL: document.referrer,
                pageURLwithQuery: window.location.href,
                breadcrumbs,
                language: document.documentElement.lang, // Ask Jackey if we need this ?
            },
            category: {
                primaryCategory: (_d = (_c = consumerProps === null || consumerProps === void 0 ? void 0 : consumerProps.page) === null || _c === void 0 ? void 0 : _c.category) === null || _d === void 0 ? void 0 : _d.primaryCategory,
                pageType: (_f = (_e = consumerProps === null || consumerProps === void 0 ? void 0 : consumerProps.page) === null || _e === void 0 ? void 0 : _e.category) === null || _f === void 0 ? void 0 : _f.pageType,
            },
            attributes: {
                pageHost: window.location.hostname,
                pageTitle: document.title,
                pageLevel: breadcrumbs.length,
                isVisibleInNav: false,
                subCategories: [...(_g = consumerProps === null || consumerProps === void 0 ? void 0 : consumerProps.page) === null || _g === void 0 ? void 0 : _g.subCategories],
            },
        },
        user: consumerProps.user,
        event: consumerProps.event,
        component: consumerProps.component || [],
    };
    pushToDataLayer(consumerProps.eventName, w3cddl);
}
const analytics = {
    trackDigitalEvent,
};

const componentViewEvent = (componentViewEventProps) => {
    const pageProps = {
        eventName: 'component.view',
        component: [
            {
                componentInfo: {
                    componentId: componentViewEventProps.componentId,
                    componentName: componentViewEventProps.componentName,
                },
                category: {
                    primaryCategory: 'myspark',
                    subCategory: componentViewEventProps.subCategory,
                },
                attributes: {
                    isSuccess: componentViewEventProps.attributes.isSuccess,
                    message: componentViewEventProps.attributes.message,
                },
            },
        ],
        page: {
            brand: componentViewEventProps.brand,
            pageName: componentViewEventProps.pageName,
            category: {
                primaryCategory: '',
                pageType: '',
            },
            subCategories: [componentViewEventProps.subCategory],
        },
        user: [],
        event: [
            {
                eventInfo: {
                    eventName: 'Component View',
                    eventAction: 'component.view',
                },
            },
        ],
    };
    analytics.trackDigitalEvent(pageProps);
};

const trackSuccessRequestEvent = (trackingProps) => {
    analytics.trackDigitalEvent({
        eventName: trackingProps.w3cddlEventName,
        page: {
            brand: trackingProps.brand,
            pageName: trackingProps.pageName,
            category: {
                primaryCategory: 'myspark',
                pageType: 'Landing Page',
            },
            subCategories: [],
        },
        user: [],
        event: [
            {
                eventInfo: {
                    eventAction: trackingProps.w3cddlEventName,
                    eventName: trackingProps.eventName,
                },
                attributes: {
                    isSuccess: true,
                    message: `${trackingProps.eventName} request is successful`,
                },
            },
        ],
    });
};
const trackFailureRequestEvent = (trackingProps) => {
    var _a, _b;
    analytics.trackDigitalEvent({
        eventName: trackingProps.w3cddlEventName,
        page: {
            brand: trackingProps.brand,
            pageName: trackingProps.pageName,
            category: {
                primaryCategory: 'myspark',
                pageType: 'Landing Page',
            },
            subCategories: [],
        },
        user: [],
        event: [
            {
                eventInfo: {
                    eventAction: trackingProps.w3cddlEventName,
                    eventName: trackingProps.eventName,
                },
                attributes: {
                    isSuccess: false,
                    internalErrorCode: ((_a = trackingProps.error) === null || _a === void 0 ? void 0 : _a.code) || 9001,
                    internalErrorMessage: trackingProps.error.message,
                    errorMessage: (_b = trackingProps.errorMap[trackingProps.error.code || 9001]) === null || _b === void 0 ? void 0 : _b.message,
                },
            },
        ],
    });
};

function useTrackPageView(pageProps) {
    useEffect(() => {
        analytics.trackDigitalEvent(pageProps);
    }, []);
}
const getTrackPageProps = ({ brand, pageName, subCategories, primaryCategory = 'myspark', pageType = 'Landing Page', user = [], event = [], }) => {
    return {
        eventName: 'page.load',
        page: {
            brand,
            pageName,
            subCategories,
            category: { primaryCategory, pageType },
        },
        user,
        event,
    };
};

const LinkRegion = ({ children, isNotTracked, regionId, }) => {
    const id = isNotTracked ? 'links-excluded' : regionId;
    return React__default.createElement("div", { "data-regionid": id }, children);
};

const trackForgotPasswordConfirmationComponent = ({ brand, isSuccess, message, }) => {
    const componentViewEventProps = {
        brand,
        pageName: 'forgotPassword.componentView',
        subCategory: 'forgotPassword',
        componentId: 'forgotPassword.resetLink',
        componentName: 'Forgot Password Reset Link',
        attributes: {
            isSuccess,
            message,
        },
    };
    componentViewEvent(componentViewEventProps);
};

const Confirmation = ({ email, onSignInResult }) => {
    const { Text, TextButton, Button, Spacer } = useComponentLibrary();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const { InfoBanner, setInfoBannerState } = useInfoBanner();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotPassword.resetLinkSuccessState',
        subCategories: ['forgotPassword'],
    }));
    const { mutate, isLoading } = useMutation({
        mutationFn: handleForgotPassword,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'A new reset link has been sent',
            });
            trackForgotPasswordConfirmationComponent({
                brand: universalLoginBrand,
                isSuccess: true,
                message: 'A new reset link has been sent.',
            });
        },
        onError: () => {
            setInfoBannerState({
                variant: 'ERROR',
                text: 'There was an error with sending the reset link. Please, try again later.',
            });
            trackForgotPasswordConfirmationComponent({
                brand: universalLoginBrand,
                isSuccess: false,
                message: 'There was an error with sending the reset link. Please, try again later.',
            });
        },
    });
    return (React__default.createElement(LinkRegion, { regionId: "Reset password" },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.resetPassword')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('resetPassword.followTheDirections')),
            React__default.createElement(InfoBanner, { spacer: "preceding" }),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { isLoading: isLoading, disabled: isLoading, onClick: () => mutate([email, universalLoginBrand, VerificationMethod.TOKEN]), variant: "primary" }, getText('resetPassword.sendResetLink'))),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn'))))));
};

const FAIL_TO_SEND_RESET_LINK = 'There was an error with sending the reset link. Please, try again later.';
const FAIL_TO_SEND_RESET_LINK_BY_T_NUMBER = 'Please use the LAN process to retrieve your password.';
const FAIL_TO_SEND_RESET_LINK_LOCKED_USER = 'There was an error with your request. Please contact your administrator.';
const ERROR_MESSAGES_MAP$b = (isSkinny) => {
    return isSkinny
        ? {
            4040: {
                message: 'This email address has been used with social sign in. Sign in with your social account.',
            },
        }
        : {
            4040: {
                message: "This is a social identity and doesn't support password recovery.",
            },
            4023: {
                message: FAIL_TO_SEND_RESET_LINK,
            },
            4022: {
                message: FAIL_TO_SEND_RESET_LINK_BY_T_NUMBER,
            },
            4024: {
                message: FAIL_TO_SEND_RESET_LINK_LOCKED_USER,
            },
        };
};
const ForgotEmailButton = ({ onForgotUserIdResult, onForgotEmailResult, }) => {
    const { TextButton } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny, isMySB } = useBrand();
    if (isSkinny) {
        return null;
    }
    return (React__default.createElement(TextButton, { onClick: isMySB ? onForgotUserIdResult : onForgotEmailResult }, getText('resetPassword.forgotYourEmail')));
};
const EmailForm$1 = ({ onForgotEmailResult, onForgotUserIdResult, onSignInResult, onSendResult, verificationMethod, }) => {
    const { Text, TextButton, Button, Spacer } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny, isMySB } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotPassword.enterEmailState',
        subCategories: ['forgotPassword'],
    }));
    const { control, formState: { errors }, handleSubmit, watch, } = useForm({
        defaultValues: {
            email: '',
        },
        mode: 'onSubmit',
    });
    const { email } = watch();
    const { mutate, error, isLoading } = useMutation({
        mutationFn: handleForgotPassword,
        onSuccess: () => {
            onSendResult(email);
        },
    });
    function submitHandler(formData) {
        mutate([formData.email, universalLoginBrand, verificationMethod]);
    }
    return (React__default.createElement(LinkRegion, { regionId: "Reset password" },
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(Margin, null,
                React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.resetPassword')),
                error ? (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$b(isSkinny) }))) : null,
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('resetPassword.resetDescription')),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(LinkRegion, { isNotTracked: true },
                    React__default.createElement(Controller, { control: control, name: "email", rules: {
                            required: true,
                            validate: isMySB ? isUserIdValid : isEmailValid,
                        }, render: ({ field }) => (React__default.createElement(EmailInput, { disabled: isLoading, error: errors.email, name: field.name, onChange: field.onChange, value: field.value, inputType: isMySB ? 'text' : 'email', label: isMySB ? getText('userIdLabel') : undefined, placeholder: isMySB ? 'Enter your User ID' : undefined })) })),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(ForgotEmailButton, { onForgotEmailResult: onForgotEmailResult, onForgotUserIdResult: onForgotUserIdResult }),
                React__default.createElement(Spacer, { variant: "spacing-04" }),
                React__default.createElement(FormRow, null,
                    React__default.createElement(Button, { isLoading: isLoading, disabled: !email || isLoading, type: "submit", variant: "primary" }, getText('resetPassword.send'))),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(LinkRow, { centered: true },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))));
};

const isCodeValid$1 = (verificationCode) => {
    return verificationCode.every((code) => code.value != null);
};
const useVerificationCodeForm = (codeLength) => {
    const { getEmptyCode } = useComponentLibrary();
    const initialFormState = {
        codeValue: getEmptyCode(codeLength),
        isCodeValid: false,
    };
    const [formState, setFormState] = useState(initialFormState);
    const handleCodeChange = (value) => {
        setFormState({
            codeValue: value,
            isCodeValid: isCodeValid$1(value),
        });
    };
    return {
        isCodeValid: formState.isCodeValid,
        codeValue: formState.codeValue,
        resetForm: () => setFormState(initialFormState),
        handleCodeChange,
    };
};

const CODE_LENGTH$1 = 6;
const ERROR_MESSAGES_MAP$a = {
    4112: { message: 'The verification code has expired. Please try again' },
    4111: { message: 'The verification code is invalid. Please try again' },
    4200: { message: 'Too many attempts. Please request a new code.' },
};
const VerificationCode$2 = ({ email, onSubmitVerificationResult, }) => {
    const { Text, Button, Spacer, CodeInput, transformCodeToString } = useComponentLibrary();
    const { InfoBanner, setInfoBannerState } = useInfoBanner();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const [activeError, setActiveError] = useState(null);
    const { isCodeValid, codeValue, resetForm, handleCodeChange } = useVerificationCodeForm(CODE_LENGTH$1);
    const { mutate, isLoading } = useMutation({
        mutationFn: handleCodeVerification$1,
        onSuccess: () => {
            onSubmitVerificationResult({
                verificationCode: transformCodeToString(codeValue),
                email,
            });
            setInfoBannerState({
                variant: null,
                text: '',
            });
        },
        onError: (err) => {
            resetForm();
            setActiveError(err);
            setInfoBannerState({
                variant: null,
                text: '',
            });
        },
    });
    const { mutate: runResend, isLoading: isLoadingResend } = useMutation({
        mutationFn: handleForgotPassword,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'A new verification code has been sent',
            });
            resetForm();
            setActiveError(null);
        },
        onError: () => {
            resetForm();
            setActiveError(null);
            setInfoBannerState({
                variant: 'ERROR',
                text: 'There was an error sending the new verification code',
            });
        },
    });
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.verifyCodeTitle')),
        React__default.createElement(InfoBanner, { spacer: "preceding" }),
        activeError ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(ErrorMessage, { error: activeError, errorMap: ERROR_MESSAGES_MAP$a }))) : null,
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(LinkRegion, { isNotTracked: true },
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('resetPassword.verifyCodeDescriptionFirstBlock')),
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('resetPassword.verifyCodeDescriptionSecondBlock')),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(CodeInput, { value: codeValue, onChange: handleCodeChange, inputProps: { 'aria-label': 'code-input', autoFocus: true } }))),
        React__default.createElement(Spacer, { variant: "spacing-02" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(Button, { disabled: !isCodeValid || isLoading || isLoadingResend, isLoading: isLoading, onClick: () => {
                    mutate([
                        email,
                        transformCodeToString(codeValue),
                        universalLoginBrand,
                    ]);
                }, variant: "primary" }, getText('resetPassword.verifyCodeVerifyButtonText'))),
        React__default.createElement(Spacer, { variant: "spacing-02" }),
        React__default.createElement(LinkRegion, { regionId: "body" },
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: isLoading || isLoadingResend, onClick: () => {
                        runResend([email, universalLoginBrand, VerificationMethod.CODE]);
                    }, variant: "default", isLoading: isLoadingResend }, getText('resetPassword.verifyCodeResendCodeButtonText'))))));
};

const ForgotPassword = ({ onForgotEmailResult, onForgotUserIdResult, onSignInResult, onSubmitVerificationResult, }) => {
    const { state, confirmEmail } = useForgotPassword();
    const { flagEnablePasswordResetByCode } = useFeatureFlags();
    const verificationMethod = flagEnablePasswordResetByCode
        ? VerificationMethod.CODE
        : VerificationMethod.TOKEN;
    if (state.step === ForgotPasswordStep.ConfirmationMessage) {
        return React__default.createElement(Confirmation, { email: state.email, onSignInResult: onSignInResult });
    }
    if (state.step === ForgotPasswordStep.VerificationCode) {
        return (React__default.createElement(VerificationCode$2, { email: state.email, onSubmitVerificationResult: onSubmitVerificationResult }));
    }
    return (React__default.createElement(EmailForm$1, { verificationMethod: verificationMethod, onSignInResult: onSignInResult, onForgotEmailResult: onForgotEmailResult, onForgotUserIdResult: onForgotUserIdResult, onSendResult: (email) => confirmEmail(email, verificationMethod) }));
};

function useHCaptcha(onSubmitCallback) {
    const captchaRef = useRef(null);
    const [isHCaptchaLoaded, setIsHCaptchaLoaded] = useState(false);
    const onLoad = () => {
        if (!(captchaRef === null || captchaRef === void 0 ? void 0 : captchaRef.current))
            return;
        setIsHCaptchaLoaded(true);
    };
    const initiateCaptcha = () => {
        if (!(captchaRef === null || captchaRef === void 0 ? void 0 : captchaRef.current))
            return;
        captchaRef.current.execute();
    };
    const onVerify = (token) => {
        if (!(captchaRef === null || captchaRef === void 0 ? void 0 : captchaRef.current))
            return;
        onSubmitCallback(token);
        captchaRef.current.resetCaptcha();
    };
    const onExpire = () => {
        if (!(captchaRef === null || captchaRef === void 0 ? void 0 : captchaRef.current))
            return;
        captchaRef.current.resetCaptcha();
    };
    const onError = (err) => {
        console.error(`hCaptcha Error: ${err}`);
    };
    return {
        initiateCaptcha,
        hCaptchaProps: {
            onVerify,
            onError,
            onExpire,
            onLoad,
            isHCaptchaLoaded,
            ref: captchaRef,
        },
    };
}
const HCaptchaElement = React__default.forwardRef((props, ref) => {
    const { hCaptchaKey, zeroRatingProps } = useConfig();
    return (React__default.createElement(HCaptcha, Object.assign({ sitekey: hCaptchaKey, size: "invisible", ref: ref }, props, zeroRatingProps)));
});

const HCaptchaDisclaimerText = () => {
    const { Text, InlineLink } = useComponentLibrary();
    const { isSkinny } = useBrand();
    return (React__default.createElement(Text, { variant: "footnoteMedium", color: !isSkinny ? colors.mawheranui : 'halfCharcoal' },
        "This site is protected by hCaptcha and its",
        ' ',
        React__default.createElement(InlineLink, { href: "https://www.hcaptcha.com/privacy", target: "_blank", rel: "noreferrer" }, "Privacy Policy"),
        ' ',
        "and",
        ' ',
        React__default.createElement(InlineLink, { href: "https://www.hcaptcha.com/terms", target: "_blank", rel: "noreferrer" }, "Terms of Service"),
        ' ',
        "apply."));
};

const RESTRICTED_ENVIRONMENTS = ['nft', 'production'];
function copyText(content) {
    if (content)
        navigator.clipboard.writeText(content);
}
const GenerateCaptchaToken = () => {
    const [captchaToken, setCaptchaToken] = useState(null);
    const { name: envName } = useConfig();
    const { Button } = useComponentLibrary();
    const { hCaptchaProps, initiateCaptcha } = useHCaptcha((token) => {
        setCaptchaToken(token);
    });
    if (RESTRICTED_ENVIRONMENTS.includes(envName))
        return React__default.createElement(ErrorMessage, { error: {}, errorMap: {} });
    useEffect(() => {
        initiateCaptcha();
    }, []);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Button, { variant: "primary", onClick: () => copyText(captchaToken), disabled: !captchaToken }, "Copy Token"),
        React__default.createElement(HCaptchaElement, Object.assign({}, hCaptchaProps)),
        captchaToken));
};

const loginAsImpersonatedUser = ({ uid, redirectURL, universalLoginBrand, }) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.post(`/v1/user/ula/impersonate`, {}, { 'x-identity-username': uid, redirectURL }, true, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

const LoadingContainer$2 = styled.div `
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
const LoadingScreen$2 = () => {
    const { LoadingSpinner, Text } = useComponentLibrary();
    return (React__default.createElement(LoadingContainer$2, null,
        React__default.createElement(Text, { variant: "bodySMedium" }, "Loading"),
        React__default.createElement(LoadingSpinner, { size: 32 })));
};

const ERROR_MESSAGES_MAP$9 = {
    4100: { message: 'Invalid/malformed redirectUrl.' },
    4101: {
        message: 'Redirect URL domain is invalid or domain not configured properly.',
    },
    9001: { message: 'Oops - something went wrong.' },
};
const Impersonate = ({ uid, goto }) => {
    const { brand: universalLoginBrand } = useConfig();
    const { isLoading, mutate, error } = useMutation({
        mutationFn: ([arg]) => loginAsImpersonatedUser(arg),
        onSuccess: () => {
            if (goto) {
                window.location.assign(goto);
            }
        },
    });
    useEffect(() => {
        if (uid && goto) {
            mutate([{ uid, redirectURL: goto, universalLoginBrand }]);
        }
    }, [uid, goto]);
    return (React__default.createElement(React__default.Fragment, null,
        isLoading ? React__default.createElement(LoadingScreen$2, null) : null,
        error ? (React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$9 })) : null));
};
var Impersonate$1 = withPageBrandGuard(Flags.flagEnableImpersonateJourney, Impersonate, () => React__default.createElement(PageNotAvailableMessage, { pageName: "Impersonation" }));

const trackSuccessfulRegistrationEvent = (trackingRegisterProps) => analytics.trackDigitalEvent({
    eventName: 'register.registerAction',
    page: {
        brand: trackingRegisterProps.brand,
        pageName: 'register.verifyCodeState',
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['register'],
    },
    user: [
        {
            profile: {
                profileInfo: {
                    profileID: trackingRegisterProps.profileID,
                },
            },
        },
    ],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Registration',
                eventAction: 'register.registerAction',
            },
            attributes: {
                isSuccess: true,
                registrationInfo: {
                    verificationType: 'Email',
                    profileInfo: {
                        profileID: trackingRegisterProps.profileID,
                    },
                },
            },
        },
    ],
});
const trackFailedRegistrationEvent = (trackingRegisterProps) => {
    var _a, _b;
    return analytics.trackDigitalEvent({
        eventName: 'register.registerAction',
        page: {
            brand: trackingRegisterProps.brand,
            pageName: trackingRegisterProps.pageName,
            category: {
                primaryCategory: 'myspark',
                pageType: 'Landing Page',
            },
            subCategories: ['register'],
        },
        user: [],
        event: [
            {
                eventInfo: {
                    eventName: 'MySpark Registration',
                    eventAction: 'register.registerAction',
                },
                attributes: {
                    isSuccess: false,
                    internalErrorCode: ((_a = trackingRegisterProps.error) === null || _a === void 0 ? void 0 : _a.code) || 5000,
                    internalErrorMessage: trackingRegisterProps.error.message,
                    errorMessage: (_b = trackingRegisterProps.errorMap[trackingRegisterProps.error.code || 5000]) === null || _b === void 0 ? void 0 : _b.message,
                },
            },
        ],
    });
};
const trackFailedRegistrationValidationEvent = (trackingRegisterProps) => analytics.trackDigitalEvent({
    eventName: 'register.registerAction',
    page: {
        brand: trackingRegisterProps.brand,
        pageName: trackingRegisterProps.pageName,
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['register'],
    },
    user: [],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Registration',
                eventAction: 'register.registerAction',
            },
            attributes: {
                isSuccess: false,
                internalErrorCode: trackingRegisterProps.internalErrorCode,
                internalErrorMessage: trackingRegisterProps.internalErrorMessage,
                errorMessage: trackingRegisterProps.errorMessage,
            },
        },
    ],
});

const handleCodeVerification = ([{ email, confirmationCode, universalLoginBrand },]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.patch(`/v1/user/identity/${email}/verified?verifyBy=Code&code=${confirmationCode}`, {
        verified: true,
    }, {}, 'realmAndBrandInHeaders', universalLoginBrand);
});
const handleCodeRenewal = ([{ email, universalLoginBrand },]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.patch(`/v1/user/identity/${email}/verified?verifyBy=Code`, {
        verified: false,
    }, {}, 'realmAndBrandInHeaders', universalLoginBrand);
});

const CODE_LENGTH = 6;
const ERROR_MESSAGES_MAP$8 = (isSkinny) => {
    const sharedErrorMessages = {
        4023: { message: 'Identity not found.' },
        4024: { message: 'The identity is already validated.' },
        4029: {
            message: 'There have been too many recent validation attempts on this identity',
        },
    };
    return isSkinny
        ? Object.assign({ 4105: { message: 'Security code is invalid. Please try again.' }, 4022: {
                message: 'Your verification code has expired. Please return to log in and try again.',
            }, 4028: { message: 'Security code is invalid. Please try again.' } }, sharedErrorMessages) : Object.assign({ 4105: {
            message: 'You’ve entered an incorrect email address or password. Please try again.',
        }, 4022: { message: 'The token or code is expired' }, 4028: { message: 'Invalid code.' } }, sharedErrorMessages);
};
const isCodeValid = (verificationCode) => {
    return verificationCode.every((code) => code.value != null);
};
const VerificationCode$1 = ({ uuid, email, onSignInResult, onSubmitVerificationResult, }) => {
    const { brand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand,
        pageName: 'register.verifyCodeState',
        subCategories: ['register'],
        primaryCategory: 'myspark',
        pageType: 'Landing Page',
    }));
    const { Text, Button, TextButton, Spacer, CodeInput, getEmptyCode, transformCodeToString, } = useComponentLibrary();
    const { InfoBanner, setInfoBannerState } = useInfoBanner();
    const { getText } = useContent();
    const { isSkinny } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    const initialFormState = {
        code: getEmptyCode(CODE_LENGTH),
        isValid: false,
    };
    const [formState, setFormState] = useState(initialFormState);
    const handleCodeChange = (code) => {
        setFormState({
            code,
            isValid: isCodeValid(code),
        });
    };
    const [activeError, setActiveError] = useState(null);
    const { mutate, isLoading } = useMutation({
        mutationFn: handleCodeVerification,
        onSuccess: () => {
            trackSuccessfulRegistrationEvent({
                brand,
                profileID: uuid || 'Unavailable',
            });
            onSubmitVerificationResult();
            setInfoBannerState({
                variant: null,
                text: '',
            });
            setActiveError(null);
        },
        onError: (err) => {
            trackFailedRegistrationEvent({
                brand,
                pageName: 'register.verifyCodeState',
                error: err,
                errorMap: ERROR_MESSAGES_MAP$8(isSkinny),
            });
            setFormState(initialFormState);
            setInfoBannerState({
                variant: null,
                text: '',
            });
            setActiveError(err);
        },
    });
    const { mutate: runResend, isLoading: isLoadingResend } = useMutation({
        mutationFn: handleCodeRenewal,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'A new verification code has been sent',
            });
            setFormState(initialFormState);
            setActiveError(null);
        },
        onError: () => {
            setFormState(initialFormState);
            setInfoBannerState({
                variant: 'ERROR',
                text: 'There was an error sending the new verification code',
            });
            setActiveError(null);
        },
    });
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, getText('register.enterCode')),
        React__default.createElement(Spacer, { variant: "spacing-01" }),
        React__default.createElement(Text, { variant: "bodySMedium" }, getText('register.toVerifyItsYou')),
        React__default.createElement(InfoBanner, { spacer: "preceding" }),
        activeError ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(ErrorMessage, { error: activeError, errorMap: ERROR_MESSAGES_MAP$8(isSkinny) }))) : null,
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(LinkRegion, { isNotTracked: true },
                isSkinny ? (React__default.createElement(Text, { variant: "bodySMedium" },
                    "A six digit code has been sent to your email address.",
                    React__default.createElement(Spacer, { variant: "spacing-03" }),
                    "Please enter the code below.")) : (React__default.createElement(Text, { variant: "bodySDemi" },
                    "A ",
                    CODE_LENGTH,
                    " digit code has been sent to",
                    ' ',
                    React__default.createElement("span", { className: "sessioncamhidetext" }, email),
                    ". Please enter it below. It's valid for 5 minutes.")),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(CodeInput, { value: formState.code, onChange: handleCodeChange, inputProps: { 'aria-label': 'code-input', autoFocus: true } }))),
        React__default.createElement(Spacer, { variant: "spacing-04" }),
        React__default.createElement(FormRow, null,
            React__default.createElement(Button, { disabled: !formState.isValid || isLoading || isLoadingResend, isLoading: isLoading, onClick: () => {
                    mutate([
                        {
                            confirmationCode: transformCodeToString(formState.code),
                            email,
                            universalLoginBrand,
                        },
                    ]);
                }, variant: "primary" }, getText('register.verify'))),
        React__default.createElement(Spacer, { variant: "spacing-02" }),
        React__default.createElement(LinkRegion, { regionId: "body" },
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: isLoading || isLoadingResend, onClick: () => {
                        runResend([
                            {
                                email,
                                universalLoginBrand,
                            },
                        ]);
                    }, variant: "default", isLoading: isLoadingResend }, getText('register.haveNotReceivedACode')))),
        isSkinny ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))) : null));
};

const VerificationConfirmation = ({ onSignInResult, }) => {
    const { Text, TextButton, Spacer, FeedbackAlert, InfoBanner } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny } = useBrand();
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, "Success"),
        isSkinny ? (React__default.createElement(FeedbackAlert, { variant: "information" }, "You have successfully registered.")) : (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(InfoBanner, { variant: "SUCCESS", text: "You have successfully registered." }))),
        React__default.createElement(Spacer, { variant: "spacing-04" }),
        React__default.createElement(LinkRow, { centered: true },
            React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))));
};

var VerifyStep;
(function (VerifyStep) {
    VerifyStep[VerifyStep["Verification"] = 0] = "Verification";
    VerifyStep[VerifyStep["Result"] = 1] = "Result";
})(VerifyStep || (VerifyStep = {}));
const initialState$5 = {
    step: VerifyStep.Verification,
};
const useVerify = () => {
    const [state, setState] = useState(initialState$5);
    const submitVerification = () => {
        setState({
            step: VerifyStep.Result,
        });
    };
    return { state, submitVerification };
};

const VerifyJourney = ({ onSignInResult, email, uuid }) => {
    const { state, submitVerification } = useVerify();
    if (state.step === VerifyStep.Result) {
        return React__default.createElement(VerificationConfirmation, { onSignInResult: onSignInResult });
    }
    return (React__default.createElement(VerificationCode$1, { email: email, uuid: uuid, onSignInResult: onSignInResult, onSubmitVerificationResult: submitVerification }));
};

const trackGoogleLoginEvent = (trackingLoginProps) => analytics.trackDigitalEvent({
    eventName: 'login.loginAction',
    page: {
        brand: trackingLoginProps.brand,
        pageName: trackingLoginProps.pageName,
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['login'],
    },
    user: [
        {
            profile: {
                attributes: {
                    loginOption: 'Google',
                    authState: 'Authenticated',
                    multiFactorAuthType: 'None',
                },
                social: {
                    google: trackingLoginProps.userID,
                },
            },
        },
    ],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Login',
                eventAction: 'login.loginAction',
            },
            attributes: { isSuccess: true },
        },
    ],
});
const trackFacebookLoginEvent = (trackingLoginProps) => analytics.trackDigitalEvent({
    eventName: 'login.loginAction',
    page: {
        brand: trackingLoginProps.brand,
        pageName: trackingLoginProps.pageName,
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['login'],
    },
    user: [
        {
            profile: {
                attributes: {
                    loginOption: 'Facebook',
                    authState: 'Authenticated',
                    multiFactorAuthType: 'None',
                },
                social: {
                    facebook: trackingLoginProps.userID,
                },
            },
        },
    ],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Login',
                eventAction: 'login.loginAction',
            },
            attributes: { isSuccess: true },
        },
    ],
});
const trackSuccessfulLoginEvent = (trackingLoginProps, userUid) => analytics.trackDigitalEvent({
    eventName: 'login.loginAction',
    page: {
        brand: trackingLoginProps.brand,
        pageName: trackingLoginProps.pageName,
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['login'],
    },
    user: [
        {
            profile: {
                profileID: userUid,
                attributes: {
                    loginOption: 'Spark',
                    authState: 'Authenticated',
                    multiFactorAuthType: trackingLoginProps.multiFactorAuthType,
                },
            },
        },
    ],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Login',
                eventAction: 'login.loginAction',
            },
            attributes: { isSuccess: true },
        },
    ],
});
const trackFailedLoginEvent = (trackingLoginProps) => {
    var _a, _b;
    return analytics.trackDigitalEvent({
        eventName: 'login.loginAction',
        page: {
            brand: trackingLoginProps.brand,
            pageName: trackingLoginProps.pageName,
            category: {
                primaryCategory: 'myspark',
                pageType: 'Landing Page',
            },
            subCategories: ['login'],
        },
        user: [
            {
                profile: {
                    attributes: {
                        loginOption: 'Spark',
                        authState: 'Unknown',
                        multiFactorAuthType: trackingLoginProps.multiFactorAuthType,
                    },
                },
            },
        ],
        event: [
            {
                eventInfo: {
                    eventName: 'MySpark Login',
                    eventAction: 'login.loginAction',
                },
                attributes: {
                    isSuccess: false,
                    internalErrorCode: ((_a = trackingLoginProps.error) === null || _a === void 0 ? void 0 : _a.code) || 9001,
                    internalErrorMessage: trackingLoginProps.error.message,
                    errorMessage: ((_b = trackingLoginProps.errorMap[trackingLoginProps.error.code || 9001]) === null || _b === void 0 ? void 0 : _b.message) || FALLBACK_ERROR_MESSAGE,
                },
            },
        ],
    });
};
const trackFailedLoginValidationEvent = (trackingLoginProps) => analytics.trackDigitalEvent({
    eventName: 'login.loginAction',
    page: {
        brand: trackingLoginProps.brand,
        pageName: 'login.loginState',
        category: {
            primaryCategory: 'myspark',
            pageType: 'Landing Page',
        },
        subCategories: ['login'],
    },
    user: [],
    event: [
        {
            eventInfo: {
                eventName: 'MySpark Login',
                eventAction: 'login.loginAction',
            },
            attributes: {
                isSuccess: false,
                internalErrorCode: trackingLoginProps.internalErrorCode,
                internalErrorMessage: trackingLoginProps.internalErrorMessage,
                errorMessage: trackingLoginProps.errorMessage,
            },
        },
    ],
});

var LoginStep;
(function (LoginStep) {
    LoginStep[LoginStep["LoginForm"] = 0] = "LoginForm";
    LoginStep[LoginStep["MultifactorVerification"] = 1] = "MultifactorVerification";
    LoginStep[LoginStep["VerifyUser"] = 2] = "VerifyUser";
})(LoginStep || (LoginStep = {}));
var MfaStep;
(function (MfaStep) {
    MfaStep[MfaStep["SelectMfaOption"] = 0] = "SelectMfaOption";
    MfaStep[MfaStep["EnterVerificationCode"] = 1] = "EnterVerificationCode";
})(MfaStep || (MfaStep = {}));
const initialState$4 = {
    email: '',
    stage: '',
    nonce: '',
    amlb: '',
    loginStep: LoginStep.LoginForm,
    mfaStep: MfaStep.EnterVerificationCode,
    mfaOptions: [],
};
const LoginContext = React__default.createContext(null);
const LoginContextProvider = ({ children, redirectURL, }) => {
    const [loginState, setLoginState] = useState(initialState$4);
    const { fallbackURL, brand } = useConfig();
    const handleMFARequired = ({ mfaOptions, stage, nonce, amlb, }) => {
        setLoginState((prevState) => (Object.assign(Object.assign({}, prevState), { stage,
            nonce,
            amlb, loginStep: LoginStep.MultifactorVerification, mfaOptions })));
    };
    const handleMfaOptionSelected = (selectedMfaType) => {
        setLoginState((prevState) => (Object.assign(Object.assign({}, prevState), { selectedMfaType })));
    };
    const handleReturnToSignIn = () => {
        setLoginState(initialState$4);
    };
    const handleVerifyUser = (email) => {
        setLoginState((prevState) => (Object.assign(Object.assign({}, prevState), { email, loginStep: LoginStep.VerifyUser })));
    };
    return (React__default.createElement(LoginContext.Provider, { value: Object.assign(Object.assign({}, loginState), { handleMFARequired,
            handleMfaOptionSelected,
            handleReturnToSignIn,
            handleVerifyUser, redirectURL: redirectURL || fallbackURL, brand }) }, children));
};
function useLogin() {
    const context = React__default.useContext(LoginContext);
    if (!context) {
        throw new Error('useLogin should be used inside LoginContextProvider');
    }
    return context;
}

const handleLogin = ({ userName, password, token, redirectURL, universalLoginBrand, rememberMe, }) => __awaiter(void 0, void 0, void 0, function* () {
    const loginData = {
        userName,
        password,
        captchaToken: token,
        rememberMe,
    };
    const reqBody = Object.assign({}, loginData);
    return httpClient.post('/v1/user/ula/login', reqBody, { redirectURL }, true, 'realmAndBrandInHeaders', universalLoginBrand);
});
const handleSubmitVerification = ([{ mfaToken, nonce, amlb, stage, redirectURL, universalLoginBrand },]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.post('v1/user/ula/mfa', {
        mfaToken,
        nonce: nonce !== null && nonce !== void 0 ? nonce : '',
        amlb: amlb !== null && amlb !== void 0 ? amlb : '',
        stage: stage !== null && stage !== void 0 ? stage : '',
    }, { redirectURL }, true, 'realmAndBrandInHeaders', universalLoginBrand);
});
const handleMFACodeRenewal = ([{ nonce, amlb, stage, universalLoginBrand },]) => __awaiter(void 0, void 0, void 0, function* () {
    const queryString = qs.stringify({
        nonce,
        amlb,
        stage,
    });
    return httpClient.get(`/v1/user/oidc/authenticate/mfa/renew?${queryString}`, {}, {}, false, 'realmAndBrandInHeaders', universalLoginBrand);
});
// 🔶 Make mfaOptions required when backwards compatibility no longer needed.
const handleMfaTypeSelection = ({ redirectURL, universalLoginBrand, nonce, amlb, mfaOptions, mfaOptionChoice, }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!mfaOptionChoice || !(mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions.length))
        throw new Error('An mfaOptionChoice and mfaOptions are required for the MFA type selection mutation');
    return httpClient.post('/v1/user/ula/mfa/options', {
        nonce,
        amlb,
        mfaOptionChoice,
        mfaOptions,
    }, { redirectURL }, false, 'realmAndBrandInHeaders', universalLoginBrand);
});

var SocialLoginStatus;
(function (SocialLoginStatus) {
    SocialLoginStatus[SocialLoginStatus["Initial"] = 0] = "Initial";
    SocialLoginStatus[SocialLoginStatus["Ready"] = 1] = "Ready";
    SocialLoginStatus[SocialLoginStatus["SigningIn"] = 2] = "SigningIn";
    SocialLoginStatus[SocialLoginStatus["Error"] = 3] = "Error";
})(SocialLoginStatus || (SocialLoginStatus = {}));

function postSocialLogin({ redirectURL, socialProvider, socialToken, firstNameApple, lastNameApple, universalLoginBrand, }) {
    return httpClient.post('/v1/user/ula/social', {
        socialProvider,
        socialToken,
        firstNameApple,
        lastNameApple,
    }, {
        redirectURL,
    }, true, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
}

function postFacebookLogin({ redirectURL, accessToken, universalLoginBrand, }) {
    return postSocialLogin({
        redirectURL,
        socialProvider: 'FACEBOOK',
        socialToken: accessToken,
        universalLoginBrand,
    });
}
function FacebookButton({ onLoginSuccess, status, setStatus, }) {
    const { SocialLoginButton, Icon } = useComponentLibrary();
    const { redirectURL } = useLogin();
    const { brand: universalLoginBrand } = useConfig();
    function handleLoginSuccess(accessToken) {
        return __awaiter(this, void 0, void 0, function* () {
            yield postFacebookLogin({ accessToken, redirectURL, universalLoginBrand });
            onLoginSuccess();
        });
    }
    function handleClick() {
        var _a;
        setStatus(SocialLoginStatus.SigningIn);
        if ((_a = window.FB) === null || _a === void 0 ? void 0 : _a.login) {
            window.FB.login((response) => {
                var _a, _b;
                if ((_a = response === null || response === void 0 ? void 0 : response.authResponse) === null || _a === void 0 ? void 0 : _a.accessToken) {
                    trackFacebookLoginEvent({
                        brand: universalLoginBrand,
                        userID: response.authResponse.userID,
                        pageName: 'login.loginState',
                    });
                    setStatus(SocialLoginStatus.Ready);
                    handleLoginSuccess((_b = response === null || response === void 0 ? void 0 : response.authResponse) === null || _b === void 0 ? void 0 : _b.accessToken);
                }
                else {
                    setStatus(SocialLoginStatus.Ready);
                }
            }, { scope: 'email' });
        }
    }
    return (React__default.createElement(SocialLoginButton, { onClick: handleClick, variant: "default", "aria-label": "facebook-login", disabled: status !== SocialLoginStatus.Ready },
        React__default.createElement(Icon, { name: "facebook_small", fill: "default" })));
}

function GoogleButton({ buttonRef, status }) {
    const { SocialLoginButton, Icon } = useComponentLibrary();
    return (React__default.createElement(SocialLoginButton, { ref: buttonRef, variant: "default", "aria-label": "google-login", disabled: status !== SocialLoginStatus.Ready },
        React__default.createElement(Icon, { name: "google_small", fill: "default" })));
}

function handleClickAppleLogin() {
    var _a, _b;
    if ((_b = (_a = window === null || window === void 0 ? void 0 : window.AppleID) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.signIn) {
        window.AppleID.auth.signIn();
    }
}
function postAppleLogin({ redirectURL, accessToken, firstNameApple, lastNameApple, universalLoginBrand, }) {
    return postSocialLogin({
        redirectURL,
        socialProvider: 'APPLE',
        socialToken: accessToken,
        firstNameApple,
        lastNameApple,
        universalLoginBrand,
    });
}
let isHandleLoginAllowed = true;
function AppleButton({ onLoginSuccess, status, appleButtonRef, }) {
    const { SocialLoginButton, Icon } = useComponentLibrary();
    const { brand: universalLoginBrand } = useConfig();
    const { redirectURL } = useLogin();
    function handleLoginSuccess(accessToken, firstNameApple, lastNameApple) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { messages } = yield postAppleLogin({
                    accessToken,
                    redirectURL,
                    firstNameApple,
                    lastNameApple,
                    universalLoginBrand,
                });
                const isLoginSuccess = messages === null || messages === void 0 ? void 0 : messages.some((message) => (message === null || message === void 0 ? void 0 : message.code) === 2000);
                isHandleLoginAllowed = true;
                if (isLoginSuccess) {
                    onLoginSuccess();
                }
            }
            catch (error) {
                isHandleLoginAllowed = true;
                console.error('Apple login error: ', error);
            }
        });
    }
    // Listen for authorization success.
    document.addEventListener('AppleIDSignInOnSuccess', (event) => {
        if (isHandleLoginAllowed) {
            isHandleLoginAllowed = false;
            const { authorization, user } = event === null || event === void 0 ? void 0 : event.detail;
            const accessToken = authorization === null || authorization === void 0 ? void 0 : authorization.id_token;
            const firstNameApple = user === null || user === void 0 ? void 0 : user.name.firstName;
            const lastNameApple = user === null || user === void 0 ? void 0 : user.name.lastName;
            handleLoginSuccess(accessToken, firstNameApple, lastNameApple);
        }
    }, { once: true });
    // Listen for authorization failures.
    document.addEventListener('AppleIDSignInOnFailure', (event) => {
        var _a;
        if (isHandleLoginAllowed) {
            console.error((_a = event.detail) === null || _a === void 0 ? void 0 : _a.error);
            isHandleLoginAllowed = false;
            setTimeout(() => {
                isHandleLoginAllowed = true;
            }, 1000);
        }
    }, { once: true });
    return (React__default.createElement(SocialLoginButton, { onClick: handleClickAppleLogin, ref: appleButtonRef, variant: "default", "aria-label": "apple-login", disabled: status !== SocialLoginStatus.Ready },
        React__default.createElement(Icon, { name: "apple_small", fill: "default" })));
}

const redirectToXtraMailHelpPage = () => {
    window.location.assign(HELP_PAGE_URL);
};
const HelpButton = () => {
    const { getText } = useContent();
    const needHelpText = getText('login.needHelp');
    const { TextButton } = useComponentLibrary();
    if (!needHelpText) {
        return null;
    }
    return (React__default.createElement(FormRow, { centered: true },
        React__default.createElement(TextButton, { onClick: redirectToXtraMailHelpPage }, needHelpText)));
};

function useScript(url) {
    const [hasCompleted, setHasCompleted] = useState(false);
    useEffect(() => {
        const scriptElement = document.createElement('script');
        function handleLoad() {
            setHasCompleted(true);
        }
        scriptElement.addEventListener('load', handleLoad);
        scriptElement.addEventListener('error', handleLoad);
        document.head.appendChild(scriptElement);
        scriptElement.src = url;
        return () => {
            scriptElement.removeEventListener('load', handleLoad);
            document.head.removeChild(scriptElement);
            setHasCompleted(false);
        };
    }, [url]);
    return { hasCompleted };
}

const APPLE_APP_ID = 'nz.co.skinny.web';
function useAppleLogin() {
    const [status, setStatus] = useState(SocialLoginStatus.Initial);
    const appleButtonRef = useRef(null);
    const redirectURI = `https://${window.location.host}`;
    const { hasCompleted } = useScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
    const appleInit = () => {
        const params = {
            clientId: APPLE_APP_ID,
            scope: 'name email',
            redirectURI,
            usePopup: true,
        };
        const appleIdAuthInit = () => { var _a, _b; return (_b = (_a = window.AppleID) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.init(params); };
        appleIdAuthInit();
    };
    useEffect(() => {
        if (!hasCompleted || appleButtonRef.current == null) {
            return;
        }
        appleInit();
        setStatus(SocialLoginStatus.Ready);
    }, [hasCompleted]);
    return { appleButtonRef, status };
}

const FACEBOOK_APP_ID_SPARK = '1058309614204764';
const FACEBOOK_APP_ID_SKINNY = '240681846117611';
function useFacebookLogin() {
    const [status, setStatus] = useState(SocialLoginStatus.Initial);
    const { isSkinny } = useBrand();
    useEffect(() => {
        var _a;
        const alreadyInitialized = Boolean((_a = window === null || window === void 0 ? void 0 : window.FB) === null || _a === void 0 ? void 0 : _a.login);
        if (alreadyInitialized) {
            setStatus(SocialLoginStatus.Ready);
            return;
        }
        window.fbAsyncInit = () => {
            window.FB.init({
                appId: isSkinny ? FACEBOOK_APP_ID_SKINNY : FACEBOOK_APP_ID_SPARK,
                cookie: true,
                xfbml: true,
                version: 'v10.0',
            });
            window.FB.AppEvents.logPageView();
            setStatus(SocialLoginStatus.Ready);
        };
    }, []);
    useScript('https://connect.facebook.net/en_US/sdk.js');
    return { status, setStatus };
}

function postGoogleLogin({ redirectURL, accessToken, universalLoginBrand, }) {
    return postSocialLogin({
        redirectURL,
        socialProvider: 'GOOGLE',
        socialToken: accessToken,
        universalLoginBrand,
    });
}
const GOOGLE_CLIENT_ID_SPARK = '245199438597-01s0j4gtd239uj3al1409ed40mak548f';
const GOOGLE_CLIENT_ID_SKINNY = '51403107239-0t6t4s6a391vp4269a5bdljp30sjplvj';
function useGoogleLogin({ onLoginSuccess, }) {
    const buttonRef = useRef(null);
    const [status, setStatus] = useState(SocialLoginStatus.Initial);
    const { hasCompleted } = useScript('https://apis.google.com/js/platform.js');
    const { redirectURL } = useLogin();
    const { isSkinny } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    function handleResponse(googleUser) {
        return __awaiter(this, void 0, void 0, function* () {
            const authResponse = googleUser.getAuthResponse(true);
            const userID = googleUser.getId();
            yield postGoogleLogin({
                redirectURL,
                accessToken: authResponse.id_token,
                universalLoginBrand,
            });
            trackGoogleLoginEvent({
                brand: universalLoginBrand,
                userID: userID.user_id,
                pageName: 'login.loginState',
            });
            onLoginSuccess();
        });
    }
    useEffect(() => {
        var _a;
        if (!hasCompleted || buttonRef.current == null) {
            return;
        }
        if ((_a = window.gapi) === null || _a === void 0 ? void 0 : _a.load) {
            window.gapi.load('auth2', () => {
                if (buttonRef.current == null) {
                    return;
                }
                const auth2 = window.gapi.auth2.init({
                    client_id: `${isSkinny ? GOOGLE_CLIENT_ID_SKINNY : GOOGLE_CLIENT_ID_SPARK}.apps.googleusercontent.com`,
                    cookiepolicy: 'single_host_origin',
                });
                auth2.attachClickHandler(buttonRef.current, {}, handleResponse);
                setStatus(SocialLoginStatus.Ready);
            });
        }
    }, [hasCompleted]);
    return { buttonRef, status };
}

const SocialContainter = styled(FormRow) `
  flex-direction: row;
  justify-content: space-between;
`;
const SocialLoginContainer = ({ onLoginSuccess, }) => {
    const { flagEnableFacebookLogin, flagEnableGoogleLogin, flagEnableAppleLogin, } = useFeatureFlags();
    const { status: facebookStatus, setStatus: setFacebookStatus } = useFacebookLogin();
    const { buttonRef, status: googleStatus } = useGoogleLogin({
        onLoginSuccess,
    });
    const { status: appleStatus, appleButtonRef } = useAppleLogin();
    return (React__default.createElement(SocialContainter, null,
        flagEnableFacebookLogin ? (React__default.createElement(FacebookButton, { status: facebookStatus, setStatus: setFacebookStatus, onLoginSuccess: onLoginSuccess })) : null,
        flagEnableGoogleLogin ? (React__default.createElement(GoogleButton, { buttonRef: buttonRef, status: googleStatus })) : null,
        flagEnableAppleLogin ? (React__default.createElement(AppleButton, { status: appleStatus, onLoginSuccess: onLoginSuccess, appleButtonRef: appleButtonRef })) : null));
};

const ANALYTICS_SYNC_TIMEOUT = 1000 ;
const InfoText = styled.span `
  > * {
    display: flex;
    justify-content: center;
    white-space: pre-wrap;
  }
`;
const InfoTextContainer = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const RegisterTextContainer = styled.div `
  margin: 0 auto;
`;
const ForgotPasswordText = styled.span `
  white-space: normal;
  word-wrap: break-word;
  text-align: left;
  line-height: 24px;
`;
const ERROR_MESSAGES_MAP$7 = (brand, action) => {
    if (brand === 'Skinny') {
        return {
            4105: {
                message: 'Incorrect email address or password. Please try again.',
            },
            5501: {
                message: `Invalid email or password. Please try using your app login details. If you don't have an app account or it is still failing please click "Can't sign in" below to reset your password.`,
            },
            9001: { message: 'Oops - something went wrong.' },
        };
    }
    if (brand === 'XtraMail') {
        return {
            4105: {
                message: 'Incorrect email address or password. Please try again.',
            },
            9001: { message: 'Oops - something went wrong.' },
            4126: {
                message: 'Your Xtra Mail has been suspended. Please contact us.',
            },
            4127: {
                message: 'Your Xtra Mail has been suspended. Please change your password.',
            },
        };
    }
    if (brand === 'MySB') {
        return {
            4105: {
                message: 'Invalid user ID or password. Please try again.',
            },
            9001: { message: 'Oops - something went wrong.' },
        };
    }
    if (brand === 'Spark') {
        return {
            4105: {
                message: 'Incorrect email address or password. Try signing in again or resetting your password.',
                linkText: 'Reset password',
                action: action === null || action === void 0 ? void 0 : action.forgotPassword,
            },
            9001: { message: 'Oops - something went wrong' },
        };
    }
    return {
        4105: {
            message: 'Incorrect email address or password. Please try again.',
        },
        9001: { message: 'Oops - something went wrong' },
    };
};
const ErrorMessageSection = ({ error, universalLoginBrand, onForgotPasswordResult, }) => {
    const { Spacer } = useComponentLibrary();
    if (!error || (error === null || error === void 0 ? void 0 : error.message) === 'Identity not verified.') {
        return null;
    }
    const errorMessageMap = ERROR_MESSAGES_MAP$7(universalLoginBrand, {
        forgotPassword: onForgotPasswordResult,
    });
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Spacer, { variant: "spacing-02" }),
        React__default.createElement(ErrorMessage, { error: error, errorMap: errorMessageMap })));
};
const LoginFormInputs = ({ control, isMySB, isLoading, errors, }) => {
    const { Spacer } = useComponentLibrary();
    const { getText } = useContent();
    return (React__default.createElement(LinkRegion, { isNotTracked: true },
        React__default.createElement(Controller, { control: control, name: "email", rules: {
                required: true,
                validate: isMySB ? isUserIdValid : isEmailValid,
            }, render: ({ field }) => (React__default.createElement(EmailInput, { disabled: isLoading, error: errors.email, name: field.name, onChange: field.onChange, value: field.value, inputType: isMySB ? 'text' : 'email', label: isMySB ? getText('userIdLabel') : undefined, placeholder: isMySB ? 'Enter your User ID' : undefined })) }),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(Controller, { control: control, name: "password", rules: {
                required: true,
                minLength: 1,
                validate: isPasswordEntered,
            }, render: ({ field }) => (React__default.createElement(PasswordInput, { disabled: isLoading, error: errors.password, name: field.name, onChange: field.onChange, title: "Password", value: field.value, placeholder: isMySB ? 'Enter your password' : undefined })) })));
};
const LoginForm = ({ onForgotPasswordResult, onLoginSuccess, onRegisterResult, }) => {
    const { brand: universalLoginBrand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'login.loginState',
        subCategories: ['login'],
        primaryCategory: 'myspark',
        pageType: 'Landing Page',
        user: [{ profile: { attributes: { authState: 'Unknown' } } }],
    }));
    const { Text, TextButton, Button, Spacer, InfoBanner, Checkbox } = useComponentLibrary();
    const { handleMFARequired, handleVerifyUser, redirectURL } = useLogin();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const { flagEnableLoginRememberMe, flagEnableRegisterJourney, flagEnableFacebookLogin, flagEnableGoogleLogin, flagEnableAppleLogin, } = useFeatureFlags();
    const hasSocialLogin = flagEnableFacebookLogin || flagEnableGoogleLogin || flagEnableAppleLogin;
    const { getText } = useContent();
    const { isSkinny, isMySB } = useBrand();
    const { mutate, error, isLoading } = useMutation({
        mutationFn: handleLogin,
        onSuccess: (res) => {
            const { mfaRequired, mfaOptions, stage, nonce, amlb } = res;
            if (mfaRequired) {
                handleMFARequired({ mfaOptions, stage, nonce, amlb });
            }
            else {
                trackSuccessfulLoginEvent({
                    brand: universalLoginBrand,
                    pageName: 'login.loginState',
                    multiFactorAuthType: 'None',
                }, res.userUid);
                setIsLoggedIn(true);
                /**
                 * This is a workaround for an issue where Safari redirects before the
                 * Adobe Launch script finishes adding the analytics data. We add a 1000
                 * ms timeout before redirecting to allow the Adobe Launch script enough
                 * time to complete adding the data.
                 */
                setTimeout(() => {
                    onLoginSuccess();
                }, ANALYTICS_SYNC_TIMEOUT);
            }
        },
        onError: (err) => {
            trackFailedLoginEvent({
                brand: universalLoginBrand,
                pageName: 'login.loginState',
                multiFactorAuthType: 'None',
                error: err,
                errorMap: ERROR_MESSAGES_MAP$7(universalLoginBrand),
            });
        },
    });
    const { control, handleSubmit, formState: { errors }, watch, } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            email: '',
            password: '',
            rememberMe: false,
        },
    });
    function executeLoginMutation({ userEmail, userPassword, token, userRememberMe, }) {
        mutate({
            userName: userEmail,
            password: userPassword,
            token,
            redirectURL,
            universalLoginBrand,
            rememberMe: flagEnableLoginRememberMe ? userRememberMe : undefined,
        });
    }
    const { email, password, rememberMe } = watch();
    const { initiateCaptcha, hCaptchaProps } = useHCaptcha((token) => executeLoginMutation({
        userEmail: email,
        userPassword: password,
        token,
        userRememberMe: rememberMe,
    }));
    function submitHandler() {
        return __awaiter(this, void 0, void 0, function* () {
            initiateCaptcha();
        });
    }
    useEffect(() => {
        var _a;
        if (((_a = errors.email) === null || _a === void 0 ? void 0 : _a.type) === 'validate') {
            trackFailedLoginValidationEvent({
                brand: universalLoginBrand,
                errorMessage: 'Please enter a valid email address',
                internalErrorCode: 4002,
                internalErrorMessage: 'Invalid email',
            });
        }
    }, [errors]);
    const isUserUnverified = (error === null || error === void 0 ? void 0 : error.message) === 'Identity not verified.';
    useEffect(() => {
        if (isUserUnverified) {
            handleVerifyUser(email);
        }
    }, [error]);
    const redirectToSkinnyBroadbandLogin = () => {
        window.location.assign(SKINNY_BROADBAND_LOGIN);
    };
    const noAccountText = getText('login.noAccount');
    const registerText = getText('login.register');
    const showRegisterButton = flagEnableRegisterJourney && noAccountText && registerText;
    let socialLoginSpacer = React__default.createElement(Spacer, { variant: "spacing-02" });
    if (hasSocialLogin) {
        socialLoginSpacer = (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(FormRow, { centered: true },
                React__default.createElement(InfoText, null,
                    React__default.createElement(Text, { variant: "footnoteMedium" }, getText('login.orSignInWith')))),
            React__default.createElement(Spacer, { variant: "spacing-02" })));
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('login.loginText')),
            React__default.createElement(Spacer, { variant: "spacing-01" }),
            React__default.createElement(Text, { variant: "bodySMedium" }, getText('login.toContinue')),
            isSkinny ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySMedium" },
                    React__default.createElement(TextButton, { onClick: redirectToSkinnyBroadbandLogin }, getText('login.clickHere')),
                    "\u00A0to login to Skinny Broadband Unlimited."))) : null,
            isLoggedIn ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(InfoBanner, { text: "You are logged in", variant: "SUCCESS" }))) : null,
            React__default.createElement(ErrorMessageSection, { error: error, universalLoginBrand: universalLoginBrand, onForgotPasswordResult: onForgotPasswordResult }),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(LinkRegion, { regionId: "Sign in" },
                React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
                    React__default.createElement(React__default.Fragment, null,
                        React__default.createElement(LoginFormInputs, { control: control, isMySB: isMySB, isLoading: isLoading, errors: errors }),
                        React__default.createElement(Spacer, { variant: "spacing-01" }),
                        React__default.createElement(TextButton, { onClick: onForgotPasswordResult },
                            React__default.createElement(ForgotPasswordText, null, getText('login.resetPasswordText'))),
                        flagEnableLoginRememberMe ? (React__default.createElement(React__default.Fragment, null,
                            React__default.createElement(Spacer, { variant: "spacing-03" }),
                            React__default.createElement(Controller, { control: control, name: "rememberMe", render: ({ field }) => (React__default.createElement(Checkbox, { label: "Remember me", onChange: () => field.onChange(!field.value), checked: field.value, disabled: isLoading || isLoggedIn })) }))) : null,
                        React__default.createElement(Spacer, { variant: "spacing-04" }),
                        React__default.createElement(FormRow, null,
                            React__default.createElement(Button, { disabled: !password || !email || isLoading || isLoggedIn, isLoading: isLoading || isLoggedIn, variant: "primary", type: "submit" }, getText('login.buttonText')),
                            React__default.createElement(HCaptchaElement, Object.assign({}, hCaptchaProps))),
                        socialLoginSpacer,
                        React__default.createElement(SocialLoginContainer, { onLoginSuccess: onLoginSuccess }),
                        showRegisterButton ? (React__default.createElement(React__default.Fragment, null,
                            React__default.createElement(Spacer, { variant: "spacing-03" }),
                            React__default.createElement(InfoTextContainer, null,
                                React__default.createElement(InfoText, null,
                                    React__default.createElement(Text, { variant: "bodySDemi" }, noAccountText)),
                                React__default.createElement(RegisterTextContainer, null,
                                    React__default.createElement(TextButton, { onClick: onRegisterResult }, registerText))))) : null,
                        React__default.createElement(HelpButton, null),
                        React__default.createElement(Spacer, { variant: "spacing-02" }),
                        React__default.createElement(HCaptchaDisclaimerText, null)))))));
};

const ColumnDiv = styled.div `
  display: flex;
  flex-direction: column;
`;
const ERROR_MESSAGES_MAP$6 = {};
const findMfaOptionIndex = (mfaType, mfaOptions) => {
    return (mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions.findIndex((mfaOption) => mfaOption.type === mfaType)) || 0;
};
const MfaOptions = ({ onMfaStepChange, onSignInResult, mfaOptions, }) => {
    const { Text, TextButton, Button, Spacer, ChoiceboxGroup, ChoiceBox } = useComponentLibrary();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const { handleMfaOptionSelected, nonce, amlb, redirectURL } = useLogin();
    const [selectedMfaType, setSelectedMfaType] = useState(undefined);
    const { handleSubmit } = useForm({
        mode: 'onSubmit',
    });
    const { mutate, error, isLoading } = useMutation({
        mutationFn: handleMfaTypeSelection,
        onSuccess: () => {
            handleMfaOptionSelected(selectedMfaType);
            onMfaStepChange(MfaStep.EnterVerificationCode);
        },
    });
    function submitHandler() {
        mutate({
            redirectURL,
            universalLoginBrand,
            nonce: nonce || '',
            amlb: amlb || '',
            mfaOptions,
            mfaOptionChoice: selectedMfaType,
        });
    }
    return (React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('mfa.optionsSubheader')),
            error ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$6 }))) : null,
            React__default.createElement(Spacer, { variant: "spacing-01" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('mfa.optionsBody')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(ChoiceboxGroup, null, mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions.map((mfaOption, i) => {
                    const { type, value } = mfaOption;
                    const mfaTypeLabels = {
                        email: 'Email address',
                        mobile: 'Mobile number',
                        app: 'Authenticator app',
                    };
                    return (React__default.createElement(ChoiceBox, { key: type, name: "mfaOption", value: type, checked: i === findMfaOptionIndex(selectedMfaType, mfaOptions), onChange: () => {
                            setSelectedMfaType(type);
                        } },
                        React__default.createElement(ColumnDiv, null,
                            React__default.createElement(Text, { variant: "bodySDemi" }, mfaTypeLabels[type]),
                            React__default.createElement(Text, { variant: "bodySMedium" }, value.replaceAll('*', '•')))));
                }))),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: !selectedMfaType, isLoading: isLoading, type: "submit", variant: "primary" }, getText('register.next'))),
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn'))))));
};

const SECURITY_CODE_INVALID = 'Security code is invalid. Please try again.';
const VERIFICATION_CODE_INVALID = 'The verification code is invalid. Please try again.';
const VERIFICATION_CODE_EXPIRED = 'The verification code has expired. Please try again.';
const VERIFICATION_CODE_EXPIRED_RETURN_TO_LOGIN = 'Your verification code has expired. Please return to log in and try again.';
const MFA_CODE_INVALID = 'Invalid MFA code';
const NONCE_TOO_MANY_REQUESTS = 'Too many invalid attempts. Please sign in again.';
const GENERIC_ERROR = 'Oops - something went wrong';
const getErrorMessagesMap = (brand, action) => {
    if (brand === 'Skinny') {
        return {
            4002: { message: SECURITY_CODE_INVALID },
            4106: { message: SECURITY_CODE_INVALID },
            4107: { message: VERIFICATION_CODE_EXPIRED_RETURN_TO_LOGIN },
            9001: { message: GENERIC_ERROR },
        };
    }
    if (brand === 'XtraMail') {
        return {
            4002: { message: VERIFICATION_CODE_INVALID },
            4106: { message: VERIFICATION_CODE_INVALID },
            4107: { message: VERIFICATION_CODE_EXPIRED },
            4108: {
                message: NONCE_TOO_MANY_REQUESTS,
                action: action === null || action === void 0 ? void 0 : action.signIn,
                linkText: 'Return to sign in',
            },
            9001: { message: GENERIC_ERROR },
        };
    }
    if (brand === 'MySB') {
        return {
            4002: { message: VERIFICATION_CODE_INVALID },
            4106: { message: VERIFICATION_CODE_INVALID },
            4107: { message: VERIFICATION_CODE_EXPIRED },
            9001: { message: GENERIC_ERROR },
        };
    }
    return {
        4002: { message: MFA_CODE_INVALID },
        4106: { message: MFA_CODE_INVALID },
        4107: { message: VERIFICATION_CODE_EXPIRED_RETURN_TO_LOGIN },
        9001: { message: GENERIC_ERROR },
    };
};

const getBodyText = (brand, mfaOption, verificationCodeLength, getText) => {
    if (brand === 'Skinny') {
        return 'A six digit code has been sent to your email address.';
    }
    if (brand === 'XtraMail') {
        if ((mfaOption === null || mfaOption === void 0 ? void 0 : mfaOption.type) === 'app') {
            return getText('mfa.viewCode');
        }
        return `${getText('mfa.codeSentTo')} ${mfaOption === null || mfaOption === void 0 ? void 0 : mfaOption.value.replaceAll('*', '•')}`;
    }
    if (brand === 'MySB') {
        return `${getText('mfa.codeSentTo')} ${mfaOption === null || mfaOption === void 0 ? void 0 : mfaOption.value.replaceAll('*', '•')}`;
    }
    return `Enter your ${verificationCodeLength}-digit verification code to continue.`;
};
const DescriptionBody = ({ mfaOption, verificationCodeLength, brand, getText, }) => {
    const { Text, Spacer } = useComponentLibrary();
    const BodyText = getBodyText(brand, mfaOption, verificationCodeLength, getText);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Spacer, { variant: "spacing-01" }),
        React__default.createElement(Text, { variant: "bodySMedium" }, BodyText),
        brand === 'XtraMail' ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Text, { variant: "bodySMedium" }, getText('mfa.enterCodeBelow')),
            React__default.createElement(Spacer, { variant: "spacing-01" }))) : null));
};
const VerificationCode = ({ onLoginSuccess, onSignInResult, }) => {
    const { brand } = useConfig();
    const { InfoBanner, setInfoBannerState, infoBannerState } = useInfoBanner();
    useTrackPageView(getTrackPageProps({
        brand,
        pageName: 'login.verifyMultiFactorAuthState',
        subCategories: ['login'],
        primaryCategory: 'myspark',
        pageType: 'Landing Page',
        user: [
            {
                profile: {
                    attributes: { authState: 'Unknown', multiFactorAuthType: 'Email' },
                },
            },
        ],
    }));
    const { Text, Spacer, Button, transformCodeToString, CodeInput, TextButton } = useComponentLibrary();
    const { isSkinny, isSpark, isXtraMail, isMySB } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    const VERIFICATION_CODE_LENGTH = isSpark ? 8 : 6;
    const { isCodeValid, codeValue, resetForm, handleCodeChange } = useVerificationCodeForm(VERIFICATION_CODE_LENGTH);
    const { getText } = useContent();
    const { handleReturnToSignIn, redirectURL, amlb, stage, nonce, mfaOptions, selectedMfaType, } = useLogin();
    const [activeError, setActiveError] = useState(null);
    const mfaOption = selectedMfaType
        ? mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions.find((option) => option.type === selectedMfaType)
        : mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions[0];
    const { mutate, error, isLoading } = useMutation({
        mutationFn: handleSubmitVerification,
        onSuccess: (res) => {
            trackSuccessfulLoginEvent({
                brand: universalLoginBrand,
                pageName: 'login.verifyMultiFactorAuthState',
                multiFactorAuthType: 'Email',
            }, res.userUid);
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'You are logged in',
            });
            onLoginSuccess();
            setActiveError(null);
        },
        onError: (err) => {
            trackFailedLoginEvent({
                brand: universalLoginBrand,
                pageName: 'login.verifyMultiFactorAuthState',
                multiFactorAuthType: 'Email',
                error: err,
                errorMap: getErrorMessagesMap(brand),
            });
            resetForm();
            setActiveError(err);
            setInfoBannerState({
                variant: null,
                text: '',
            });
        },
    });
    const { mutate: runResend, isLoading: isLoadingResend } = useMutation({
        mutationFn: handleMFACodeRenewal,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'A new verification code has been sent.',
            });
            resetForm();
            setActiveError(null);
        },
        onError: (err) => {
            resetForm();
            setActiveError(null);
            const validationError = getValidationError(err);
            if ((validationError === null || validationError === void 0 ? void 0 : validationError.code) === 4160) {
                setInfoBannerState({
                    variant: 'ERROR',
                    text: 'This session has timed out. Please sign in again.',
                    buttonText: 'Return to sign in',
                    onClick: onSignInResult,
                });
                return;
            }
            setInfoBannerState({
                variant: 'ERROR',
                text: 'There was an error sending the new verification code',
            });
        },
    });
    const isNonceExpired = (error === null || error === void 0 ? void 0 : error.message) === 'Nonce has expired.';
    const hasSubtitle = isSkinny || isSpark;
    const handleButtonClick = isNonceExpired
        ? handleReturnToSignIn
        : () => runResend([
            {
                nonce: nonce !== null && nonce !== void 0 ? nonce : '',
                amlb: amlb !== null && amlb !== void 0 ? amlb : '',
                stage: stage !== null && stage !== void 0 ? stage : '',
                universalLoginBrand,
            },
        ]);
    function handleSubmit(event) {
        event.preventDefault();
        mutate([
            {
                redirectURL,
                mfaToken: transformCodeToString(codeValue),
                nonce: nonce !== null && nonce !== void 0 ? nonce : '',
                stage: stage !== null && stage !== void 0 ? stage : '',
                amlb: amlb !== null && amlb !== void 0 ? amlb : '',
                universalLoginBrand,
            },
        ]);
    }
    const secondaryButtonLabel = isNonceExpired
        ? getText('login.returnToSignInButtonText')
        : getText('login.resendVerificationCode');
    const shouldShowResend = !(isXtraMail && (mfaOption === null || mfaOption === void 0 ? void 0 : mfaOption.type) === 'app');
    return (React__default.createElement(Margin, null,
        React__default.createElement("form", { onSubmit: handleSubmit },
            React__default.createElement(Text, { variant: "subheadingL" }, getText('login.twoStepVerification')),
            hasSubtitle ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySMedium" }, getText('login.toVerifyItsYou')),
                React__default.createElement(Spacer, { variant: "spacing-02" }))) : null,
            infoBannerState.variant ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(InfoBanner, { spacer: "preceding" }),
                React__default.createElement(Spacer, { variant: "spacing-01" }))) : null,
            activeError ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(ErrorMessage, { error: activeError, errorMap: getErrorMessagesMap(brand, {
                        signIn: onSignInResult,
                    }) }),
                React__default.createElement(Spacer, { variant: "spacing-01" }))) : null,
            React__default.createElement(FormRow, null,
                React__default.createElement(DescriptionBody, { getText: getText, brand: brand, mfaOption: mfaOption, verificationCodeLength: VERIFICATION_CODE_LENGTH }),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(LinkRegion, { isNotTracked: true },
                    React__default.createElement(CodeInput, { value: codeValue, onChange: handleCodeChange, inputProps: { 'aria-label': 'code-input', autoFocus: true } }))),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { type: "submit", variant: "primary", disabled: !isCodeValid || isLoading || isLoadingResend, isLoading: isLoading }, getText('login.continueVerify'))),
            shouldShowResend ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(FormRow, null,
                        React__default.createElement(Button, { onClick: handleButtonClick, disabled: isLoadingResend || isLoading, variant: "default", isLoading: isLoadingResend }, secondaryButtonLabel))))) : null,
            isMySB ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(LinkRow, { centered: true },
                        React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))) : null)));
};

const MultifactorVerification = ({ onLoginSuccess, onSignInResult, }) => {
    var _a;
    const { mfaOptions } = useLogin();
    const hasMultipleMfaOptions = ((_a = mfaOptions === null || mfaOptions === void 0 ? void 0 : mfaOptions.length) !== null && _a !== void 0 ? _a : 0) > 1;
    const [mfaStep, setMfaStep] = useState(hasMultipleMfaOptions
        ? MfaStep.SelectMfaOption
        : MfaStep.EnterVerificationCode);
    if (mfaStep === MfaStep.SelectMfaOption)
        return (React__default.createElement(MfaOptions, { onMfaStepChange: setMfaStep, onSignInResult: onSignInResult, mfaOptions: mfaOptions }));
    return (React__default.createElement(VerificationCode, { onLoginSuccess: onLoginSuccess, onSignInResult: onSignInResult }));
};

const LoginHandler = ({ onForgotPasswordResult, onLoginSuccess, onRegisterResult, }) => {
    const { loginStep, email, handleReturnToSignIn } = useLogin();
    if (loginStep === LoginStep.VerifyUser) {
        // Unable to get UUID from login journey - will impact analytics 'uuid' value
        return (React__default.createElement(VerifyJourney, { email: email, onSignInResult: handleReturnToSignIn }));
    }
    return loginStep === LoginStep.MultifactorVerification ? (React__default.createElement(MultifactorVerification, { onLoginSuccess: onLoginSuccess, onSignInResult: handleReturnToSignIn })) : (React__default.createElement(LoginForm, { onForgotPasswordResult: onForgotPasswordResult, onLoginSuccess: onLoginSuccess, onRegisterResult: onRegisterResult }));
};
const Login = (props) => {
    return (React__default.createElement(LoginContextProvider, { redirectURL: props.redirectURL },
        React__default.createElement(LoginHandler, Object.assign({}, props))));
};

const ERROR_MESSAGES_MAP$5 = (brand, action) => {
    if (brand === 'Spark') {
        return {
            4090: {
                message: 'This email address has been used before. Try signing in or use another email address.',
                linkText: 'Sign in',
                action: action === null || action === void 0 ? void 0 : action.signIn,
            },
        };
    }
    return {
        4090: {
            message: 'Sorry, this email address has been used before. Try signing in or use another one',
        },
    };
};
const CheckboxRow = styled(FormRow) `
  white-space: pre-line;
  row-gap: 20px;
`;
const getEmailAvailability = ([email, redirectURL, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield httpClient.get(`/v1/user/identity/availability/${email}`, {}, { redirectURL }, false, 'realmAndBrandInHeaders', universalLoginBrand);
    if (response.available === false) {
        throw new ValidationError('Email address is not available', 4090);
    }
    return response;
});
const RegisterCredentialsForm = ({ onSignInResult, onSaveCredentialsResult, }) => {
    const { brand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand,
        pageName: 'register.enterEmailAndPasswordState',
        subCategories: ['register'],
        primaryCategory: 'myspark',
        pageType: 'Landing Page',
    }));
    const { Text, TextButton, Button, Spacer, Checkbox } = useComponentLibrary();
    const { control, handleSubmit, formState: { errors }, watch, } = useForm({
        mode: 'onSubmit',
        shouldFocusError: true,
        defaultValues: {
            email: '',
            password: '',
            marketingCommsOptIn: true,
        },
    });
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const { isSpark } = useBrand();
    const queryParams = new URLSearchParams(window.location.search);
    const redirectURL = queryParams.get('goto');
    const { password, email, marketingCommsOptIn } = watch();
    const { run, error, isPending } = useAsync({
        deferFn: getEmailAvailability,
        onResolve: () => {
            onSaveCredentialsResult(email, password, marketingCommsOptIn);
        },
        onReject: (err) => {
            trackFailedRegistrationEvent({
                brand,
                pageName: 'register.enterEmailAndPasswordState',
                error: err,
                errorMap: ERROR_MESSAGES_MAP$5(universalLoginBrand),
            });
        },
    });
    function submitHandler(formData) {
        run(formData.email, redirectURL, universalLoginBrand);
    }
    useEffect(() => {
        var _a;
        if (((_a = errors.email) === null || _a === void 0 ? void 0 : _a.type) === 'validate') {
            trackFailedRegistrationValidationEvent({
                brand,
                pageName: 'register.enterEmailAndPasswordState',
                errorMessage: 'Please enter a valid email address',
                internalErrorCode: 4002,
                internalErrorMessage: 'Invalid email',
            });
        }
    }, [errors]);
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, getText('register.title')),
        React__default.createElement(Spacer, { variant: "spacing-01" }),
        React__default.createElement(Text, { variant: "bodySMedium" }, getText('register.toManage')),
        error ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$5(universalLoginBrand, {
                    signIn: onSignInResult,
                }) }))) : null,
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(LinkRegion, { isNotTracked: true },
                React__default.createElement(Controller, { control: control, name: "email", rules: {
                        required: true,
                        validate: isEmailValid,
                    }, render: ({ field }) => (React__default.createElement(EmailInput, { error: errors.email, disabled: isPending, name: field.name, onChange: field.onChange, value: field.value })) }),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(Controller, { control: control, name: "password", rules: {
                        required: true,
                        minLength: 1,
                        maxLength: 100,
                        validate: isPasswordEntered,
                    }, render: ({ field }) => (React__default.createElement(PasswordInput, { isPasswordStrengthEnabled: true, title: "Password", disabled: isPending, name: field.name, onChange: field.onChange, value: field.value })) }),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                isSpark ? (React__default.createElement(Controller, { control: control, name: "marketingCommsOptIn", render: ({ field }) => (React__default.createElement(CheckboxRow, null,
                        React__default.createElement(Checkbox, { label: `${getText('register.marketingCommsMessage')}`, checked: field.value, onChange: () => field.onChange(!field.value) }),
                        React__default.createElement(Text, { variant: "footnoteMedium" }, `${getText('register.marketingCommsNote')}`))) })) : null),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: !email || !isPasswordValid(password) || isPending, isLoading: isPending, type: "submit", variant: "primary" }, getText('register.next'))),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))));
};

const postUserRegistrationDetails = ([{ email, firstName, lastName, password, marketingComms, verificationType, redirectURL, universalLoginBrand, },]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.post('/v1/user/identity', {
        email,
        firstName,
        lastName,
        password,
        marketingComms,
        verificationType,
    }, { redirectURL }, false, 'realmAndBrandInHeaders', universalLoginBrand);
});
const RegisterDetailsForm = ({ onSignInResult, onSaveDetailsResult, email, password, marketingCommsOptIn, }) => {
    const { brand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand,
        pageName: 'register.enterNameState',
        subCategories: ['register'],
        primaryCategory: 'myspark',
        pageType: 'Landing Page',
    }));
    const { Text, TextButton, Button, Spacer, TextInput } = useComponentLibrary();
    const { control, watch, formState, handleSubmit } = useForm({
        mode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
        },
    });
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const queryParams = new URLSearchParams(window.location.search);
    const redirectURL = queryParams.get('goto');
    const { firstName, lastName } = watch();
    const { run, isPending } = useAsync({
        deferFn: postUserRegistrationDetails,
        onResolve: (res) => {
            onSaveDetailsResult(firstName, lastName, res.uuid);
        },
    });
    function submitHandler() {
        const registerBodyParams = {
            email,
            password,
            firstName,
            lastName,
            marketingComms: marketingCommsOptIn,
            verificationType: 'Code',
            redirectURL,
            universalLoginBrand,
        };
        run(registerBodyParams);
    }
    return (React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('register.title')),
            React__default.createElement(Spacer, { variant: "spacing-01" }),
            React__default.createElement(Text, { variant: "bodySMedium" }, getText('register.toManage')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('register.enterYourName')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(LinkRegion, { isNotTracked: true },
                    React__default.createElement(Text, { variant: "bodySDemi" }, getText('firstName')),
                    React__default.createElement(Controller, { control: control, name: "firstName", rules: {
                            required: true,
                            minLength: 1,
                            maxLength: 100,
                            pattern: engAlphabetOnlyRegex,
                        }, render: ({ field }) => (React__default.createElement(TextInput, { name: field.name, type: "text", disabled: isPending, onChange: field.onChange, value: field.value })) }))),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('lastName')),
                React__default.createElement(Controller, { control: control, name: "lastName", rules: {
                        required: true,
                        minLength: 1,
                        maxLength: 100,
                        pattern: engAlphabetOnlyRegex,
                    }, render: ({ field }) => (React__default.createElement(TextInput, { name: field.name, type: "text", disabled: isPending, onChange: field.onChange, value: field.value })) })),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { disabled: !formState.isValid || isPending, isLoading: isPending, type: "submit", variant: "primary" }, getText('register.nextStepText'))),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))));
};

var RegisterStep;
(function (RegisterStep) {
    RegisterStep[RegisterStep["RegisterCredentialsForm"] = 0] = "RegisterCredentialsForm";
    RegisterStep[RegisterStep["RegisterDetailsForm"] = 1] = "RegisterDetailsForm";
    RegisterStep[RegisterStep["RegisterVerification"] = 2] = "RegisterVerification";
})(RegisterStep || (RegisterStep = {}));
const initialState$3 = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    uuid: '',
    step: RegisterStep.RegisterCredentialsForm,
};
const useRegister = () => {
    const [state, setState] = useState(initialState$3);
    const saveCredentials = (email, password, marketingCommsOptIn) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: RegisterStep.RegisterDetailsForm, email,
            password,
            marketingCommsOptIn })));
    };
    const saveDetails = (firstName, lastName, uuid) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: RegisterStep.RegisterVerification, firstName,
            lastName,
            uuid })));
    };
    return { state, saveCredentials, saveDetails };
};

const Register = ({ onSignInResult }) => {
    const { state, saveCredentials, saveDetails } = useRegister();
    if (state.step === RegisterStep.RegisterVerification) {
        return (React__default.createElement(VerifyJourney, { email: state.email, uuid: state.uuid, onSignInResult: onSignInResult }));
    }
    if (state.step === RegisterStep.RegisterCredentialsForm) {
        return (React__default.createElement(RegisterCredentialsForm, { onSignInResult: onSignInResult, onSaveCredentialsResult: saveCredentials }));
    }
    return (React__default.createElement(RegisterDetailsForm, { email: state.email, password: state.password, marketingCommsOptIn: state.marketingCommsOptIn, onSignInResult: onSignInResult, onSaveDetailsResult: saveDetails }));
};
var Register$1 = withPageBrandGuard(Flags.flagEnableRegisterJourney, Register, () => React__default.createElement(PageNotAvailableMessage, { pageName: "Registration" }));

const LoadingContainer$1 = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => spacing.s3};
`;
const LoadingScreen$1 = () => {
    const { LoadingSpinner } = useComponentLibrary();
    return (React__default.createElement(LoadingContainer$1, null,
        React__default.createElement(LoadingSpinner, { size: 32 })));
};

const handleGetTokenDetails$1 = ([verificationToken, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.get(`/v1/user/Verification/MySparkBusinessForgottenPassword/${verificationToken}`, {}, {}, false, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});
const handleSecurityQuestions = ([verificationToken, securityQuestionsAndAnswersData, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.patch(`/v1/user/Verification/MySparkBusinessForgottenPasswordAnswers/${verificationToken}`, securityQuestionsAndAnswersData, {}, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});
const handleCreateSecurityQuestions = ([verificationToken, securityQuestions, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.patch(`/v1/user/Verification/MySparkBusinessForgottenPasswordAnswers/${verificationToken}`, securityQuestions, {}, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

var SecurityQuestionStep;
(function (SecurityQuestionStep) {
    SecurityQuestionStep[SecurityQuestionStep["CreateSecurityQuestions"] = 0] = "CreateSecurityQuestions";
    SecurityQuestionStep[SecurityQuestionStep["SetSecurityQuestionsConfirmation"] = 1] = "SetSecurityQuestionsConfirmation";
    SecurityQuestionStep[SecurityQuestionStep["AnswerSecurityQuestions"] = 2] = "AnswerSecurityQuestions";
    SecurityQuestionStep[SecurityQuestionStep["SecurityQuestionsError"] = 3] = "SecurityQuestionsError";
})(SecurityQuestionStep || (SecurityQuestionStep = {}));
const initialState$2 = {
    step: null,
    error: null,
};
const useSecurityQuestion = () => {
    const [state, setState] = useState(initialState$2);
    const setSecurityQuestionsStateErrored = (error) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: SecurityQuestionStep.SecurityQuestionsError, error })));
    };
    const setSecurityQuestionsStep = (step) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step })));
    };
    return {
        state,
        setSecurityQuestionsStep,
        setSecurityQuestionsStateErrored,
    };
};

const INVALID_TOKEN_ERROR_MESSAGE = 'This link has expired.';
const EXPIRED_TOKEN_ERROR_MESSAGE = 'This link has expired.';
const EXPIRED_TOKEN_ACTIVATE_ACCOUNT_ERROR_MESSAGE = 'This link has expired. Please contact your admin to get a new activation link generated.';
const EXPIRED_TOKEN_UNLOCK_ACCOUNT_ERROR_MESSAGE = 'This link has expired. Please contact your admin to unlock your account.';
const EXPIRED_TOKEN_ENABLE_ACCOUNT_ERROR_MESSAGE = 'This link has expired. Please contact your admin to enable your account.';
const INCORRECT_ANSWER_ERROR_MESSAGE = 'At least one or all of your questions were answered incorrectly.';
const LOCKED_ACCOUNT_ERROR_MESSAGE = 'Your account has been locked. Please contact your administrator to reset your password.';
const findAttributeValueByKey = (data, key) => {
    var _a;
    return (_a = data === null || data === void 0 ? void 0 : data.find((dataAttribute) => dataAttribute.key === key)) === null || _a === void 0 ? void 0 : _a.value;
};

const SecurityQuestionRetriever = ({ token, setSecurityQuestionsStateErrored, setSecurityQuestionsStep, onGetTokenDetails, children, }) => {
    const [userIdentity, setUserIdentity] = useState('');
    const [securityQuestions, setSecurityQuestions] = useState([]);
    const { brand: universalLoginBrand } = useConfig();
    const { isLoading } = useQuery({
        queryKey: ['SecurityQuestionsRetriever', token],
        queryFn: () => handleGetTokenDetails$1([token, universalLoginBrand]),
        onSuccess: (res) => {
            var _a, _b, _c, _d;
            const userId = findAttributeValueByKey((_a = res.tokenData) === null || _a === void 0 ? void 0 : _a.data, 'userId') || '';
            const journeyType = findAttributeValueByKey((_b = res.tokenData) === null || _b === void 0 ? void 0 : _b.data, 'journeyType');
            if (!journeyType) {
                setSecurityQuestionsStateErrored(new ValidationError('no journey type was found', 9001));
            }
            else {
                setUserIdentity(userId);
                onGetTokenDetails(userId);
                if (journeyType === 'ForgotPassword') {
                    setSecurityQuestionsStep(SecurityQuestionStep.AnswerSecurityQuestions);
                }
                else {
                    setSecurityQuestionsStep(SecurityQuestionStep.CreateSecurityQuestions);
                }
                const questions = (_d = (_c = res.tokenData) === null || _c === void 0 ? void 0 : _c.securityQuestions) === null || _d === void 0 ? void 0 : _d.map((questionData) => ({
                    questionId: questionData.key,
                    questionText: questionData.value,
                }));
                setSecurityQuestions(questions);
            }
        },
        onError: (error) => {
            const responseError = error;
            setSecurityQuestionsStateErrored(new ValidationError(responseError.message, responseError.code));
        },
    });
    return React__default.createElement(React__default.Fragment, null, children({ userIdentity, isLoading, securityQuestions }));
};

const ERROR_MESSAGES_MAP$4 = () => {
    return {
        4001: {
            message: INCORRECT_ANSWER_ERROR_MESSAGE,
        },
        4110: { message: INVALID_TOKEN_ERROR_MESSAGE },
        4111: { message: EXPIRED_TOKEN_ERROR_MESSAGE },
        4116: { message: LOCKED_ACCOUNT_ERROR_MESSAGE },
        9001: {
            message: 'There was an error with sending the answers. Please try again later.',
        },
    };
};
const AnswerSecurityQuestionForm = ({ token, onSubmitSecurityQuestionResult, setSecurityQuestionsAnswers, setSecurityQuestionsStateErrored, securityQuestions, }) => {
    const { Button, Spacer, Text } = useComponentLibrary();
    const { brand: universalLoginBrand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotPassword.securityQuestionsAnswer',
        subCategories: ['forgotPassword'],
    }));
    const { mutate, isLoading, error } = useMutation({
        mutationFn: handleSecurityQuestions,
        onSuccess: () => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Forgot Password Security Questions Action',
                w3cddlEventName: 'forgotPassword.securityQuestionsAction',
                pageName: 'forgotPassword.securityQuestionsAnswer',
            };
            trackSuccessRequestEvent(trackingData);
            onSubmitSecurityQuestionResult();
        },
        onError: (responseError) => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Forgot Password Security Questions Action',
                w3cddlEventName: 'forgotPassword.securityQuestionsAction',
                pageName: 'forgotPassword.securityQuestionsAnswer',
                error: responseError,
                errorMap: ERROR_MESSAGES_MAP$4(),
            };
            trackFailureRequestEvent(trackingData);
            if (responseError.code && responseError.code === 4116) {
                setSecurityQuestionsStateErrored(new ValidationError(responseError.message, responseError.code));
            }
        },
    });
    const { control, handleSubmit, formState } = useForm({
        defaultValues: {
            securityAnswer1: '',
            securityAnswer2: '',
        },
    });
    function submitHandler(formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const securityAnswers = [
                { key: 'securityAnswer1', value: formData.securityAnswer1 },
                { key: 'securityAnswer2', value: formData.securityAnswer2 },
            ];
            setSecurityQuestionsAnswers(securityAnswers);
            mutate([token, securityAnswers, universalLoginBrand]);
        });
    }
    return (React__default.createElement(LinkRegion, { regionId: "Security questions" },
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(Margin, null,
                React__default.createElement(Text, { variant: "subheadingL" }, "Security questions"),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, "Please answer the following security questions to continue with changing your password."),
                error ? (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$4() }))) : null,
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(FormRow, null,
                    React__default.createElement(LinkRegion, { isNotTracked: true },
                        React__default.createElement(Controller, { control: control, name: "securityAnswer1", rules: { required: true }, render: ({ field }) => (React__default.createElement(PasswordInput, { title: securityQuestions[0].questionText, disabled: isLoading, name: field.name, onChange: field.onChange, value: field.value, placeholder: "Enter your answer" })) }),
                        React__default.createElement(Spacer, { variant: "spacing-03" }),
                        React__default.createElement(Controller, { control: control, name: "securityAnswer2", rules: { required: true }, render: ({ field }) => (React__default.createElement(PasswordInput, { title: securityQuestions[1].questionText, disabled: isLoading, name: field.name, onChange: field.onChange, value: field.value, placeholder: "Enter your answer" })) }))),
                React__default.createElement(FormRow, null,
                    React__default.createElement(Spacer, { variant: "spacing-03" }),
                    React__default.createElement(Button, { disabled: !formState.isValid || isLoading, isLoading: isLoading, type: "submit", variant: "primary" }, "Continue"))))));
};

const SecurityQuestionDropdownField = ({ control, name, label, options, }) => (React__default.createElement(LinkRegion, { isNotTracked: true },
    React__default.createElement(Controller, { control: control, name: name, rules: { required: true }, render: ({ field }) => (React__default.createElement(FormDropdown, { label: label, name: field.name, value: field.value, options: options, onChange: field.onChange, placeholder: "Select question" })) })));

const SecurityQuestionInputField = ({ control, name, }) => {
    const { TextInput } = useComponentLibrary();
    return (React__default.createElement(LinkRegion, { isNotTracked: true },
        React__default.createElement(Controller, { control: control, name: name, rules: { required: true }, render: ({ field }) => (React__default.createElement(TextInput, { name: field.name, value: field.value, onChange: field.onChange, placeholder: "Enter answer" })) })));
};

const ERROR_MESSAGES_MAP$3 = () => {
    return {
        4110: { message: INVALID_TOKEN_ERROR_MESSAGE },
        4111: { message: EXPIRED_TOKEN_ERROR_MESSAGE },
        9001: {
            message: 'There was an error with sending the questions and answers. Please try again later.',
        },
    };
};
const CreateSecurityQuestionsForm = ({ token, securityQuestions, setSecurityQuestionsStep, setSecurityQuestionsAnswers, }) => {
    const [hasTermsAccepted, setHasTermsAccepted] = useState(false);
    const { brand: universalLoginBrand } = useConfig();
    const { Button, Spacer, Text, Checkbox, InlineLink } = useComponentLibrary();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'register.securityQuestionsSetup',
        subCategories: ['register'],
    }));
    const { mutate, isLoading, error } = useMutation({
        mutationFn: handleCreateSecurityQuestions,
        onSuccess: () => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Register Security Questions Setup Action',
                w3cddlEventName: 'register.securityQuestionsSetupAction',
                pageName: 'register.securityQuestionsSetup',
            };
            trackSuccessRequestEvent(trackingData);
            setSecurityQuestionsStep(SecurityQuestionStep.SetSecurityQuestionsConfirmation);
        },
        onError: (responseError) => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Register Security Questions Setup Action',
                w3cddlEventName: 'register.securityQuestionsSetupAction',
                pageName: 'register.securityQuestionsSetup',
                error: responseError,
                errorMap: ERROR_MESSAGES_MAP$3(),
            };
            trackFailureRequestEvent(trackingData);
        },
    });
    const { control, handleSubmit, formState, watch } = useForm({
        defaultValues: {
            securityQuestion1: null,
            securityAnswer1: '',
            securityQuestion2: null,
            securityAnswer2: '',
        },
    });
    const submitHandler = (formData) => {
        var _a, _b;
        const securityAnswers = [
            { key: 'securityAnswer1', value: formData.securityAnswer1 },
            { key: 'securityAnswer2', value: formData.securityAnswer2 },
        ];
        const securityQuestionsAndAnswers = [
            ...securityAnswers,
            { key: 'securityQuestion1', value: (_a = formData.securityQuestion1) === null || _a === void 0 ? void 0 : _a.value },
            { key: 'securityQuestion2', value: (_b = formData.securityQuestion2) === null || _b === void 0 ? void 0 : _b.value },
            { key: 'termsAndConditions', value: hasTermsAccepted.toString() },
        ];
        setSecurityQuestionsAnswers(securityAnswers);
        mutate([token, securityQuestionsAndAnswers, universalLoginBrand]);
    };
    const watchedQuestion1 = watch('securityQuestion1');
    const watchedQuestion2 = watch('securityQuestion2');
    const questionOptions = securityQuestions.map((question) => ({
        value: question.questionId,
        label: question.questionText,
    }));
    const filteredOptions1 = questionOptions.filter((option) => option.value !== (watchedQuestion2 === null || watchedQuestion2 === void 0 ? void 0 : watchedQuestion2.value));
    const filteredOptions2 = questionOptions.filter((option) => option.value !== (watchedQuestion1 === null || watchedQuestion1 === void 0 ? void 0 : watchedQuestion1.value));
    return (React__default.createElement(LinkRegion, { regionId: "Security questions" },
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(Margin, null,
                React__default.createElement(Text, { variant: "subheadingL" }, "Security questions"),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, "Please set up 2 security questions."),
                error ? (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$3() }))) : null,
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(FormRow, null,
                    React__default.createElement(SecurityQuestionDropdownField, { control: control, name: "securityQuestion1", label: "Question 1", options: filteredOptions1 }),
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(SecurityQuestionInputField, { control: control, name: "securityAnswer1" }),
                    React__default.createElement(Spacer, { variant: "spacing-03" }),
                    React__default.createElement(SecurityQuestionDropdownField, { control: control, name: "securityQuestion2", label: "Question 2", options: filteredOptions2 }),
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(SecurityQuestionInputField, { control: control, name: "securityAnswer2" })),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(Checkbox, { label: React__default.createElement(FormRow, null,
                        React__default.createElement(Text, { variant: "bodySDemi" },
                            "I accept the",
                            ' ',
                            React__default.createElement(InlineLink, { href: "https://www.sparkdigital.co.nz/support/mysparkdigital-terms", target: "_blank", rel: "noreferrer", variant: "bodySDemiStyle" }, "MySpark Business terms and conditions")),
                        React__default.createElement(Spacer, { variant: "spacing-01" }),
                        React__default.createElement(Text, { variant: "footnoteMedium", color: colors.mawheranui }, "I am authorised to create or update this account and accept these terms on behalf of my employer.")), checked: hasTermsAccepted, onChange: () => setHasTermsAccepted(!hasTermsAccepted) }),
                React__default.createElement(FormRow, null,
                    React__default.createElement(Spacer, { variant: "spacing-03" }),
                    React__default.createElement(Button, { disabled: !(formState.isValid && hasTermsAccepted) || isLoading, isLoading: isLoading, type: "submit", variant: "primary" }, "Continue"))))));
};

const ERROR_MESSAGES_MAP$2 = {
    4110: { message: INVALID_TOKEN_ERROR_MESSAGE },
    4111: { message: EXPIRED_TOKEN_ERROR_MESSAGE },
    4112: { message: EXPIRED_TOKEN_ERROR_MESSAGE },
    4113: { message: EXPIRED_TOKEN_ACTIVATE_ACCOUNT_ERROR_MESSAGE },
    4114: { message: EXPIRED_TOKEN_UNLOCK_ACCOUNT_ERROR_MESSAGE },
    4115: { message: EXPIRED_TOKEN_ENABLE_ACCOUNT_ERROR_MESSAGE },
    4116: { message: LOCKED_ACCOUNT_ERROR_MESSAGE },
    9001: { message: 'Something went wrong' },
};
const SecurityQuestionError = ({ error, onSignInResult, }) => {
    const { Text, Spacer, Button } = useComponentLibrary();
    if (!error) {
        return null;
    }
    let pageConfig = {
        title: 'Reset password',
        hasButton: true,
    };
    switch (error.code) {
        case 4113:
            pageConfig = {
                title: 'Activate account',
                hasButton: false,
            };
            break;
        case 4114:
            pageConfig = {
                title: 'Unlock account',
                hasButton: false,
            };
            break;
        case 4115:
            pageConfig = {
                title: 'Enable account',
                hasButton: false,
            };
            break;
        case 4116:
            pageConfig = {
                title: 'Security questions',
                hasButton: false,
            };
            break;
    }
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, pageConfig.title),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$2 }),
        pageConfig.hasButton ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(Button, { onClick: onSignInResult, variant: "primary" }, "Return to sign in"))) : null));
};

const SecurityQuestionsConfirmation = ({ onSubmitSecurityQuestionResult }) => {
    const { Text, Button, Spacer, InfoBanner } = useComponentLibrary();
    const { brand: universalLoginBrand } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'register.securityQuestionsSetupSuccess',
        subCategories: ['register'],
    }));
    return (React__default.createElement(LinkRegion, { regionId: "Security questions" },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, "Security questions"),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(InfoBanner, { variant: "SUCCESS", text: "Your security questions have successfully been saved." }),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { onClick: onSubmitSecurityQuestionResult, variant: "primary" }, "Set password")))));
};

const SecurityQuestionJourney = ({ emailValidationToken, setSecurityQuestionsAnswers, onSubmitSecurityQuestionResult, onGetTokenDetails, onSignInResult, }) => {
    const { state, setSecurityQuestionsStateErrored, setSecurityQuestionsStep } = useSecurityQuestion();
    if (state.step === SecurityQuestionStep.SecurityQuestionsError) {
        return (React__default.createElement(SecurityQuestionError, { error: state.error, onSignInResult: onSignInResult }));
    }
    return (React__default.createElement(SecurityQuestionRetriever, { token: emailValidationToken, setSecurityQuestionsStateErrored: setSecurityQuestionsStateErrored, setSecurityQuestionsStep: setSecurityQuestionsStep, onGetTokenDetails: onGetTokenDetails }, ({ isLoading, securityQuestions, userIdentity }) => {
        if (isLoading)
            return React__default.createElement(LoadingScreen$1, null);
        if (!userIdentity)
            return (React__default.createElement(SecurityQuestionError, { error: new ValidationError('user Id not found', 9001), onSignInResult: onSignInResult }));
        if (!securityQuestions)
            return (React__default.createElement(SecurityQuestionError, { error: new ValidationError('security questions not found', 9001), onSignInResult: onSignInResult }));
        if (state.step === SecurityQuestionStep.SetSecurityQuestionsConfirmation) {
            return (React__default.createElement(SecurityQuestionsConfirmation, { onSubmitSecurityQuestionResult: onSubmitSecurityQuestionResult }));
        }
        if (state.step === SecurityQuestionStep.AnswerSecurityQuestions) {
            return (React__default.createElement(AnswerSecurityQuestionForm, { token: emailValidationToken, securityQuestions: securityQuestions, onSubmitSecurityQuestionResult: onSubmitSecurityQuestionResult, setSecurityQuestionsAnswers: setSecurityQuestionsAnswers, setSecurityQuestionsStateErrored: setSecurityQuestionsStateErrored }));
        }
        return (React__default.createElement(CreateSecurityQuestionsForm, { token: emailValidationToken, securityQuestions: securityQuestions, setSecurityQuestionsStep: setSecurityQuestionsStep, setSecurityQuestionsAnswers: setSecurityQuestionsAnswers }));
    }));
};

const SetPasswordConfirmation = ({ onSignInResult, }) => {
    var _a;
    const { Text, Button, TextButton, Spacer, FeedbackAlert, InfoBanner } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny, isMySB } = useBrand();
    const continueButtonText = isMySB ? 'Continue to Sign in' : 'Continue';
    const resetPasswordSuccessMessage = ((_a = getText('resetPassword.successMessage')) === null || _a === void 0 ? void 0 : _a.toString()) ||
        "You've successfully changed your password.";
    return (React__default.createElement(LinkRegion, { regionId: "Set new password" },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.success')),
            isSkinny ? (React__default.createElement(FeedbackAlert, { variant: "information" }, resetPasswordSuccessMessage)) : (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, { variant: "spacing-02" }),
                React__default.createElement(InfoBanner, { variant: "SUCCESS", text: resetPasswordSuccessMessage }))),
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(FormRow, null, isSkinny ? (React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn'))) : (React__default.createElement(Button, { onClick: onSignInResult, variant: "primary" }, continueButtonText))))));
};

const PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY = 'Your password reset link has expired or has already been used.';
const USED_PASSWORD_ERROR_MESSAGE_SKINNY = 'Ah, looks like you’ve used that password before. You need to create a new super secret one.';
const EXPIRED_CODE_ERROR_MESSAGE = 'Your password reset attempt has expired. Please try again.';
const INVALID_CODE_ERROR_MESSAGE = 'Your password reset attempt is invalid. Please try again.';
const getErrorMessages = (universalLoginBrand, verificationMethod) => {
    if (universalLoginBrand === 'Skinny')
        return {
            4110: { message: PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY },
            4111: { message: PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY },
            4022: { message: PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY },
            4023: { message: PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY },
            4028: { message: PASSWORD_RESET_LINK_ERROR_MESSAGE_SKINNY },
            4033: { message: USED_PASSWORD_ERROR_MESSAGE_SKINNY },
        };
    if (universalLoginBrand === 'MySB')
        return {
            4001: { message: INVALID_CODE_ERROR_MESSAGE },
            4110: { message: INVALID_CODE_ERROR_MESSAGE },
            4111: { message: EXPIRED_CODE_ERROR_MESSAGE },
        };
    if (verificationMethod === VerificationMethod.CODE)
        return {
            4110: { message: INVALID_CODE_ERROR_MESSAGE },
            4111: { message: EXPIRED_CODE_ERROR_MESSAGE },
            4022: { message: EXPIRED_CODE_ERROR_MESSAGE },
            4023: { message: INVALID_CODE_ERROR_MESSAGE },
            4028: { message: INVALID_CODE_ERROR_MESSAGE },
            4033: { message: 'The new password is a previous password for this id.' },
        };
    return {
        4110: { message: 'Token is invalid.' },
        4111: { message: 'Token is expired.' },
        4022: { message: 'Token is expired.' },
        4023: { message: 'Token is invalid.' },
        4028: { message: 'Token is invalid.' },
        4033: { message: 'The new password is a previous password for this id.' },
    };
};
const SetPasswordError = ({ onForgotPasswordResult, onSignInResult, email = '', error, }) => {
    const { Text, Button, TextButton, Spacer } = useComponentLibrary();
    const { getText } = useContent();
    const { InfoBanner, setInfoBannerState } = useInfoBanner();
    const { isSkinny, isMySB } = useBrand();
    const { brand: universalLoginBrand } = useConfig();
    const { flagEnablePasswordResetByCode } = useFeatureFlags();
    const verificationMethod = flagEnablePasswordResetByCode
        ? VerificationMethod.CODE
        : VerificationMethod.TOKEN;
    const errorMap = getErrorMessages(universalLoginBrand, verificationMethod);
    const { mutate: onResendLink, isLoading } = useMutation({
        mutationFn: handleForgotPassword,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: 'A new reset link has been sent',
            });
        },
        onError: () => {
            setInfoBannerState({
                variant: 'ERROR',
                text: 'There was an error sending the reset link',
            });
        },
    });
    const reenterUserIdentityText = isMySB
        ? 'Reenter your User ID'
        : 'Reenter your email';
    /*
     * For a token to be re-sent (rather than restarting /forgotpassword) the following conditions must be met:
     * - using token based verification
     * - with an email address present
     */
    const canRequestResend = verificationMethod === VerificationMethod.TOKEN && email;
    return (React__default.createElement(Margin, null,
        React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.enterPassword')),
        React__default.createElement(InfoBanner, { spacer: "preceding" }),
        error ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-02" }),
            React__default.createElement(ErrorMessage, { error: error, errorMap: errorMap }))) : null,
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(FormRow, null, canRequestResend && !isMySB ? (React__default.createElement(Button, { disabled: isLoading, isLoading: isLoading, onClick: () => onResendLink([
                email,
                universalLoginBrand,
                VerificationMethod.TOKEN,
            ]), variant: "primary" }, "Resend Link")) : (React__default.createElement(Button, { onClick: onForgotPasswordResult, variant: "primary" }, reenterUserIdentityText))),
        isSkinny ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, { variant: "spacing-04" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(LinkRegion, { regionId: "body" },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))) : null));
};

const getParamByVerificationMethod = {
    [VerificationMethod.CODE]: 'code',
    [VerificationMethod.TOKEN]: 'token',
};
const handleSetPassword = ({ password, passwordToken, email, universalLoginBrand, verificationMethod, additionalData, }) => __awaiter(void 0, void 0, void 0, function* () {
    if (universalLoginBrand === 'MySB' && additionalData) {
        return httpClient.patch(`/v1/user/Identity/MySparkBusinessSetPassword/${passwordToken}`, [...additionalData, { key: 'password', value: password }], {}, 'withoutRealmAndBrandInHeaders', 'MySB');
    }
    const verificationMethodParam = getParamByVerificationMethod[verificationMethod];
    const queryString = qs.stringify({
        [verificationMethodParam]: passwordToken,
    });
    return httpClient.patch(`/v1/user/identity/${email}/password?${queryString}`, {
        password,
    }, {}, 'realmAndBrandInHeaders', universalLoginBrand);
});
const handleGetTokenDetails = ([passwordToken, universalLoginBrand,]) => __awaiter(void 0, void 0, void 0, function* () {
    const isSkinny = universalLoginBrand === 'Skinny';
    const processType = isSkinny
        ? 'SkinnyForgottenPassword'
        : 'SparkForgottenPassword';
    return httpClient.get(`/v1/utility/verification/${processType}/${passwordToken}`, {}, {}, false, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

const SetPasswordIdentityRetriever = ({ setPasswordStateErrored, passwordToken, children, }) => {
    const [userIdentity, setUserIdentity] = useState('');
    const { brand: universalLoginBrand } = useConfig();
    const { isLoading } = useQuery({
        queryKey: ['SetPasswordRetriever', passwordToken],
        queryFn: () => handleGetTokenDetails([passwordToken, universalLoginBrand]),
        onSuccess: (res) => {
            var _a, _b, _c, _d;
            const userEmail = ((_c = (_b = (_a = res.tokenData) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.find((tokenAttribute) => tokenAttribute.key === 'emailAddress')) === null || _c === void 0 ? void 0 : _c.value) || '';
            if ((_d = res.tokenData) === null || _d === void 0 ? void 0 : _d.expired) {
                setPasswordStateErrored(new ValidationError('Token expired', 4111), userEmail);
            }
            else {
                setUserIdentity(userEmail);
            }
        },
        onError: () => {
            setPasswordStateErrored(new ValidationError('Email not found', 4110), '');
        },
    });
    return React__default.createElement(React__default.Fragment, null, children({ userIdentity, isLoading }));
};

var PasswordMinimumLength;
(function (PasswordMinimumLength) {
    PasswordMinimumLength[PasswordMinimumLength["Default"] = 8] = "Default";
    PasswordMinimumLength[PasswordMinimumLength["MySb"] = 10] = "MySb";
})(PasswordMinimumLength || (PasswordMinimumLength = {}));
const FormRowWithMargin = styled(FormRow) `
  margin-bottom: ${({ theme: { spacing } }) => spacing.s4};
`;
const SetPasswordFormInternal = ({ setPasswordStateConfirmed, setPasswordStateErrored, passwordToken, onSignInResult, userIdentity, verificationMethod, securityQuestionsAnswers, }) => {
    var _a;
    const { Button, TextButton, Spacer, Text } = useComponentLibrary();
    const { getText } = useContent();
    const { isSkinny, isMySB } = useBrand();
    const { brand: universalLoginBrand, launchDarklyFlags } = useConfig();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'register.enterNewPasswordState',
        subCategories: ['register'],
    }));
    const { mutate, isLoading: isPasswordPending } = useMutation({
        mutationFn: handleSetPassword,
        onSuccess: () => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Register Set Password Success',
                w3cddlEventName: 'register.setPasswordAction',
                pageName: 'register.enterNewPasswordState',
            };
            trackSuccessRequestEvent(trackingData);
            setPasswordStateConfirmed(userIdentity);
        },
        onError: (error) => {
            const trackingData = {
                brand: universalLoginBrand,
                eventName: 'Register Set Password Failure',
                w3cddlEventName: 'register.setPasswordAction',
                pageName: 'register.enterNewPasswordState',
                error,
                errorMap: getErrorMessages(universalLoginBrand, verificationMethod),
            };
            trackFailureRequestEvent(trackingData);
            setPasswordStateErrored(error, userIdentity);
        },
    });
    const { control, handleSubmit, formState, watch } = useForm({
        mode: 'onChange',
        defaultValues: {
            password: '',
            reenteredPassword: '',
        },
    });
    function submitHandler(formData) {
        return __awaiter(this, void 0, void 0, function* () {
            mutate({
                password: formData.password,
                passwordToken,
                email: userIdentity,
                universalLoginBrand,
                verificationMethod,
                additionalData: isMySB ? securityQuestionsAnswers : undefined,
            });
        });
    }
    const password = watch('password');
    const reenteredPassword = watch('reenteredPassword');
    const doPasswordsMatch = reenteredPassword === password;
    const disableButton = isMySB
        ? !formState.isValid || isPasswordPending || !doPasswordsMatch
        : !formState.isValid || isPasswordPending;
    const isMysbPasswordStrengthEnabled = (_a = launchDarklyFlags === null || launchDarklyFlags === void 0 ? void 0 : launchDarklyFlags['release-mysb-password-strength']) !== null && _a !== void 0 ? _a : false;
    const passwordMinimumLength = isMySB && isMysbPasswordStrengthEnabled
        ? PasswordMinimumLength.MySb
        : PasswordMinimumLength.Default;
    const passwordsDontMatchAlert = reenteredPassword && !doPasswordsMatch ? (React__default.createElement(FieldErrorMessage, { variant: "bodySDemi" }, getText('resetPassword.passwordsDontMatch'))) : null;
    return (React__default.createElement(LinkRegion, { regionId: "Set new password" },
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(Margin, null,
                React__default.createElement(Text, { variant: "subheadingL" }, getText('resetPassword.enterPassword')),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('resetPassword.enterNewPassword')),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(FormRowWithMargin, null,
                    React__default.createElement(LinkRegion, { isNotTracked: true },
                        React__default.createElement(Controller, { control: control, name: "password", rules: {
                                required: true,
                                minLength: passwordMinimumLength,
                                maxLength: 100,
                                validate: (value) => isPasswordValid(value, passwordMinimumLength),
                            }, render: ({ field }) => {
                                var _a;
                                return (React__default.createElement(PasswordInput, { isPasswordStrengthEnabled: true, title: ((_a = getText('resetPassword.newPassword')) === null || _a === void 0 ? void 0 : _a.toString()) ||
                                        'New password', disabled: isPasswordPending, name: field.name, onChange: field.onChange, value: field.value, passwordMinimalLength: passwordMinimumLength, placeholder: isMySB ? 'Enter new password' : undefined }));
                            } })),
                    isMySB ? (React__default.createElement(React__default.Fragment, null,
                        React__default.createElement(Spacer, { variant: "spacing-03" }),
                        React__default.createElement(LinkRegion, { isNotTracked: true },
                            React__default.createElement(Controller, { control: control, name: "reenteredPassword", render: ({ field }) => {
                                    var _a;
                                    return (React__default.createElement(PasswordInput, { title: ((_a = getText('resetPassword.reenterNewPassword')) === null || _a === void 0 ? void 0 : _a.toString()) || 'Confirm new password', disabled: isPasswordPending, name: field.name, onChange: field.onChange, value: field.value, passwordMinimalLength: passwordMinimumLength, placeholder: isMySB ? 'Enter new password' : undefined }));
                                } })),
                        passwordsDontMatchAlert)) : null),
                React__default.createElement(FormRow, null,
                    React__default.createElement(Button, { disabled: disableButton, isLoading: isPasswordPending, type: "submit", variant: "primary" }, getText('resetPassword.changePassword'))),
                isSkinny ? (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Spacer, { variant: "spacing-04" }),
                    React__default.createElement(LinkRow, { centered: true },
                        React__default.createElement(LinkRegion, { regionId: "body" },
                            React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))) : null))));
};
function SetPasswordForm({ setPasswordStateConfirmed, setPasswordStateErrored, passwordToken, onSignInResult, onForgotPasswordResult, verificationMethod, email, securityQuestionsAnswers, }) {
    const { flagEnableSecurityQuestionJourney } = useFeatureFlags();
    const noEmailErrorScreen = (React__default.createElement(SetPasswordError, { email: "", error: new ValidationError('No email', 4110), onForgotPasswordResult: onForgotPasswordResult, onSignInResult: onSignInResult }));
    if (verificationMethod === VerificationMethod.TOKEN &&
        !flagEnableSecurityQuestionJourney) {
        return (React__default.createElement(SetPasswordIdentityRetriever, { setPasswordStateErrored: setPasswordStateErrored, passwordToken: passwordToken }, ({ userIdentity, isLoading }) => {
            if (isLoading)
                return React__default.createElement(LoadingScreen$1, null);
            if (userIdentity)
                return (React__default.createElement(SetPasswordFormInternal, { setPasswordStateConfirmed: setPasswordStateConfirmed, setPasswordStateErrored: setPasswordStateErrored, passwordToken: passwordToken, onSignInResult: onSignInResult, userIdentity: userIdentity, verificationMethod: VerificationMethod.TOKEN, securityQuestionsAnswers: securityQuestionsAnswers }));
            return noEmailErrorScreen;
        }));
    }
    if (flagEnableSecurityQuestionJourney && email) {
        return (React__default.createElement(SetPasswordFormInternal, { setPasswordStateConfirmed: setPasswordStateConfirmed, setPasswordStateErrored: setPasswordStateErrored, passwordToken: passwordToken, onSignInResult: onSignInResult, userIdentity: email, verificationMethod: VerificationMethod.TOKEN, securityQuestionsAnswers: securityQuestionsAnswers }));
    }
    if (verificationMethod === VerificationMethod.CODE && email) {
        return (React__default.createElement(SetPasswordFormInternal, { setPasswordStateConfirmed: setPasswordStateConfirmed, setPasswordStateErrored: setPasswordStateErrored, passwordToken: passwordToken, onSignInResult: onSignInResult, userIdentity: email, verificationMethod: VerificationMethod.CODE }));
    }
    return noEmailErrorScreen;
}

var SetPasswordStep;
(function (SetPasswordStep) {
    SetPasswordStep[SetPasswordStep["PasswordForm"] = 0] = "PasswordForm";
    SetPasswordStep[SetPasswordStep["PasswordConfirmation"] = 1] = "PasswordConfirmation";
    SetPasswordStep[SetPasswordStep["PasswordError"] = 2] = "PasswordError";
    SetPasswordStep[SetPasswordStep["AnswerSecurityQuestions"] = 3] = "AnswerSecurityQuestions";
})(SetPasswordStep || (SetPasswordStep = {}));
const initialState$1 = {
    error: null,
    step: SetPasswordStep.PasswordForm,
    passwordToken: '',
    email: '',
    securityQuestionsAnswers: [],
};
const useSetPassword = () => {
    const [state, setState] = useState(initialState$1);
    const setPasswordStateConfirmed = (email) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: SetPasswordStep.PasswordConfirmation, email })));
    };
    const setAnswerSecurityQuestionsState = () => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: SetPasswordStep.AnswerSecurityQuestions })));
    };
    const setPasswordFormState = () => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: SetPasswordStep.PasswordForm })));
    };
    const setEmailState = (email) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { email })));
    };
    const setSecurityQuestionsAnswersState = (securityQuestionsAnswers) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { securityQuestionsAnswers })));
    };
    function setPasswordStateErrored(error, email) {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { error, step: SetPasswordStep.PasswordError, email })));
    }
    return {
        state,
        setPasswordStateConfirmed,
        setPasswordStateErrored,
        setAnswerSecurityQuestionsState,
        setPasswordFormState,
        setEmailState,
        setSecurityQuestionsAnswersState,
    };
};

const SetPassword = ({ onSignInResult, onForgotPasswordResult, query, }) => {
    var _a, _b, _c;
    const { flagEnablePasswordResetByCode, flagEnableSecurityQuestionJourney } = useFeatureFlags();
    const { state, setPasswordStateConfirmed, setPasswordStateErrored, setAnswerSecurityQuestionsState, setPasswordFormState, setEmailState, setSecurityQuestionsAnswersState, } = useSetPassword();
    useEffect(() => {
        if (flagEnableSecurityQuestionJourney) {
            setAnswerSecurityQuestionsState();
        }
    }, []);
    const verificationMethod = flagEnablePasswordResetByCode
        ? VerificationMethod.CODE
        : VerificationMethod.TOKEN;
    if (query == null) {
        return React__default.createElement(LoadingScreen$1, null);
    }
    const emailValidationToken = (_a = query.emailValidationToken) === null || _a === void 0 ? void 0 : _a.toString();
    const verificationCode = (_b = query.verificationCode) === null || _b === void 0 ? void 0 : _b.toString();
    const email = (_c = query.email) === null || _c === void 0 ? void 0 : _c.toString();
    const passwordToken = verificationMethod === VerificationMethod.CODE
        ? verificationCode
        : emailValidationToken;
    const userIdentity = flagEnableSecurityQuestionJourney ? state.email : email;
    if (!passwordToken) {
        return (React__default.createElement(SetPasswordError, { email: "", error: new ValidationError('No token or code (unable to query for email)', 4110), onForgotPasswordResult: onForgotPasswordResult, onSignInResult: onSignInResult }));
    }
    if (state.step === SetPasswordStep.AnswerSecurityQuestions) {
        return (React__default.createElement(SecurityQuestionJourney, { emailValidationToken: passwordToken, setSecurityQuestionsAnswers: setSecurityQuestionsAnswersState, onSubmitSecurityQuestionResult: setPasswordFormState, onGetTokenDetails: setEmailState, onSignInResult: onSignInResult }));
    }
    if (state.step === SetPasswordStep.PasswordError) {
        return (React__default.createElement(SetPasswordError, { email: state.email, error: state.error, onForgotPasswordResult: onForgotPasswordResult, onSignInResult: onSignInResult }));
    }
    if (state.step === SetPasswordStep.PasswordConfirmation) {
        return React__default.createElement(SetPasswordConfirmation, { onSignInResult: onSignInResult });
    }
    return (React__default.createElement(SetPasswordForm, { setPasswordStateConfirmed: setPasswordStateConfirmed, setPasswordStateErrored: setPasswordStateErrored, passwordToken: passwordToken, verificationMethod: verificationMethod, onSignInResult: onSignInResult, onForgotPasswordResult: onForgotPasswordResult, email: userIdentity, securityQuestionsAnswers: state.securityQuestionsAnswers }));
};

const loginWithSingleSignon = ([{ accessToken, captchaToken, redirectURL, universalLoginBrand },]) => __awaiter(void 0, void 0, void 0, function* () {
    const reqBody = {
        accessToken,
        captchaToken,
    };
    return httpClient.post(`/v1/user/ula/login/oauth2`, reqBody, {
        redirectURL,
    }, true, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

const LoadingContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;
const SpinnerContainer = styled.div `
  flex: 1 1 auto;
  width: 32px;
  height: 32px;
`;
const TextContainer = styled.div `
  flex: 1 1 auto;
`;
const LoadingScreen = () => {
    const { LoadingSpinner, Spacer, Text } = useComponentLibrary();
    const { getText } = useContent();
    return (React__default.createElement(LoadingContainer, null,
        React__default.createElement(SpinnerContainer, null,
            React__default.createElement(LoadingSpinner, { size: 32 })),
        React__default.createElement(TextContainer, null,
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { component: "span", variant: "bodySMedium" }, getText('authenticatingTo')))));
};

const APP_SSO_COOKIE_NAME = {
    spark: 'sparkapp_token',
    skinny: 'skinnyapp_token',
};
const getAppCookieName = (brand) => {
    if (brand === 'Skinny') {
        return APP_SSO_COOKIE_NAME.skinny;
    }
    return APP_SSO_COOKIE_NAME.spark;
};

const ERROR_MESSAGES_MAP$1 = {
    4100: { message: 'Invalid/malformed redirectUrl.' },
    4101: {
        message: 'Redirect URL domain is invalid or domain not configured properly.',
    },
    4109: { message: 'Session ID returned by /authenticate is invalid.' },
    4110: {
        message: 'Client authentication failed with error code invalid_client from OpenAm according to RFC6749 section 5.2.',
    },
    4113: { message: 'Invalid realm value.' },
    4121: { message: 'Identity not verified.' },
    4112: { message: 'CAPTCHA verification failed.' },
    9001: { message: 'Something went wrong' },
};

const AppSingleSignon = ({ goto = DEFAULT_REDIRECT_URL, }) => {
    const [hasRun, setHasRun] = useState(false);
    const { brand: universalLoginBrand } = useConfig();
    const { isLoading, mutate, error } = useMutation({
        mutationFn: loginWithSingleSignon,
        onSuccess: () => {
            if (goto) {
                window.location.assign(goto);
            }
        },
    });
    const { initiateCaptcha, hCaptchaProps } = useHCaptcha((captchaToken) => {
        const cookieName = getAppCookieName(universalLoginBrand);
        const accessToken = cookies.get(cookieName);
        if (!accessToken) {
            Sentry.captureException(new Error('Access token not found in cookies'), {
                extra: { cookieName, accessToken },
            });
        }
        mutate([
            {
                captchaToken,
                accessToken: accessToken !== null && accessToken !== void 0 ? accessToken : '',
                redirectURL: goto,
                universalLoginBrand,
            },
        ]);
    });
    useEffect(() => {
        if (hCaptchaProps.isHCaptchaLoaded || !hasRun) {
            initiateCaptcha();
            setHasRun(true);
        }
    }, [hCaptchaProps.isHCaptchaLoaded]);
    return (React__default.createElement(React__default.Fragment, null,
        isLoading ? React__default.createElement(LoadingScreen, null) : null,
        error ? (React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP$1 })) : null,
        React__default.createElement(HCaptchaElement, Object.assign({}, hCaptchaProps))));
};
var AppSingleSignon$1 = withPageBrandGuard(Flags.flagEnableAppSSOJourney, AppSingleSignon, () => React__default.createElement(PageNotAvailableMessage, { pageName: "Single Sign-On" }));

const handleForgotUserId = ([email, universalLoginBrand]) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.put(`/v1/user/Identity/${email}/action/MySparkBusinessForgottenUserId`, {}, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

const trackForgotUserIdConfirmationComponent = ({ brand, isSuccess, message, }) => {
    const componentViewEventProps = {
        brand,
        pageName: 'forgotUserId.componentView',
        subCategory: 'forgotUserId',
        componentId: 'forgotUserId.recoverUserIdEmailLink',
        componentName: 'Forgot User ID recover email Link',
        attributes: {
            isSuccess,
            message,
        },
    };
    componentViewEvent(componentViewEventProps);
};

const CONFIRMATION_SENT_SUCCESSFUL_MESSAGE = 'An email containing your user ID has been sent again.';
const ERROR_MESSAGE = (brand) => {
    return brand === 'MySB'
        ? 'There was an error with sending the user ID information email. Please try again later.'
        : 'There was an error sending the reset link.';
};
const ForgotUserIdConfirmation = ({ email, onSignInResult, }) => {
    const { Text, TextButton, Button, Spacer } = useComponentLibrary();
    const { getText } = useContent();
    const { brand: universalLoginBrand } = useConfig();
    const { InfoBanner, setInfoBannerState } = useInfoBanner();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotUserId.recoverUserIdSuccessState',
        subCategories: ['forgotUserId'],
    }));
    const { mutate, isLoading } = useMutation({
        mutationFn: handleForgotUserId,
        onSuccess: () => {
            setInfoBannerState({
                variant: 'SUCCESS',
                text: CONFIRMATION_SENT_SUCCESSFUL_MESSAGE,
            });
            trackForgotUserIdConfirmationComponent({
                brand: universalLoginBrand,
                isSuccess: true,
                message: CONFIRMATION_SENT_SUCCESSFUL_MESSAGE,
            });
        },
        onError: () => {
            setInfoBannerState({
                variant: 'ERROR',
                text: ERROR_MESSAGE(universalLoginBrand),
            });
            trackForgotUserIdConfirmationComponent({
                brand: universalLoginBrand,
                isSuccess: false,
                message: ERROR_MESSAGE(universalLoginBrand),
            });
        },
    });
    return (React__default.createElement(LinkRegion, { regionId: "Recover user ID" },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('forgotUserId.title')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('forgotUserId.confirmationDetails')),
            React__default.createElement(InfoBanner, { spacer: "preceding" }),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(FormRow, null,
                React__default.createElement(Button, { isLoading: isLoading, disabled: isLoading, onClick: () => mutate([email, universalLoginBrand]), variant: "primary" }, getText('forgotUserId.resendEmail'))),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(LinkRow, { centered: true },
                React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn'))))));
};

const ContactAdmin = ({ onSignInResult }) => {
    const { Text, TextButton, Spacer } = useComponentLibrary();
    const { brand: universalLoginBrand } = useConfig();
    const { getText } = useContent();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotUserId.recoverEmailAddress',
        subCategories: ['forgotUserId'],
    }));
    return (React__default.createElement(LinkRegion, { regionId: "Recover email address" },
        React__default.createElement(Margin, null,
            React__default.createElement(Text, { variant: "subheadingL" }, getText('forgotUserId.contactAdminTitle')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(Text, { variant: "bodySDemi" }, getText('forgotUserId.contactAdminMessage')),
            React__default.createElement(Spacer, { variant: "spacing-03" }),
            React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))));
};

const ERROR_MESSAGES_MAP = () => {
    return {
        4024: {
            message: 'There was an error with sending the user ID information email. Please try again later.',
        },
    };
};
const EmailForm = ({ onSignInResult, onSendResult, onForgotEmailResult, }) => {
    const { Text, TextButton, Button, Spacer } = useComponentLibrary();
    const { brand: universalLoginBrand } = useConfig();
    const { getText } = useContent();
    useTrackPageView(getTrackPageProps({
        brand: universalLoginBrand,
        pageName: 'forgotUserId.enterEmailState',
        subCategories: ['forgotUserId'],
    }));
    const { control, formState: { errors }, handleSubmit, watch, } = useForm({
        defaultValues: {
            email: '',
        },
        mode: 'onSubmit',
    });
    const { email } = watch();
    const { mutate, error, isLoading } = useMutation({
        mutationFn: handleForgotUserId,
        onSuccess: () => onSendResult(email),
    });
    function submitHandler(formData) {
        mutate([formData.email, universalLoginBrand]);
    }
    return (React__default.createElement(LinkRegion, { regionId: "Recover user ID" },
        React__default.createElement("form", { onSubmit: handleSubmit(submitHandler) },
            React__default.createElement(Margin, null,
                React__default.createElement(Text, { variant: "subheadingL" }, getText('forgotUserId.title')),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(Text, { variant: "bodySDemi" }, getText('forgotUserId.description')),
                error ? (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Spacer, { variant: "spacing-02" }),
                    React__default.createElement(ErrorMessage, { error: error, errorMap: ERROR_MESSAGES_MAP() }))) : null,
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(LinkRegion, { isNotTracked: true },
                    React__default.createElement(Controller, { control: control, name: "email", rules: {
                            required: true,
                            validate: isEmailValid,
                        }, render: ({ field }) => (React__default.createElement(EmailInput, { disabled: isLoading, error: errors.email, name: field.name, onChange: field.onChange, value: field.value, inputType: "text", placeholder: "Enter your email", label: getText('emailLabel') })) })),
                React__default.createElement(Spacer, { variant: "spacing-01" }),
                React__default.createElement(TextButton, { onClick: onForgotEmailResult }, getText('forgotUserId.forgotYourEmail')),
                React__default.createElement(Spacer, { variant: "spacing-04" }),
                React__default.createElement(FormRow, null,
                    React__default.createElement(Button, { isLoading: isLoading, disabled: !email || isLoading, type: "submit", variant: "primary" }, getText('forgotUserId.send'))),
                React__default.createElement(Spacer, { variant: "spacing-03" }),
                React__default.createElement(LinkRow, { centered: true },
                    React__default.createElement(TextButton, { onClick: onSignInResult }, getText('returnToSignIn')))))));
};

var ForgotUserIdStep;
(function (ForgotUserIdStep) {
    ForgotUserIdStep[ForgotUserIdStep["EnterEmail"] = 0] = "EnterEmail";
    ForgotUserIdStep[ForgotUserIdStep["ConfirmationMessage"] = 1] = "ConfirmationMessage";
    ForgotUserIdStep[ForgotUserIdStep["ContactAdminMessage"] = 2] = "ContactAdminMessage";
    ForgotUserIdStep[ForgotUserIdStep["DisplayError"] = 3] = "DisplayError";
})(ForgotUserIdStep || (ForgotUserIdStep = {}));
const initialState = {
    step: ForgotUserIdStep.EnterEmail,
    email: '',
};
const useForgotUserId = () => {
    const [state, setState] = useState(initialState);
    const confirmEmail = (email) => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: ForgotUserIdStep.ConfirmationMessage, email })));
    };
    const contactAdmin = () => {
        setState((prevState) => (Object.assign(Object.assign({}, prevState), { step: ForgotUserIdStep.ContactAdminMessage })));
    };
    return {
        state,
        confirmEmail,
        contactAdmin,
    };
};

const ForgotUserId = ({ onSignInResult }) => {
    const { state, confirmEmail, contactAdmin } = useForgotUserId();
    if (state.step === ForgotUserIdStep.ConfirmationMessage) {
        return React__default.createElement(ForgotUserIdConfirmation, { onSignInResult: onSignInResult, email: state.email });
    }
    if (state.step === ForgotUserIdStep.ContactAdminMessage) {
        return React__default.createElement(ContactAdmin, { onSignInResult: onSignInResult });
    }
    return (React__default.createElement(EmailForm, { onSignInResult: onSignInResult, onSendResult: (email) => confirmEmail(email), onForgotEmailResult: contactAdmin }));
};
var ForgotUserId$1 = withPageBrandGuard(Flags.flagEnableForgotUserIdJourney, ForgotUserId, () => React__default.createElement(PageNotAvailableMessage, { pageName: "User ID recovery" }));

class ErrorBoundary extends React__default.Component {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
        };
    }
    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI.
        return { error };
    }
    componentDidCatch(error, errorInfo) {
        // displays the actual error in the console, to get a clear fail fast feedback
        // eslint-disable-next-line no-console
        console.error('ErrorBoundary did catch error: ', error, errorInfo);
        const { onError } = this.props;
        Sentry.captureException(error);
        if (onError) {
            onError(error);
        }
    }
    render() {
        const { error } = this.state;
        const { fallback: Fallback, children, showFallback = true } = this.props;
        if (error && showFallback) {
            return React__default.createElement(Fallback, { error: error });
        }
        return React__default.createElement(React__default.Fragment, null, children);
    }
}

const DEFAULT_ERROR_MESSAGE = 'This page is currently unavailable. Please try again later. Contact us if the problem persists.';
const Container = styled.div `
  width: 100%;
  height: 100%;
  text-align: center;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => spacing.s4};
`;
const GenericErrorScreen = ({ message = DEFAULT_ERROR_MESSAGE, }) => {
    const { Text, Spacer } = useComponentLibrary();
    return (React__default.createElement(Container, null,
        React__default.createElement(Text, { variant: "subheadingL" }, "Sorry, something went wrong"),
        React__default.createElement(Spacer, { variant: "spacing-03" }),
        React__default.createElement(Text, { variant: "bodySMedium" }, message)));
};

// We need a version of an error fallback that doesn't rely on our components as we want it to catch errors at the highest level of _app.tsx (above the component library provider)
const FullWidthBox = styled.div `
  width: 100%;
  height: 100%;
  text-align: center;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  font-family: sans-serif;
`;
const TextLine1 = styled.p `
  font-size: 24px;
  line-height: 1.5;
  margin-bottom: 15px;
`;
const TextLine2 = styled.p `
  font-size: 16px;
  line-height: 1.5;
`;
const TopLevelErrorScreen = () => {
    return (React__default.createElement(FullWidthBox, null,
        React__default.createElement(TextLine1, null, "Sorry, something went wrong"),
        React__default.createElement(TextLine2, null, "This page is currently unavailable. Please try again later. Contact us if the problem persists.")));
};

// https://tanstack.com/query/v4/docs/react/guides/window-focus-refetching
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});
function UniversalLoginProvider({ children, componentLibrary, config, launchDarklyFlags, }) {
    const flagsByBrand = featureFlagsByBrand[config.brand] || {};
    const loginProviderComponent = (React__default.createElement(ComponentLibraryProvider, { componentLibrary: componentLibrary },
        React__default.createElement(ErrorBoundary, { fallback: () => React__default.createElement(GenericErrorScreen, null) },
            React__default.createElement(QueryClientProvider, { client: queryClient },
                React__default.createElement(AuthorableContentsProvider, null, children)))));
    return (React__default.createElement(ErrorBoundary, { fallback: () => React__default.createElement(TopLevelErrorScreen, null) },
        React__default.createElement(FeatureFlagsProvider, { flags: flagsByBrand },
            React__default.createElement(ConfigProvider, Object.assign({ launchDarklyFlags: launchDarklyFlags }, config), loginProviderComponent))));
}

const requestGuestLogin = ({ captchaToken, redirectURL, universalLoginBrand = 'Spark', }) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.post('/v1/user/ula/login/guest', {
        captchaToken,
    }, { redirectURL }, true, 'withoutRealmAndBrandInHeaders', universalLoginBrand);
});

const requestLogout = (universalLoginBrand) => __awaiter(void 0, void 0, void 0, function* () {
    return httpClient.post('/v1/user/ula/logout', {}, {}, true, 'realmInHeaders', universalLoginBrand);
});

var LogoBrandFromUrl;
(function (LogoBrandFromUrl) {
    LogoBrandFromUrl["SkinnyJump"] = "SKINNYJUMP";
})(LogoBrandFromUrl || (LogoBrandFromUrl = {}));

var data$1 = {
	ulaContentModelList: {
		items: [
			{
				emailLabel: {
					plaintext: "Email"
				},
				userIdLabel: {
					plaintext: "User ID"
				},
				authenticatingTo: {
					plaintext: "Authenticating to My Spark Business"
				},
				firstName: {
					plaintext: ""
				},
				lastName: {
					plaintext: ""
				},
				returnToSignIn: {
					plaintext: "Return to sign in"
				},
				login_loginText: {
					plaintext: "Sign in "
				},
				login_toContinue: {
					plaintext: "to continue to your Business portal"
				},
				login_clickHere: {
					plaintext: null
				},
				login_buttonText: {
					plaintext: "Sign in"
				},
				login_register: {
					plaintext: ""
				},
				login_resetPasswordText: {
					plaintext: "Forgot your password?"
				},
				login_orSignInWith: {
					plaintext: null
				},
				login_noAccount: {
					plaintext: ""
				},
				login_needHelp: {
					plaintext: null
				},
				login_twoStepVerification: {
					plaintext: "Two-step verification"
				},
				login_toVerifyItsYou: {
					plaintext: "to verify it’s you"
				},
				login_continueVerify: {
					plaintext: "Continue"
				},
				login_resendVerificationCode: {
					plaintext: "Request a new code"
				},
				login_returnToSignInButtonText: {
					plaintext: "Return to sign in"
				},
				resetPassword_resetDescription: {
					plaintext: "Please enter your user ID. We'll send you a link to the associated email account to reset your password."
				},
				resetPassword_send: {
					plaintext: "Email reset link"
				},
				resetPassword_forgotYourEmail: {
					plaintext: "Forgot your User ID?"
				},
				resetPassword_sendResetLink: {
					plaintext: "Resend reset link"
				},
				resetPassword_resetPassword: {
					plaintext: "Reset password"
				},
				resetPassword_followTheDirections: {
					plaintext: "If you've entered a valid user ID, an email has been sent with instructions to reset your password."
				},
				resetPassword_enterNewPassword: {
					plaintext: "Please enter your new password below."
				},
				resetPassword_changePassword: {
					plaintext: "Set password"
				},
				resetPassword_enterPassword: {
					plaintext: "Set new password"
				},
				resetPassword_success: {
					plaintext: "Set new password"
				},
				resetPassword_successMessage: {
					plaintext: "Your new password has successfully been set."
				},
				resetPassword_newPassword: {
					plaintext: "New password"
				},
				resetPassword_verifyCodeTitle: {
					plaintext: "Enter code"
				},
				resetPassword_verifyCodeDescriptionFirstBlock: {
					plaintext: null
				},
				resetPassword_verifyCodeDescriptionSecondBlock: {
					plaintext: "Please enter it to continue."
				},
				resetPassword_verifyCodeVerifyButtonText: {
					plaintext: "Continue"
				},
				resetPassword_verifyCodeResendCodeButtonText: {
					plaintext: "Resend verification code"
				},
				resetPassword_reenterNewPassword: {
					plaintext: "Confirm new password"
				},
				resetPassword_passwordsDontMatch: {
					plaintext: "Passwords don’t match"
				},
				register_toManage: {
					plaintext: "to access and manage your account"
				},
				register_enterYourName: {
					plaintext: "Enter your name to personalise your SparkID."
				},
				register_nextStepText: {
					plaintext: "Next"
				},
				register_next: {
					plaintext: "Next"
				},
				register_enterCode: {
					plaintext: "Enter code"
				},
				register_toVerifyItsYou: {
					plaintext: "to verify it’s you"
				},
				register_verify: {
					plaintext: "Verify"
				},
				register_haveNotReceivedACode: {
					plaintext: "Haven't received a code?"
				},
				recoverEmail_enterYourName: {
					plaintext: "Enter your name and we’ll recover the email associated with your account."
				},
				recoverEmail_continue: {
					plaintext: "Continue"
				},
				recoverEmail_selectYourEmail: {
					plaintext: "Select your email address below and we'll send a reminder shortly."
				},
				recoverEmail_submit: {
					plaintext: "Submit"
				},
				mfa_viewCode: {
					plaintext: "Open your authenticator app to view the six digit code."
				},
				forgotUserId_title: {
					plaintext: "Recover user ID"
				},
				forgotUserId_description: {
					plaintext: " Please enter your email and we'll send you your user ID information."
				},
				forgotUserId_forgotYourEmail: {
					plaintext: " Forgot your email?"
				},
				forgotUserId_send: {
					plaintext: "Send email"
				},
				forgotUserId_confirmationDetails: {
					plaintext: "If your email address is associated with an account, you will receive your user ID information. Please check your spam folder as well."
				},
				forgotUserId_contactAdminTitle: {
					plaintext: "Recover email address"
				},
				forgotUserId_contactAdminMessage: {
					plaintext: "Please contact your business administrator to recover your email address."
				},
				forgotUserId_resendEmail: {
					plaintext: "Resend email"
				},
				mfa_codeSentTo: {
					plaintext: "Enter the six-digit verification code we sent to "
				},
				mfa_optionsBody: {
					plaintext: "A second step is required to complete sign in. Please choose where to send your six-digit verification code."
				},
				mfa_optionsSubheader: {
					plaintext: "Two-step verification"
				}
			}
		]
	}
};
var mysbAuthorableContentsMockData = {
	data: data$1
};

var data = {
	ulaContentModelList: {
		items: [
			{
				authenticatingTo: {
					plaintext: "Authenticating to Xtramail"
				},
				emailLabel: {
					plaintext: "Email"
				},
				firstName: {
					plaintext: "First name"
				},
				lastName: {
					plaintext: "Last name"
				},
				login_buttonText: {
					plaintext: "Sign in"
				},
				login_clickHere: {
					plaintext: null
				},
				login_continueVerify: {
					plaintext: "Continue"
				},
				login_loginText: {
					plaintext: "Sign in "
				},
				login_needHelp: {
					plaintext: "Need help?"
				},
				login_noAccount: {
					plaintext: "Don’t have an account?"
				},
				login_orSignInWith: {
					plaintext: null
				},
				login_register: {
					plaintext: "Register"
				},
				login_resendVerificationCode: {
					plaintext: "Request a new code"
				},
				login_resetPasswordText: {
					plaintext: "Can't sign in?"
				},
				login_returnToSignInButtonText: {
					plaintext: "Return to sign in"
				},
				login_toContinue: {
					plaintext: "to continue"
				},
				login_toVerifyItsYou: {
					plaintext: null
				},
				login_twoStepVerification: {
					plaintext: "Enter code"
				},
				mfa_codeSentTo: {
					plaintext: "A six digit code has been sent to"
				},
				mfa_enterCodeBelow: {
					plaintext: "Please enter it below."
				},
				mfa_optionsBody: {
					plaintext: "How would you like to sign in using two-step verification?"
				},
				mfa_optionsSubheader: {
					plaintext: "Two-step verification"
				},
				mfa_viewCode: {
					plaintext: "Open your authenticator app to view the six digit code."
				},
				recoverEmail_continue: {
					plaintext: "Continue"
				},
				recoverEmail_enterYourName: {
					plaintext: "Enter your name and we’ll recover the email associated with your account."
				},
				recoverEmail_selectYourEmail: {
					plaintext: "Select your email address below and we'll send a reminder shortly."
				},
				recoverEmail_submit: {
					plaintext: "Submit"
				},
				register_enterCode: {
					plaintext: "Enter code"
				},
				register_enterYourName: {
					plaintext: "Enter your name to personalise your SparkID."
				},
				register_haveNotReceivedACode: {
					plaintext: "Haven't received a code?"
				},
				register_next: {
					plaintext: "Next"
				},
				register_nextStepText: {
					plaintext: "Next"
				},
				register_toManage: {
					plaintext: "to access and manage your account"
				},
				register_toVerifyItsYou: {
					plaintext: "to verify it’s you"
				},
				register_verify: {
					plaintext: "Verify"
				},
				resetPassword_changePassword: {
					plaintext: "Change password"
				},
				resetPassword_enterNewPassword: {
					plaintext: null
				},
				resetPassword_enterPassword: {
					plaintext: "Enter password"
				},
				resetPassword_followTheDirections: {
					plaintext: "If your email address is associated with a MySpark account, we'll email you instructions to reset your password."
				},
				resetPassword_forgotYourEmail: {
					plaintext: ""
				},
				resetPassword_newPassword: {
					plaintext: "New password"
				},
				resetPassword_resetDescription: {
					plaintext: "Please enter your Xtra Mail email address. We'll send a verification code to the recovery methods you've provided."
				},
				resetPassword_resetPassword: {
					plaintext: "Reset password"
				},
				resetPassword_send: {
					plaintext: "Send"
				},
				resetPassword_sendResetLink: {
					plaintext: "Resend reset link"
				},
				resetPassword_success: {
					plaintext: null
				},
				resetPassword_successMessage: {
					plaintext: "You've successfully changed your Xtra Mail password."
				},
				resetPassword_verifyCodeDescriptionFirstBlock: {
					plaintext: "If your email address is associated with an Xtra Mail account, we'll send a verification code to your recovery methods."
				},
				resetPassword_verifyCodeDescriptionSecondBlock: {
					plaintext: "Please enter it to continue."
				},
				resetPassword_verifyCodeResendCodeButtonText: {
					plaintext: "Request a new code"
				},
				resetPassword_verifyCodeTitle: {
					plaintext: "Enter code"
				},
				resetPassword_verifyCodeVerifyButtonText: {
					plaintext: "Continue"
				},
				returnToSignIn: {
					plaintext: "Return to sign in"
				}
			}
		]
	}
};
var xtraMailAuthorableContentsMockData = {
	data: data
};

const getAuthorableContentsMock = rest.get(`${authorableContentBaseUrl}/:modelId/:contentId/:format`, (req, res, ctx) => {
    switch (req.params.modelId) {
        case 'ula-mysb':
            return res(ctx.status(200), ctx.json(mysbAuthorableContentsMockData));
        case 'ula-xtramail':
            return res(ctx.status(200), ctx.json(xtraMailAuthorableContentsMockData));
        default:
            return res(ctx.status(200), ctx.json(xtraMailAuthorableContentsMockData));
    }
});
const handlers$b = [getAuthorableContentsMock];

function makeResponseMessage(code, message) {
    return {
        messages: [
            {
                message,
                code,
            },
        ],
    };
}
const recoverEmailGetRegex = /\/v1\/user\/identity\/(.*)\/action\/sparkForgottenUserId/;
const handlers$a = [
    rest.get('/v1/user/identity', (req, res, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (((_a = req.url.searchParams.get('firstName')) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'tester' &&
            ((_b = req.url.searchParams.get('lastName')) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'test') {
            return res(ctx.status(200), ctx.json(Object.assign(Object.assign({}, makeResponseMessage(2000, "We've emailed you. Please check your email to retrieve your account details.")), { identities: [
                    {
                        email: 'ha******el@someemail.com',
                        rowId: 0,
                    },
                    {
                        email: 'monk***an@another.com',
                        rowId: 1,
                    },
                    {
                        email: 'the******ves@forever.com',
                        rowId: 2,
                    },
                ] })));
        }
        return res(ctx.status(404), ctx.json(Object.assign(Object.assign({}, makeResponseMessage(4023, "Sorry, we couldn't find a Spark ID associated with the details provided.")), { identities: [
                {
                    email: '',
                    rowId: 0,
                },
                {
                    email: '',
                    rowId: 1,
                },
                {
                    email: '',
                    rowId: 2,
                },
            ] })));
    })),
    rest.put(recoverEmailGetRegex, (req, res, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        var _c;
        const firstName = req.url.searchParams.get('firstName');
        const lastName = req.url.searchParams.get('lastName');
        const rowId = req.url.searchParams.get('rowId');
        const email = (_c = recoverEmailGetRegex.exec(req.url.pathname)) === null || _c === void 0 ? void 0 : _c[1];
        if (!firstName || !lastName || !rowId) {
            return res(ctx.status(401), ctx.json(makeResponseMessage(4002, 'One or more parameters are missing')));
        }
        if (email !== 'ha******el@someemail.com') {
            return res(ctx.json({
                messages: [
                    {
                        code: 4030,
                        message: 'Incorrect email',
                    },
                ],
            }));
        }
        return res(ctx.status(200), ctx.json(makeResponseMessage(2000, 'A confirmation email has been sent, please follow the link to reset your password')));
    })),
];

const forgotPassword2000 = {
    messages: [
        {
            message: 'A new verification code has been sent',
            code: 2001,
        },
    ],
};
const forgotPassword4002 = {
    messages: [
        {
            message: "The parameter is invalid and doesn't match one of the allowed values.",
            code: 4002,
        },
    ],
};

const forgotEmailRegex = /\/v1\/user\/identity\/(.*)\/action\/sparkForgottenPassword/;
const mySparkBusinessForgotEmailRegex = /\/v1\/user\/Identity\/(.*)\/action\/MySparkBusinessForgottenPassword/;
const handlers$9 = [
    rest.put(forgotEmailRegex, (req, res, ctx) => {
        const regexRes = forgotEmailRegex.exec(req.url.pathname);
        if (!regexRes || (regexRes === null || regexRes === void 0 ? void 0 : regexRes.length) === 0) {
            return res(ctx.status(400), ctx.json(forgotPassword4002));
        }
        const email = regexRes[1];
        // is spark id - success response
        if (email === 'bob.builder@spark.co.nz') {
            return res(ctx.status(200), ctx.json(forgotPassword2000));
        }
        // is social id - fail response
        if (email === 'facebook.user@email.com') {
            return res(ctx.status(403), ctx.json({
                messages: [
                    {
                        message: "This is a social identity and doesn't support password recovery.",
                        code: 4040,
                    },
                ],
                socialProvider: 'Facebook',
            }));
        }
        return res(ctx.status(404), ctx.json({
            messages: [
                {
                    code: 4023,
                    message: "Sorry, we couldn't find a Spark ID associated with this email address.",
                },
            ],
        }));
    }),
    rest.put(mySparkBusinessForgotEmailRegex, (req, res, ctx) => {
        const regexRes = mySparkBusinessForgotEmailRegex.exec(req.url.pathname);
        if (!regexRes || (regexRes === null || regexRes === void 0 ? void 0 : regexRes.length) === 0) {
            return res(ctx.status(400), ctx.json(forgotPassword4002));
        }
        const userId = regexRes[1];
        if (userId === 'mysb.user@spark.co.nz') {
            return res(ctx.status(200), ctx.json({
                messages: [
                    {
                        message: 'A reset link has been sent',
                        code: 2000,
                    },
                ],
            }));
        }
        if (userId === 't999999@spark') {
            return res(ctx.status(404), ctx.json({
                messages: [
                    {
                        code: 4022,
                        message: 'Please use the LAN process to retrieve your password.',
                    },
                ],
            }));
        }
        if (userId === 'locked.user@spark.co.nz') {
            return res(ctx.status(404), ctx.json({
                messages: [
                    {
                        code: 4024,
                        message: 'There was an error with your request. Please contact your administrator.',
                    },
                ],
            }));
        }
        return res(ctx.status(404), ctx.json({
            messages: [
                {
                    code: 4023,
                    message: 'Failed to send the reset link.',
                },
            ],
        }));
    }),
];

const forgotPasswordVerifyCode2000 = {
    messages: [
        {
            message: 'Success',
            code: 2000,
        },
    ],
    token: 'abcdefgh12345',
};
const forgotPasswordVerifyCode4111 = {
    messages: [
        {
            message: 'Code not found',
            code: 4111,
        },
    ],
};
const forgotPasswordVerifyCode4200 = {
    messages: [
        {
            message: 'Too many recent requests to validate that primary key.',
            code: 4200,
        },
    ],
};
const forgotPasswordVerifyCode4112 = {
    messages: [
        {
            message: 'Code is expired',
            code: 4112,
        },
    ],
};
const forgotPasswordVerifyCode9001 = {
    messages: [
        {
            message: 'Various failure scenarios (caused by failures in downstream systems)',
            code: 9001,
        },
    ],
};

const codeVerificationRegex = /\/v1\/utility\/verification\/XtramailForgottenPassword\/(?<email>[^/]+)\/(?<code>[^/]+)/;
const handlers$8 = [
    rest.get(codeVerificationRegex, (req, res, ctx) => {
        var _a;
        const { code } = ((_a = codeVerificationRegex.exec(req.url.pathname)) === null || _a === void 0 ? void 0 : _a.groups) || {};
        // success response
        if (code === '111111') {
            return res(ctx.status(200), ctx.json(forgotPasswordVerifyCode2000));
        }
        // fail response expired
        if (code === '000000') {
            return res(ctx.status(400), ctx.json(forgotPasswordVerifyCode4112));
        }
        // fail response too many retries
        if (code === '555555') {
            return res(ctx.status(400), ctx.json(forgotPasswordVerifyCode4200));
        }
        // generic server error
        if (code === '999999') {
            return res(ctx.status(500), ctx.json(forgotPasswordVerifyCode9001));
        }
        // fail response invalid
        return res(ctx.status(400), ctx.json(forgotPasswordVerifyCode4111));
    }),
];

const forgotUserId4002 = {
    messages: [
        {
            message: "The parameter is invalid and doesn't match one of the allowed values.",
            code: 4002,
        },
    ],
};

const forgotUserIdRegex = /\/v1\/user\/Identity\/(.*)\/action\/MySparkBusinessForgottenUserId/;
const handlers$7 = [
    rest.put(forgotUserIdRegex, (req, res, ctx) => {
        const regexRes = forgotUserIdRegex.exec(req.url.pathname);
        if (!regexRes || (regexRes === null || regexRes === void 0 ? void 0 : regexRes.length) === 0) {
            return res(ctx.status(400), ctx.json(forgotUserId4002));
        }
        const email = regexRes[1];
        if (email === 'mysb.user@spark.co.nz') {
            return res(ctx.status(200), ctx.json({
                messages: [
                    {
                        message: 'Success',
                        code: 2000,
                    },
                ],
            }));
        }
        return res(ctx.status(404), ctx.json({
            messages: [
                {
                    code: 4024,
                    message: 'Failed to send the email with the user ID.',
                },
            ],
        }));
    }),
];

function makeLoginResponse({ code, message, mfaRequired, mfaOptions, stage, nonce, amlb, }) {
    return {
        messages: [
            {
                message,
                code,
            },
        ],
        mfaRequired,
        mfaOptions,
        nonce,
        stage,
        amlb,
    };
}
const loginResendCodeHandler = (response) => rest.get('/v1/user/oidc/authenticate/mfa/renew', (_, res, ctx) => {
    return res(ctx.status(403), ctx.json(response));
});
const handlers$6 = [
    rest.post('/v1/user/ula/login', (req, res, ctx) => {
        const VALID_USERNAMES = [
            'mfa_test@spark.co.nz',
            'nomfa_test@spark.co.nz',
            'suspendedxtramail@email.com',
            'securitysuspendedxtramail@email.com',
            'xtramailmfa@example.com',
            'mysb.user@spark.co.nz',
            'mysb.user.mfa.single.method@spark.co.nz',
            'mysb.user.mfa.multi.methods@spark.co.nz',
        ];
        if (req.body.userName === 'error@spark.co.nz') {
            return res(ctx.status(500), ctx.set('X-Global-Transaction-ID', `${Date.now()}`), ctx.json(makeLoginResponse({ code: 9001, message: 'Something went wrong' })));
        }
        if (!VALID_USERNAMES.includes(req.body.userName) ||
            req.body.password !== 'Password1!') {
            return res(ctx.status(401), ctx.json(makeLoginResponse({
                code: 4105,
                message: 'Incorrect email address or password. Please try again.',
            })));
        }
        if (!req.body.captchaToken) {
            return res(ctx.status(400), ctx.json(makeLoginResponse({
                code: 4112,
                message: 'Captcha token invalid/expired.',
            })));
        }
        if (req.body.userName === 'mysb.user@spark.co.nz') {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'login successful.',
                mfaRequired: false,
            })));
        }
        if (req.body.userName === 'nomfa_test@spark.co.nz') {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'Non-MFA login successful.',
                mfaRequired: false,
            })));
        }
        if (req.body.userName === 'suspendedxtramail@email.com') {
            return res(ctx.status(403), ctx.json(makeLoginResponse({
                code: 4126,
                message: 'Provided Xtra Mail account has been suspended.',
                mfaRequired: false,
            })));
        }
        if (req.body.userName === 'securitysuspendedxtramail@email.com') {
            return res(ctx.status(403), ctx.json(makeLoginResponse({
                code: 4127,
                message: 'Provided Xtra Mail account has been security suspended.',
                mfaRequired: false,
            })));
        }
        if (req.body.userName === 'xtramailmfa@example.com') {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'MFA login required',
                mfaRequired: true,
                mfaOptions: [
                    {
                        type: 'email',
                        value: 't8****4@s****.nz',
                    },
                    {
                        type: 'mobile',
                        value: '021****401',
                    },
                    {
                        type: 'app',
                        value: '',
                    },
                ],
                stage: 'HOTP2',
                nonce: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdXRoSW5kZXhWYWx1',
                amlb: '01',
            })));
        }
        if (req.body.userName === 'mysb.user.mfa.single.method@spark.co.nz') {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'MFA login required',
                mfaRequired: true,
                mfaOptions: [
                    {
                        type: 'email',
                        value: 't8****5@s****.nz',
                    },
                ],
                stage: 'HOTP2',
                nonce: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdXRoSW5kZXhWYWx1',
                amlb: '01',
            })));
        }
        if (req.body.userName === 'mysb.user.mfa.multi.methods@spark.co.nz') {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'MFA login required',
                mfaRequired: true,
                mfaOptions: [
                    {
                        type: 'email',
                        value: 't8****5@s****.nz',
                    },
                    {
                        type: 'mobile',
                        value: '021****402',
                    },
                ],
                stage: 'HOTP2',
                nonce: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdXRoSW5kZXhWYWx1',
                amlb: '01',
            })));
        }
        return res(ctx.status(200), ctx.json(makeLoginResponse({
            code: 2000,
            message: 'MFA login successful.',
            mfaRequired: true,
            stage: 'HOTP2',
            nonce: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdXRoSW5kZXhWYWx1',
            amlb: '01',
        })));
    }),
    rest.post('/v1/user/ula/mfa/options', (req, res, ctx) => {
        if (!req.body.nonce)
            return res(ctx.status(403), ctx.json(makeLoginResponse({
                code: 4300,
                message: 'Nonce could not be found',
            })));
        return res(ctx.status(200), ctx.json(makeLoginResponse({
            code: 2000,
            message: 'Request successfully processed',
        })));
    }),
    rest.post('/v1/user/ula/mfa', (req, res, ctx) => {
        const VALID_MFA_VERIFY_CODES = ['111111', '11111111'];
        if (VALID_MFA_VERIFY_CODES.find((code) => req.body.mfaToken === code)) {
            return res(ctx.status(200), ctx.json(makeLoginResponse({
                code: 2000,
                message: 'MFA verified successfully',
            })));
        }
        if (req.body.mfaToken === '410800') {
            return res(ctx.status(400), ctx.json(makeLoginResponse({ code: 4108, message: 'Too many attempts' })));
        }
        if (req.body.mfaToken === '410700') {
            return res(ctx.status(400), ctx.json(makeLoginResponse({ code: 4107, message: 'Expired' })));
        }
        if (req.body.mfaToken === '410800') {
            return res(ctx.status(400), ctx.json(makeLoginResponse({
                code: 4108,
                message: 'Too many requests for nonce',
            })));
        }
        if (req.body.mfaToken === '412600') {
            return res(ctx.status(400), ctx.json(makeLoginResponse({
                code: 4126,
                message: 'Locked account',
            })));
        }
        if (req.body.mfaToken === '900100') {
            return res(ctx.status(400), ctx.json(makeLoginResponse({ code: 9100, message: 'Generic error' })));
        }
        return res(ctx.status(400), ctx.json(makeLoginResponse({ code: 4002, message: 'Invalid MFA code' })));
    }),
    loginResendCodeHandler(makeLoginResponse({
        code: 2000,
        message: 'A new MFA code has been sent',
        amlb: 'HOTP2',
        stage: '01',
        nonce: 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhdXRoSW5kZXhWYWx1ZSI6IlJlbWVtYmVyTWUiLCJvdGsiOiJvNWV2Nmc3aXMwNDlyZmN0Mjducjlyc2JvaCIsImF1dGhJbmRleFR5cGUiOiJzZXJ2aWNlIiwicmVhbG0iOiJvPW9ubGluZSxvdT1zZXJ2aWNlcyxkYz1vcGVuYW0sZGM9c3BhcmssZGM9Y28sZGM9bnoiLCJzZXNzaW9uSWQiOiJhN1VINzR0M0VfNk9BNWZ1ZzlhOEY4R01zWVUuKkFBSlRTUUFDTURJQUFsTkxBQnc0UnpONWNrd3ZSbGhQVTIxeU4wSTVhVVV5TXpCM1RsbFVXWGM5QUFKVE1RQUNNREUuKiJ9.NELZX8mELjfKS79hcyKLCaWVW514uh9Xx-G5xU334wI',
    })),
];

function makeLogoutResponse(code, message) {
    return {
        messages: [
            {
                message,
                code,
            },
        ],
    };
}
const handlers$5 = [
    rest.get('/v1/user/ula/logout', (_, res, ctx) => {
        return res(ctx.status(200), ctx.json(makeLogoutResponse(2000, 'Success')));
    }),
];

const registerUser2000 = {
    uuid: 'SSC1627838749393',
    messages: [
        {
            message: 'Identity created successfully.',
            code: 2000,
        },
    ],
};
const registerUser4002 = {
    uuid: 'SSC1627838749393',
    messages: [
        {
            message: 'Invalid verification type',
            code: 4002,
        },
    ],
};
const registerUser4100 = {
    uuid: 'SSC1627838749393',
    messages: [
        {
            message: 'The email address has already been used for a Spark Identity.',
            code: 4100,
        },
    ],
};

const registerValidate2000 = {
    available: true,
    messages: [
        {
            message: 'Email address is available',
            code: 2000,
        },
    ],
};
const registerValidate2001 = {
    available: false,
    messages: [
        {
            message: 'The selected email is already in use.',
            code: 2001,
        },
    ],
};

const verifyUser2000 = {
    messages: [
        {
            message: 'The Spark Identity was successfully verified.',
            code: 2000,
        },
    ],
};
const verifyUser2001 = {
    messages: [
        {
            message: 'A new verification code was generated and emailed.',
            code: 2001,
        },
    ],
};
const verifyUser4020 = {
    messages: [
        {
            message: 'Both a token and code parameter have been supplied',
            code: 4020,
        },
    ],
};
const verifyUser4025 = {
    messages: [
        {
            message: 'Invalid method of verification',
            code: 4025,
        },
    ],
};
const verifyUser4026 = {
    messages: [
        {
            message: 'No verification method specified',
            code: 4026,
        },
    ],
};
const verifyUser4027 = {
    messages: [
        {
            message: 'Verified was true, but no code is provided',
            code: 4027,
        },
    ],
};
const verifyUser4028 = {
    messages: [
        {
            message: 'Invalid code.',
            code: 4028,
        },
    ],
};

const registerGetRegex = /\/v1\/user\/identity\/availability\/(.*)/;
const registerPatchRegex = /\/v1\/user\/identity\/(.*)\/verified/;
const handlers$4 = [
    rest.get(registerGetRegex, (req, res, ctx) => {
        var _a;
        const email = (_a = registerGetRegex.exec(req.url.pathname)) === null || _a === void 0 ? void 0 : _a[1];
        if (email === 'bob.builder@spark.co.nz') {
            return res(ctx.status(200), ctx.json(registerValidate2001));
        }
        return res(ctx.status(200), ctx.json(registerValidate2000));
    }),
    rest.post('/v1/user/identity', (req, res, ctx) => {
        if (req.body.email === 'bob.builder@spark.co.nz') {
            return res(ctx.status(400), ctx.json(registerUser4100));
        }
        if (req.body.verificationType !== 'Code' &&
            req.body.verificationType !== 'Token') {
            return res(ctx.status(400), ctx.json(registerUser4002));
        }
        if (!req.body.email ||
            !req.body.password ||
            !req.body.firstName ||
            !req.body.lastName) {
            return res(ctx.status(400), ctx.json(registerUser4002));
        }
        return res(ctx.status(200), ctx.json(registerUser2000));
    }),
    rest.patch(registerPatchRegex, (req, res, ctx) => {
        const verifyBy = req.url.searchParams.get('verifyBy');
        if (verifyBy !== 'Token' && verifyBy !== 'Code') {
            return res(ctx.status(400), ctx.json(verifyUser4025));
        }
        if (!verifyBy) {
            return res(ctx.status(400), ctx.json(verifyUser4026));
        }
        const VERIFY_CODE = '111111';
        const codeParam = req.url.searchParams.get('code') || '';
        const tokenParam = req.url.searchParams.get('token') || '';
        if (codeParam && tokenParam) {
            return res(ctx.status(400), ctx.json(verifyUser4020));
        }
        if (!codeParam && req.body.verified) {
            return res(ctx.status(400), ctx.json(verifyUser4027));
        }
        if (req.body.verified && codeParam !== VERIFY_CODE) {
            return res(ctx.status(403), ctx.json(verifyUser4028));
        }
        if (!req.body.verified) {
            return res(ctx.status(200), ctx.json(verifyUser2001));
        }
        return res(ctx.status(200), ctx.json(verifyUser2000));
    }),
];

const makeSecurityQuestionsResponse = (code, message) => ({
    messages: [
        {
            message,
            code,
        },
    ],
});
const makeSecurityQuestionsSuccessResponse = (validated, expired, journeyType) => {
    const securityQuestions = journeyType === 'ForgotPassword'
        ? [
            {
                key: 'id4',
                value: "What's your favorite color?",
            },
            {
                key: 'id5',
                value: "What's your favorite animal?",
            },
        ]
        : [
            {
                key: 'id1',
                value: "What's your mother’s maiden name?",
            },
            {
                key: 'id2',
                value: "What's the name of your first pet?",
            },
            {
                key: 'id3',
                value: "What's the name of the street you grew up on?",
            },
            {
                key: 'id4',
                value: "What's your favorite color?",
            },
            {
                key: 'id5',
                value: "What's your favorite animal?",
            },
        ];
    return {
        messages: [
            {
                message: 'Success',
                code: 2000,
            },
        ],
        tokenData: {
            actor: 'SSC1616123332505498',
            primaryKey: 'SSC1616123332505498',
            type: 'Token',
            validated,
            expired,
            data: [
                {
                    key: 'userId',
                    value: 'mysb@spark.co.nz',
                },
                {
                    key: 'journeyType',
                    value: journeyType,
                },
            ],
            securityQuestions,
        },
    };
};
const getIdentityAndQuestionsRegex = /\/v1\/user\/Verification\/MySparkBusinessForgottenPassword\/(.*)/;
const postQuestionsAndAnswersRegex = /\/v1\/user\/Verification\/MySparkBusinessForgottenPasswordAnswers\/(.*)/;
const handlers$3 = [
    rest.get(getIdentityAndQuestionsRegex, (req, res, ctx) => {
        var _a;
        const token = (_a = getIdentityAndQuestionsRegex.exec(req.url.pathname)) === null || _a === void 0 ? void 0 : _a[1];
        if (token === 'expiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4111, 'Expired token')));
        }
        if (token === 'invalidtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4110, 'Invalid token')));
        }
        if (token === 'resetpasswordexpiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4112, 'Expired token')));
        }
        if (token === 'activateaccountexpiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4113, 'Expired token')));
        }
        if (token === 'unlockaccountexpiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4114, 'Expired token')));
        }
        if (token === 'enableaccountexpiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4115, 'Expired token')));
        }
        if (token === 'forgotpasswordvalidtoken' ||
            token === 'forgotpasswordinvalidtoken' ||
            token === 'forgotpasswordexpiredtoken') {
            return res(ctx.status(200), ctx.json(makeSecurityQuestionsSuccessResponse(true, false, 'ForgotPassword')));
        }
        if (token === 'enableuservalidtoken' ||
            token === 'enableuserinvalidtoken' ||
            token === 'enableuserexpiredtoken') {
            return res(ctx.status(200), ctx.json(makeSecurityQuestionsSuccessResponse(true, false, 'EnableUser')));
        }
        return res(ctx.status(500), ctx.json(makeSecurityQuestionsResponse(9001, 'Error')));
    }),
    rest.patch(postQuestionsAndAnswersRegex, (req, res, ctx) => {
        var _a, _b;
        const emailToken = (_a = postQuestionsAndAnswersRegex.exec(req.url.pathname)) === null || _a === void 0 ? void 0 : _a[1];
        if (emailToken === 'forgotpasswordinvalidtoken' ||
            emailToken === 'enableuserinvalidtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4110, 'Invalid token')));
        }
        if (emailToken === 'forgotpasswordexpiredtoken' ||
            emailToken === 'enableuserexpiredtoken') {
            return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4111, 'Expired token')));
        }
        if (emailToken === 'forgotpasswordvalidtoken') {
            const CORRECT_ANSWER = 'cat';
            const LOCKED_ACCOUNT_ANSWER = 'locked';
            const answer2 = (_b = req.body.find((answer) => answer.key === 'securityAnswer2')) === null || _b === void 0 ? void 0 : _b.value;
            if (answer2 === LOCKED_ACCOUNT_ANSWER) {
                return res(ctx.status(403), ctx.json(makeSecurityQuestionsResponse(4116, 'Locked Account')));
            }
            if (answer2 === CORRECT_ANSWER) {
                return res(ctx.status(200), ctx.json(makeSecurityQuestionsResponse(2000, 'Success')));
            }
            return res(ctx.status(200), ctx.json(makeSecurityQuestionsResponse(4001, 'Invalid answer')));
        }
        if (emailToken === 'enableuservalidtoken') {
            return res(ctx.status(200), ctx.json(makeSecurityQuestionsResponse(2000, 'Success')));
        }
        return res(ctx.status(500), ctx.json(makeSecurityQuestionsResponse(9001, 'Error')));
    }),
];

function makeSetPasswordResponse(code, message) {
    return {
        messages: [
            {
                message,
                code,
            },
        ],
    };
}
const mockedPasswordResponse = {
    messages: [
        {
            message: 'Success',
            code: 2000,
        },
    ],
    tokenData: {
        actor: 'SSC1616123332505498',
        primaryKey: 'SSC1616123332505498',
        type: 'Token',
        validated: false,
        expired: false,
        data: [
            {
                key: 'emailAddress',
                value: 't969965@spark.co.nz',
            },
        ],
    },
};
const mockedExpiredTokenLookupResponse = Object.assign(Object.assign({}, mockedPasswordResponse), { tokenData: Object.assign(Object.assign({}, mockedPasswordResponse.tokenData), { expired: true }) });
const getIdentityRegex = /\/v1\/utility\/verification\/SparkForgottenPassword\/(.*)/;
const patchPasswordRegex = /\/v1\/user\/identity\/(.*)\/password/;
const mySparkBusinessPatchPasswordRegex = /\/v1\/user\/Identity\/MySparkBusinessSetPassword\/(.*)/;
const handlers$2 = [
    rest.get(getIdentityRegex, (req, res, ctx) => {
        var _a;
        const token = (_a = getIdentityRegex.exec(req.url.pathname)) === null || _a === void 0 ? void 0 : _a[1];
        if (token === 'faillookuptoken') {
            return res(ctx.status(404), ctx.json(makeSetPasswordResponse(404, 'Token invalid')));
        }
        if (token === 'expiredlookuptoken') {
            return res(ctx.status(200), ctx.json(mockedExpiredTokenLookupResponse));
        }
        return res(ctx.status(200), ctx.json(mockedPasswordResponse));
    }),
    rest.patch(patchPasswordRegex, (req, res, ctx) => {
        const emailToken = req.url.searchParams.get('token');
        const code = req.url.searchParams.get('code');
        const passwordToken = emailToken || code;
        if (!passwordToken) {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4050, 'No token')));
        }
        if (passwordToken === 'validationfailuretoken') {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4020, 'Various validation failure token')));
        }
        if (passwordToken === 'invalidtoken') {
            return res(ctx.status(403), ctx.json(makeSetPasswordResponse(4028, 'Invalid token')));
        }
        if (passwordToken === 'toomanyattemptstoken') {
            return res(ctx.status(429), ctx.json(makeSetPasswordResponse(4031, 'Too many attempts token')));
        }
        if (passwordToken === 'expiredtoken') {
            return res(ctx.status(403), ctx.json(makeSetPasswordResponse(4022, 'expired token')));
        }
        if (passwordToken === 'noidentitytoken') {
            return res(ctx.status(404), ctx.json(makeSetPasswordResponse(4023, 'No identity for token')));
        }
        if (passwordToken === 'wrongemailtoken') {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4032, 'Incorrect email for token')));
        }
        if (passwordToken === 'previouslyusedpasswordtoken') {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4033, 'Previously used password')));
        }
        if (passwordToken === 'previouslytoosimilarpasswordtoken') {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4034, 'Previously too similar password')));
        }
        if (passwordToken === 'validtoken') {
            return res(ctx.status(200), ctx.json(makeSetPasswordResponse(2000, 'Success')));
        }
        return res(ctx.status(500), ctx.json(makeSetPasswordResponse(9001, 'Error')));
    }),
    rest.patch(mySparkBusinessPatchPasswordRegex, (req, res, ctx) => {
        var _a, _b, _c;
        const password = (_a = req.body.find((answer) => answer.key === 'password')) === null || _a === void 0 ? void 0 : _a.value;
        const securityAnswer1 = (_b = req.body.find((answer) => answer.key === 'securityAnswer1')) === null || _b === void 0 ? void 0 : _b.value;
        const securityAnswer2 = (_c = req.body.find((answer) => answer.key === 'securityAnswer2')) === null || _c === void 0 ? void 0 : _c.value;
        const validAnswers = securityAnswer1 === 'black' && securityAnswer2 === 'cat';
        if (password === 'Failpasswordtoken1!') {
            return res(ctx.status(403), ctx.json(makeSetPasswordResponse(4110, 'Invalid token')));
        }
        if (password === 'Expiredpasswordtoken1!') {
            return res(ctx.status(403), ctx.json(makeSetPasswordResponse(4111, 'Expired token')));
        }
        if (password === 'Password1!@' && !validAnswers) {
            return res(ctx.status(400), ctx.json(makeSetPasswordResponse(4001, 'Invalid answer')));
        }
        if (password === 'Password1!@' && validAnswers) {
            return res(ctx.status(200), ctx.json(makeSetPasswordResponse(2000, 'Success')));
        }
        return res(ctx.status(500), ctx.json(makeSetPasswordResponse(9001, 'Error')));
    }),
];

function makeSingleSignonResponse(code, message) {
    return {
        messages: [
            {
                code,
                message,
            },
        ],
    };
}
const handlers$1 = [
    rest.post('/v1/user/ula/login/oauth2', (req, res, ctx) => {
        const redirectUrl = req.headers.get('redirectURL');
        if (!req.body.captchaToken) {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4002, 'oauth2Login.body.captchaToken (null): must not be null')));
        }
        if (!redirectUrl || redirectUrl === 'undefined') {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(9001, "Required request header 'redirectUrl' for method parameter type String is not present")));
        }
        if (redirectUrl === 'htp://www.spark.co.nz') {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4100, 'Invalid/malformed redirectUrl.')));
        }
        if (redirectUrl === 'https://www.sparky.co.nz') {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4101, 'Redirect URL domain is invalid or domain not configured properly.')));
        }
        if (!req.body.accessToken) {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4002, 'oauth2Login.body.accessToken (null): must not be null')));
        }
        if (req.body.captchaToken === 'invalid_token') {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4112, 'Captcha verification failed.')));
        }
        if (req.body.accessToken === 'invalid_token') {
            return res(ctx.status(400), ctx.json(makeSingleSignonResponse(4123, 'The access token is invalid or expired.')));
        }
        return res(ctx.status(200), ctx.json(makeSingleSignonResponse(2000, 'Success')));
    }),
];

const handlers = [
    ...handlers$b,
    ...handlers$a,
    ...handlers$9,
    ...handlers$3,
    ...handlers$7,
    ...handlers$8,
    ...handlers$6,
    ...handlers$5,
    ...handlers$4,
    ...handlers$2,
    ...handlers$1,
];

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    'default': handlers
});

export { AppSingleSignon$1 as AppSingleSignon, ComponentLibraryProvider, ForgotEmail$1 as ForgotEmailJourney, ForgotPassword as ForgotPasswordJourney, ForgotUserId$1 as ForgotUserIdJourney, GenerateCaptchaToken, Impersonate$1 as ImpersonateJourney, Login as LoginJourney, LogoBrandFromUrl, Register$1 as RegisterJourney, SetPassword as SetPasswordJourney, SparkComponentLibrary, UniversalLoginProvider, VerifyJourney, loginAsImpersonatedUser, index as mocks, requestGuestLogin, requestLogout, setEnvConfig, useComponentLibrary, useScript };
//# sourceMappingURL=index.esm.js.map
